import { ChangeDetectionStrategy, Component, DOCUMENT, DestroyRef, ElementRef, HostListener, computed, effect, inject, linkedSignal, signal, untracked, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartEditorPageComponent } from '../chart-editor-page/chart-editor-page.component';
import { PlatformHumanDesignService } from '@data/_generated-api/platform-api/services/platform-human-design.service';
import { HdBodygraphChartPreset, HdBodygraphConfig, HdBodygraphPlanetaryActivation, HdGateBlockPinV1, HdMoonTransitPinV1 } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import { HdBodygraphTransitOverlay } from '@/app/human-design/chart/bodygraph/hd-bodygraph-transit-overlay.models';
import moment from 'moment';
import { filterHdCycleOverlayActivations } from '@/app/human-design/chart/bodygraph/hd-cycle-overlay.utils';
import { HdCycleCalculateRequest } from '@/app/human-design/chart/bodygraph/hd-cycle-chart.models';
import { buildCycleChartPreset, extractCycleSourceCore, findHdCycleMoment, withHdBirthCoordinates } from '@/app/human-design/chart/library/hd-cycle-chart.utils';
import { PlatformGeocodingService } from '@data/_generated-api/platform-api/services/platform-geocoding.service';
import { HdCompositeCalculateRequest } from '@/app/human-design/chart/bodygraph/hd-composite-chart.models';
import { buildCompositeChartPreset } from '@/app/human-design/chart/library/hd-composite-chart.utils';
import {
  HdCompositeThemeInfoComponent,
  HdCompositeThemeInfoData,
} from '@/app/human-design/chart/bodygraph/advanced-properties/hd-composite-theme-info.component';
import {
  HdRelationInfoPanelComponent,
  HdRelationInfoPanelData,
} from '@/app/human-design/chart/bodygraph/relation-info/hd-relation-info-panel.component';
import { HdRelationInfoTopic } from '@/app/human-design/chart/bodygraph/relation-info/hd-relation-info.service';
import {
  HdNodalEnvironmentsInfoPanelComponent,
  HdNodalEnvironmentsInfoPanelData,
} from '@/app/human-design/chart/bodygraph/nodal-environments/hd-nodal-environments-info-panel.component';
import {
  HdProfileInfoPanelComponent,
  HdProfileInfoPanelData,
} from '@/app/human-design/chart/bodygraph/profile-info/hd-profile-info-panel.component';
import { HdIncarnationCrossPanelService } from '@/app/human-design/chart/bodygraph/incarnation-cross-info/hd-incarnation-cross-panel.service';
import {
  HdBasicsInfoPanelComponent,
  HdBasicsInfoPanelData,
} from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-basics-info-panel.component';
import {
  HdCosmologyInfoPanelComponent,
  HdCosmologyInfoPanelData,
} from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-cosmology-info-panel.component';
import {
  HdMindLogPanelComponent,
  HdMindLogPanelData,
} from '@/app/human-design/chart/bodygraph/advanced-properties/hd-mind-log-panel.component';
import {
  HdTypeInfoPanelComponent,
  HdTypeInfoPanelData,
} from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-type-info-panel.component';
import {
  HdAuthorityInfoPanelComponent,
  HdAuthorityInfoPanelData,
} from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-authority-info-panel.component';
import {
  HdDefinitionInfoPanelComponent,
  HdDefinitionInfoPanelData,
} from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-definition-info-panel.component';
import {
  HdConditioningInfoPanelComponent,
  HdConditioningInfoPanelData,
} from '@/app/human-design/chart/conditioning/hd-conditioning-info-panel.component';
import { HdTypeInfoTopic } from '@/app/human-design/chart/bodygraph/chart-basics-info/hd-chart-basics-info-metadata';
import { HdChartBasics, HdDefinitionKey } from '@/app/human-design/chart/bodygraph/basic-properties/hd-chart-properties.types';
import {
  deriveBasicProperties,
  deriveIncarnationCross,
  deriveLifeCross,
  deriveProfile,
} from '@/app/human-design/chart/bodygraph/basic-properties/hd-chart-properties.util';
import { HD_JUST_NOW_ICONS, buildCycleChartName, buildCycleMomentLabel, hdCycleLabel } from '@/app/human-design/chart/library/hd-chart-cycle-metadata';
import { HdChartCyclesService } from '@/app/human-design/chart/library/hd-chart-cycles.service';
import { HdChartRenameDialogComponent, HdChartRenameTarget } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-rename-dialog.component';
import { HdMomentSaveDialogComponent, HdMomentSaveTarget } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-moment-save-dialog.component';
import { EgiPageNavigationService } from '@/app/platform/platform-context/egi-page-navigation.service';
import { HD_LIFE_TIMELINE_PAGE_ID } from '../hd-page-ids';
import { HdTransitTimeSliderComponent } from '@/app/human-design/chart/bodygraph/time-slider/hd-transit-time-slider.component';
import { TransitViewTimeService } from '@/app/human-design/transits/transit-view-time.service';
import { buildMomentChartName, buildTransitSnapshotPreset } from '@/app/human-design/chart/library/hd-transit-chart.utils';
import { HdJustNowTransitPinV1 } from '@/app/human-design/chart/bodygraph/hd-transit-chart.models';
import { HdNotesPanelComponent } from '@/app/human-design/chart/bodygraph/notes/hd-notes-panel.component';
import {
  HdNotesFloatingPanelComponent,
  HdNotesFloatingPanelData,
} from '@/app/human-design/chart/bodygraph/notes/hd-notes-floating-panel.component';
import { HdSimpleExtraPlanetVm } from '@/app/human-design/chart/bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { HD_CHIRON_DISPLAY_CONFIG } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdSideStatsSpaceMeasurement, sideStatsFitBelowResonanceGrid } from '@/app/human-design/chart/bodygraph/planet-grid/hd-side-stats-space.util';
import { tripleChartSidePullPx } from '@/app/human-design/chart/bodygraph/hd-triple-chart-space.util';
import { HdChartGroupService } from '@/app/human-design/chart/library/hd-chart-group.service';
import { HdBodygraphConfiguratorComponent } from '@/app/human-design/chart/bodygraph/hd-bodygraph-configurator/hd-bodygraph-configurator.component';
import { HdBasicPropertiesComponent } from '@/app/human-design/chart/bodygraph/basic-properties/hd-basic-properties.component';
import { HdAdvancedPropertiesComponent } from '@/app/human-design/chart/bodygraph/advanced-properties/hd-advanced-properties.component';
import {
  HdCycleDefinitionInfoRequest,
  HdCycleLifeCrossInfoRequest,
} from '@/app/human-design/chart/bodygraph/advanced-properties/hd-cycles-section.component';
import { HdBodygraphChannelInfoRequest, HdBodygraphChartComponent } from '@/app/human-design/chart/bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdPlanetGridComponent } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid/hd-planet-grid.component';
import { HdPlanetGridDensity } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-activation-block/hd-planet-activation-block.component';
import { HdSimplePlanetGridComponent } from '@/app/human-design/chart/bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { HdJustNowBasePlanetGridComponent, HdJustNowBasePlanetGridLabels } from '@/app/human-design/chart/bodygraph/planet-grid/hd-just-now-base-planet-grid/hd-just-now-base-planet-grid.component';
import { HdJustNowTransitPlanetGridComponent, HdJustNowTransitPlanetGridLabels } from '@/app/human-design/chart/bodygraph/planet-grid/hd-just-now-transit-planet-grid/hd-just-now-transit-planet-grid.component';
import { HdJustNowLineDataResolver } from '@/app/human-design/chart/bodygraph/planet-grid/hd-just-now-fixing.utils';
import { getHdPlanetDisplayConfig, getHdPlanetDisplayConfigByPlanetId } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { findActivationFromList } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid.utils';
import { HD_MANDALA_PATTERN_OPTIONS_DEFAULT, HdMandalaPatternOptions, HdMandalaPatternsViewData } from '@/app/human-design/chart/mandala/patterns/hd-mandala-patterns-view.models';
import { HdGateGuessingGamePanelComponent } from '@/app/human-design/chart/bodygraph/gate-guessing-game/hd-gate-guessing-game-panel/hd-gate-guessing-game-panel.component';
import { HdChannelGuessingGamePanelComponent } from '@/app/human-design/chart/bodygraph/channel-guessing-game/hd-channel-guessing-game-panel/hd-channel-guessing-game-panel.component';
import { HdDrawChartGamePanelComponent } from '@/app/human-design/chart/bodygraph/draw-chart-game/hd-draw-chart-game-panel/hd-draw-chart-game-panel.component';
import { DockAreaComponent } from '@shared/floating-panel/dock-area.component';
import { DockFixedTabDirective } from '@shared/floating-panel/dock-fixed-tab.directive';
import { EResizeHandleComponent } from '@shared/components/atoms/e-resize-handle/e-resize-handle.component';
import { FloatingPanelDockZone, FloatingPanelRef } from '@shared/floating-panel/floating-panel.types';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdTransitsPanelComponent, HdTransitsPanelData } from '@/app/human-design/transits/transits-panel/hd-transits-panel.component';
import { HdRaveNewYearRequest, HdRaveNewYearService } from '@/app/human-design/transits/rave-new-year/hd-rave-new-year.service';
import { hdRaveNewYearChartMoment } from '@/app/human-design/transits/rave-new-year/hd-rave-new-year';
import { HdChironPosition, resolveHdChironPosition } from '@/app/human-design/chart/bodygraph/hd-chiron.models';
import { HdChartHoverHighlightService } from '@/app/human-design/chart/bodygraph/hd-chart-hover-highlight.service';
import { SharedModule } from '@shared/shared.module';
import { ContextMenuItem, ContextMenuService } from '@shared/context-menu/context-menu.service';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdChartTabsService } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-tabs.service';
import {
  HdChartTabGameSettingsV1,
  HdChartTabSnapshotV1,
} from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-tab-snapshot.models';
import { HdChartGlobalSettingsV1 } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HdChartTabBarComponent, HdChartTabBarItem } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-tab-bar.component';
import { HdChartPdfExportService } from '@/app/human-design/chart/print/hd-chart-pdf-export.service';
import { isHdChartPrintable } from '@/app/human-design/chart/print/hd-chart-print';
import { HdChartPrintDialogComponent, HdChartPrintDialogTarget } from '@/app/human-design/chart/print/hd-chart-print-dialog.component';
import type { HdChartPrintOptions } from '@/app/human-design/chart/print/hd-chart-print-options';
import { HdChartButtonSize, HdChartShortcutBarComponent, HdChartSideMode, HdChartViewMode } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-shortcut-bar.component';
import { HdBodygraphRenderStateFactory } from '@/app/human-design/chart/bodygraph/hd-bodygraph-render-state.factory';
import { filterHdBodygraphConfigBySide } from '@/app/human-design/chart/bodygraph/hd-bodygraph-config.utils';
import {
  HdMandalaGateGlyph,
  HdMandalaQuarterTexts,
  HdMandalaSeasonTexts,
  HdMandalaZodiacNames,
  MandalaWheelComponent,
} from '@/app/human-design/chart/mandala/mandala-wheel.component';
import {
  HdMandalaQuarterColorVariant,
  HdMandalaZodiacRingVariant,
  MANDALA_INFO_RING_CYCLE,
  MANDALA_WHEEL_TARGET_OUTER_RADIUS,
  MOON_TRANSIT_WHEEL_EDGE_RADIUS,
  MandalaInfoRingKey,
  buildMandalaWheelLayout,
} from '@/app/human-design/chart/mandala/mandala.constants';
import { HD_SEASON_MARKER_ROOM } from '@/app/human-design/chart/mandala/hd-season-markers';
import {
  buildHdMandalaGateGlyphs,
  buildHdMandalaQuarterTexts,
  buildHdMandalaSeasonTexts,
  buildHdMandalaZodiacNames,
  hdMandalaSideGlyphColor,
} from '@/app/human-design/chart/mandala/mandala-wheel-inputs';
import { buildHdMoonTransitOverview, HdMoonTransitFormation } from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit';
import {
  HdMandalaMoonTransitViewData,
  HdMoonTransitConnectorStyle,
  HdMoonTransitGateMarkerStyle,
  HdMoonTransitPin,
} from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit-view.models';
import { HdMoonTransitCardLayerComponent } from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit-card-layer.component';
import { HdMoonTransitLegendComponent } from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit-legend.component';
import {
  buildHdJustNowTransitOverview,
  filterHdJustNowPersonalityActivations,
  HD_JUST_NOW_TRANSIT_BASE_COLOR,
  HD_JUST_NOW_TRANSIT_COLOR,
  HD_JUST_NOW_TRANSIT_TEXT_COLOR,
  HdJustNowTransitColorMode,
  HdJustNowTransitFormation,
} from '@/app/human-design/chart/mandala/just-now-transit/hd-just-now-transit';
import {
  HdJustNowTransitFormationClickEvent,
  HdMandalaJustNowTransitViewData,
} from '@/app/human-design/chart/mandala/just-now-transit/hd-just-now-transit-view.models';
import { HdJustNowTransitCardLayerComponent } from '@/app/human-design/chart/mandala/just-now-transit/hd-just-now-transit-card-layer.component';
import {
  HD_LOCKS_KEYS_LOCKS,
  HD_LOCKS_KEYS_SHIFT_PERIOD_IDS,
  HdLocksKeysPeriodId,
  getHdLocksKeysPeriodMeta,
} from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-metadata';
import { hdLocksKeysPeriodText } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-period-text';
import { HdMandalaLocksKeysViewData } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-view.models';
import { HdBodygraphLocksKeysOverlay } from '@/app/human-design/chart/bodygraph/hd-bodygraph-locks-keys-layer';
import { HdLocksKeysStateService } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-state.service';
import { HdChartSearchContextService } from '@/app/human-design/chart/library/hd-chart-search-context.service';
import { buildHdLocksKeysGeneralImprintConfig } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys';
import { HdLocksKeysPanelComponent } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-panel.component';
import {
  HD_JUST_NOW_PANEL_TITLE,
  HdJustNowPanelComponent,
} from '@/app/human-design/chart/just-now-panel/hd-just-now-panel.component';
import { HdJustNowPanelStateService } from '@/app/human-design/chart/just-now-panel/hd-just-now-panel-state.service';
import { HD_BIRTH_WINDOW_ICON, HD_BIRTH_WINDOW_PANEL_KEY, HdBirthWindowPanelComponent } from '@/app/human-design/chart/birth-window/hd-birth-window-panel.component';
import { HdBirthWindowStateService } from '@/app/human-design/chart/birth-window/hd-birth-window-state.service';
import { buildBirthWindowCalculateRequest } from '@/app/human-design/chart/birth-window/hd-birth-window-data.service';
import { HdBirthWindowConfirmDialogComponent, HdBirthWindowConfirmTarget } from '@/app/human-design/chart/birth-window/hd-birth-window-confirm-dialog.component';
import { birthWindowActivationsAt } from '@/app/human-design/chart/birth-window/birth-window-activations.builder';
import { buildBirthWindowPreviewPreset, preserveBirthEntryMode } from '@/app/human-design/chart/birth-window/birth-window-preset.builder';
import { formatBirthClock, formatBirthWindowSignedDelta, hdBirthWindowTextKey } from '@/app/human-design/chart/birth-window/birth-window-format';
import { HD_BIRTH_WINDOW_CHART_TYPES } from '@/app/human-design/chart/birth-window/birth-window.models';
import {
  HdBirthWindowMomentExact,
  HdBirthWindowOverlay,
  applyHdBirthWindowMomentTime,
  buildHdBirthWindowMomentPreset,
  buildHdBirthWindowMomentPreview,
  hdBirthWindowOverlayOf,
} from '@/app/human-design/chart/birth-window/birth-window-moment';
import { carryUserHandwork } from '@/app/human-design/chart/library/hd-chart-edit.utils';
import {
  hdChartBirthMomentKey,
  hdChartBirthMomentLabel,
  hdChartContentFingerprint,
} from '@/app/human-design/chart/library/hd-chart-library-sync.utils';
import {
  HdLocksKeysLockDetailComponent,
  HdLocksKeysLockDetailData,
  hdLocksKeysLockDetailPanelKey,
} from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-lock-detail.component';
import { HdGateInfoOpenBehavior } from '@/app/human-design/chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';
import { HdChartGameDialogComponent } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-game-dialog.component';
import { HdChartAnnotationLayerComponent } from '@/app/human-design/chart/bodygraph/annotation/hd-chart-annotation-layer.component';
import { HdGateBlockLayerComponent } from '@/app/human-design/chart/bodygraph/gate-blocks/hd-gate-block-layer.component';
import { HdGateBlockPin, hdGateBlockKey } from '@/app/human-design/chart/bodygraph/gate-blocks/hd-gate-block.models';
import { HdAnnotationStore } from '@/app/human-design/chart/bodygraph/annotation/hd-annotation-store';
import {
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HdBodygraphActiveGateColorPreset,
  HdBodygraphCenterBorderStyle,
  HdBodygraphColorConfigPresetId,
} from '@/app/human-design/chart/bodygraph/hd-bodygraph-color-config';
import {
  HD_PLANET_GRID_COLOR_CONFIG_PRESETS,
  HdBaseRotation,
  HdBeneathTheLineStyle,
  HdGateNumberAlign,
  HdGateNumberFontSize,
  HdNodeBackgroundStyle,
  HdPlanetGridColorConfigPresetId,
  HdUndefinedActivationStyle,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid-color-config';
import { HD_BODYGRAPH_GATES } from '@/app/human-design/chart/bodygraph/hd-bodygraph-metadata';
import {
  HD_CHART_GAME_OPTIONS,
  HdBridgeGateStyle,
  HdChartGameId,
  HdChartGamePlayRequest,
  HdChartGameVariantId,
} from '@/app/human-design/chart/bodygraph/hd-chart-controls-options';
import { HdChartLibraryService } from '@/app/human-design/chart/library/hd-chart-library.service';
import { HdChartDataCache } from '@/app/human-design/chart/library/hd-chart-data-cache.service';
import { RemoteChartChange } from '@common/chart-change-signals/chart-change-signals';
import { HdChartCreateDialogComponent } from '@/app/human-design/chart/library/hd-chart-create-dialog.component';
import { HdChartManagerDialogComponent } from '@/app/human-design/chart/library/hd-chart-manager-dialog.component';
import { HdChartShareDialogComponent } from '@/app/human-design/chart/library/hd-chart-share-dialog.component';
import { HdSharedChartBannerComponent } from '@/app/human-design/chart/library/hd-shared-chart-banner.component';
import { HdChartShareService } from '@/app/human-design/chart/library/hd-chart-share.service';
import { HdChartShareAccessService } from '@/app/human-design/chart/library/hd-chart-share-access.service';
import { HdChartShareChange, HdChartShareTarget, HdSharedChartLoadError, HdSharedChartResult } from '@/app/human-design/chart/library/hd-chart-share.models';
import { HdChartPanelViewMode } from '@/app/human-design/chart/bodygraph/hd-chart-panel-view-mode';
import { HdChartCalculationResult, HdChartDocumentResult, chartTypeForPreset } from '@/app/human-design/chart/library/hd-chart-library.models';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { NOTIFIER } from '@shared/notifier/types';
import { ViewportService } from '@core/services/viewport.service';
import { AppSettingsPanelService } from '@core/services/app-settings-panel.service';
import { PageTitleService } from '@core/page-title/page-title.service';
import {
  applyHdGlobalShiftMarkers,
  hdBirthUtc,
  isHdMomentAfterGlobalShift,
  markHdConfigurationForGlobalShift,
} from '@/app/human-design/chart/hd-global-shift';


/**
 * Ruimte-detectie wiel ↔ planeetgrids: onder deze wielmaat (px) is de
 * both/mandala-weergave naast de geavanceerde Resonance-LPS niet meer zinvol
 * (Full HD) en wordt de chart-kaart verborgen; erboven krimpt het geheel
 * gewoon mee. De clearance dekt de grid-zijmarge (20px) plus wat ademruimte.
 */
const HD_MANDALA_MIN_WHEEL_PX = 560;
const HD_MANDALA_GRID_CLEARANCE_PX = 28;

/**
 * Eén zijchart van het drieluik: de configuratie plus het label erboven.
 * `labelKind` stuurt de labelkleur — design-rood / personality-zwart op een
 * gewone tab, chart-1-blauw / chart-2-groen op een cycle-/composite-tab.
 */
interface HdTripleSideChart {
  readonly configuration: HdBodygraphConfig;
  readonly label: string;
  readonly labelKind: 'design' | 'personality' | 'chart1' | 'chart2';
}

/**
 * Overlay-snapshot van één moment: de vorm die zowel de live just-now-sessie,
 * het document van een snapshot-tab als een tijdslider-sample aanneemt.
 */
interface HdTransitOverlaySnapshot {
  readonly personalityActivations: readonly HdBodygraphPlanetaryActivation[];
  /** Informatieve Chiron van het moment (metadata.extraPlanets), voor de transit-grid. */
  readonly personalityChiron: HdSimpleExtraPlanetVm | null;
  /** `metadata`-blok van het momentdocument — bewaard voor "Moment bewaren" (Chiron-bron in het snapshot-document). */
  readonly momentMetadata: Record<string, unknown> | null;
  readonly fetchedAt: Date;
}

/**
 * Throttle van de tijdslider naar een volledige momentberekening: tijdens het
 * slepen volgt de chart live met hoogstens één berekening per interval, en de
 * laatste stand komt altijd nog (trailing). Bijgestuurd 2026-09-17: was een
 * 400 ms-debounce die pas bij stilstand rekende.
 */
const TRANSIT_SAMPLE_THROTTLE_MS = 300;
/** Venster van de live-slider rond het activatiemoment van de overlay (2 dagen voor en na). */
const LIVE_SLIDER_WINDOW_MS = 48 * 3_600_000;
/** Plafond van de sample-cache (chart + minuut → snapshot). */
const TRANSIT_SAMPLE_CACHE_MAX = 60;
/** Geboortevenster: na zoveel rust op de kandidaattijd volgt het exacte document (spec §3.3). */
const BIRTH_WINDOW_PREVIEW_DEBOUNCE_MS = 400;
const BIRTH_WINDOW_EXACT_CACHE_MAX = 200;
/** Breedte van het chartgedeelte voor zwevende leespanelen (zie publishChartContentWidth). */
const HD_CHART_CONTENT_WIDTH_VAR = '--hd-chart-content-width';
/**
 * Info-panelen van het Basis-paneel die de actieve chart volgen en dus op een
 * chart vergrendeld kunnen worden (naast de poort-, PHS-, kanaal- en
 * centerpanelen van de basis). De "Toon alle"-overzichten (`-all`) volgen ook,
 * maar dragen enkel markeringen en krijgen geen slot.
 */
const BASICS_FOLLOWING_PANEL_KEYS: ReadonlySet<string> = new Set([
  'type-info',
  'strategy-info',
  'signature-info',
  'authority-info',
  'definition-info',
  'profile-info',
  'nodal-environments-info',
]);

/**
 * Chart-omgeving met BAC-layout + tabsysteem + settings-sidepanel (spec
 * `docs/superpowers/specs/2026-06-11-chart-tabs-sidepanel-design.md`).
 *
 * Erft alle chart/game-logica van {@link ChartEditorPageComponent}; voegt toe:
 *  - per-tab snapshots via {@link HdChartTabsService} (extract/apply op de
 *    geërfde signals),
 *  - globale-settings-persistentie (hydrate in constructor, debounced effect),
 *  - de tab-bar, shortcut-bar en het settings-panel i.p.v. chart-presets.
 */
@Component({
  selector: 'chart-environment-full-page',
  imports: [
    SharedModule,
    HdBodygraphChartComponent,
    HdBodygraphConfiguratorComponent,
    HdBasicPropertiesComponent,
    HdAdvancedPropertiesComponent,
    HdPlanetGridComponent,
    HdSimplePlanetGridComponent,
    HdJustNowBasePlanetGridComponent,
    HdJustNowTransitPlanetGridComponent,
    HdGateGuessingGamePanelComponent,
    HdChannelGuessingGamePanelComponent,
    HdDrawChartGamePanelComponent,
    DockAreaComponent,
    DockFixedTabDirective,
    EResizeHandleComponent,
    HdChartTabBarComponent,
    HdChartShortcutBarComponent,
    HdChartGameDialogComponent,
    HdChartAnnotationLayerComponent,
    HdGateBlockLayerComponent,
    HdChartCreateDialogComponent,
    HdChartManagerDialogComponent,
    HdChartShareDialogComponent,
    HdSharedChartBannerComponent,
    HdChartRenameDialogComponent,
    HdChartPrintDialogComponent,
    HdMomentSaveDialogComponent,
    HdBirthWindowConfirmDialogComponent,
    HdTransitTimeSliderComponent,
    HdNotesPanelComponent,
    MandalaWheelComponent,
    HdMoonTransitCardLayerComponent,
    HdMoonTransitLegendComponent,
    HdJustNowTransitCardLayerComponent,
  ],
  providers: [HdChartHoverHighlightService, HdChartTabsService, HdAnnotationStore],
  templateUrl: './chart-environment-full-page.component.html',
  styleUrl: './chart-environment-full-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartEnvironmentFullPageComponent extends ChartEditorPageComponent {
  protected readonly tabsService = inject(HdChartTabsService);
  protected readonly annotationStore = inject(HdAnnotationStore);
  private readonly humanDesignApi = inject(PlatformHumanDesignService);
  private readonly pageConfirmer = inject(CONFIRMER);
  private readonly pageContextMenu = inject(ContextMenuService);
  private readonly pageTransloco = inject(TranslocoService);
  private readonly pageContentLang = inject(HdContentLanguageService);
  private readonly chartLibrary = inject(HdChartLibraryService);
  private readonly raveNewYear = inject(HdRaveNewYearService);
  /** Chart delen via link (spec 2026-10-20-chart-share): deellinks + gedeelde charts ophalen. */
  private readonly chartShare = inject(HdChartShareService);
  private readonly shareAccess = inject(HdChartShareAccessService);
  /** Per-chart datacache (spec 2026-10-06): laadstatus voor de tab-loader + seintjes van elders. */
  private readonly dataCache = inject(HdChartDataCache);
  private readonly cyclesService = inject(HdChartCyclesService);
  private readonly relationsService = inject(HdChartGroupService);
  /** Geocode-reparatie voor imports zonder coördinaten (Maia Mechanics). */
  private readonly geocodingService = inject(PlatformGeocodingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly notifier = inject(NOTIFIER);
  private readonly chartPdfExport = inject(HdChartPdfExportService);
  private readonly transitsFloatingPanels = inject(FloatingPanelService);
  /** Incarnatiekruis-panelen: één per kruis + het gedeelde algemene IX-paneel. */
  private readonly incarnationCrossPanels = inject(HdIncarnationCrossPanelService);
  /** Mobiele compositie (docs/MOBIEL.md §5): phone = toolbar + verticale stroom. */
  protected readonly viewport = inject(ViewportService);
  /** User-menu "Applicatie-instellingen" → settings-paneel openen (ook na navigatie hierheen). */
  private readonly appSettingsPanel = inject(AppSettingsPanelService);
  private readonly pageTitle = inject(PageTitleService);

  private readonly openSettingsPanelOnRequest = effect(() => {
    if (this.appSettingsPanel.pendingOpen()) {
      untracked(() => {
        this.appSettingsPanel.clearPending();
        this.settingsPanelOpen.set(true);
      });
    }
  });

  // ── Transits-paneel (spec §4 Plaatsing, besluit 2) ──────────────────────
  /** Dedup-key van het transits floating panel. */
  private readonly transitsPanelKey = 'transits';

  /** Shortcut-toggle state: het transits-paneel is open (floating of gedockt). */
  protected readonly transitsPanelOpen = computed(() =>
    this.transitsFloatingPanels.panels().some(panel => panel.key === this.transitsPanelKey),
  );

  /**
   * Shortcut-bar transits-toggle: open het floating panel (meteen gedockt in
   * de full-zone) of focus het bestaande; bij toggle-uit sluiten we het.
   */
  protected handleTransitsToggled(): void {
    if (this.transitsPanelOpen()) {
      this.transitsFloatingPanels.closeForKey(this.transitsPanelKey);
      return;
    }
    this.transitsFloatingPanels.openOrFocus<HdTransitsPanelComponent, HdTransitsPanelData>({
      key: this.transitsPanelKey,
      component: HdTransitsPanelComponent,
      data: this.buildTransitsPanelData(),
      dockTab: { label: 'Transits' },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
      // Losgemaakt: (smal, zie HdTransitsViewStateService) links naast het
      // rechterpaneel i.p.v. gecentreerd, zodat dat zichtbaar blijft.
      undockPlacement: 'beside-dock',
    });
  }

  // ── Losgemaakte notities (zwevend venster) ────────────────────────────
  /** Dedup-key van het zwevende notities-venster. */
  private readonly notesPanelKey = 'chart-notes';

  /** De notities staan in een zwevend venster; de vaste Notities-tab toont dan een plaatshouder. */
  protected readonly notesFloating = computed(() =>
    this.transitsFloatingPanels.panels().some(panel => panel.key === this.notesPanelKey),
  );

  /**
   * "Losmaken" in de Notities-docktab: de notities openen als zwevend venster
   * (niet dockbaar, de vaste tab ís de dockplek), zodat de rest van het
   * rechterpaneel zichtbaar wordt om uit te lezen en te kopiëren.
   */
  protected handleNotesUndockRequested(): void {
    this.transitsFloatingPanels.openOrFocus<HdNotesFloatingPanelComponent, HdNotesFloatingPanelData>({
      key: this.notesPanelKey,
      component: HdNotesFloatingPanelComponent,
      data: this.buildNotesPanelData(),
      dockTab: {
        label: this.pageTranslate('humanDesign.notesPanel.floatingTabLabel'),
        icon: 'note-sticky',
      },
      dockable: false,
      closable: true,
      bypassAutoDock: true,
      startPosition: 'center',
    });
    // Het rechterpaneel komt vrij: meteen naar de Geavanceerd-tab (daar staat
    // doorgaans wat je wil overnemen, bv. de cyclus-info).
    this.rightDockArea()?.activateFixedTab('advanced');
  }

  /** "Terugzetten" in de plaatshouder: venster sluiten, de vaste tab toont de notities weer. */
  protected handleNotesRedockRequested(): void {
    this.transitsFloatingPanels.closeForKey(this.notesPanelKey);
  }

  private buildNotesPanelData(): HdNotesFloatingPanelData {
    return {
      savedChartId: this.savedChartId(),
      chartName: this.activePreset()?.name ?? '',
      openChart: chartId => void this.loadChartFromLibrary(chartId, /* inNewTab */ true),
    };
  }

  private pageTranslate(key: string): string {
    return this.pageTransloco.translate<string>(key, undefined, this.pageContentLang.effectiveLang());
  }

  /**
   * Labels van de vaste tabs van het rechterpaneel (content-taal). De tabs
   * worden op hun id (`dockFixedTabId`) geactiveerd, nooit op dit label.
   * `effectiveLang()` (via pageTranslate) herevalueert zelf na elke laad-tick.
   */
  protected readonly dockTabLabels = computed(() => {
    return {
      basic: this.pageTranslate('humanDesign.dockTabs.basic'),
      advanced: this.pageTranslate('humanDesign.dockTabs.advanced'),
      editor: this.pageTranslate('humanDesign.dockTabs.editor'),
      notes: this.pageTranslate('humanDesign.dockTabs.notes'),
    };
  });

  /** Teksten van de plaatshouder in de vaste Notities-tab (content-taal, zoals het notities-paneel). */
  protected readonly notesPlaceholderText = computed(() => {
    this.pageContentLang.translationsLoadedTick();
    return {
      placeholder: this.pageTranslate('humanDesign.notesPanel.floatingPlaceholder'),
      redock: this.pageTranslate('humanDesign.notesPanel.redock'),
    };
  });

  private buildTransitsPanelData(): HdTransitsPanelData {
    return {
      showChiron: this.showChiron(),
      compact: true,
      // Een vanuit de transits bewaard moment opent als nieuwe tab (momentcharts
      // activeren daarbij zelf de Notities-docktab).
      openChart: chartId => void this.loadChartFromLibrary(chartId, /* inNewTab */ true),
      // Vanuit de transits altijd de transit-variant (enkel personality); de
      // volledige varianten staan in de chart manager (Steven 2026-10-25).
      openJustNow: () => void this.handleJustNowRequested({ personalityOnly: true }),
      openRaveNewYear: year => void this.handleRaveNewYearRequested({ year, variant: 'transit' }),
    };
  }

  // ── Locks & Keys-modus (spec 2026-08-27) ─────────────────────────────────

  protected readonly locksKeysState = inject(HdLocksKeysStateService);

  private readonly chartSearchContext = inject(HdChartSearchContextService);

  /**
   * Spiegelt de actieve tab naar de zoek-context (poort-info, Locks & Keys): de
   * eigen chart blijft uit de treffers, en een cycle-/moment-tab zoekt de
   * relatie-scope vanuit de natal chart, zoals de Cycles-sectie.
   */
  private readonly syncChartSearchContext = effect(() => {
    const savedChartId = this.savedChartId();
    const preset = this.activePreset();
    const relationSource = preset?.cycle?.source?.chartId ?? preset?.transit?.source?.chartId ?? savedChartId;
    const matchScope = this.chartMatchScope();
    const chartKey = savedChartId ?? `tab:${this.tabsService.activeTabId()}`;
    untracked(() => {
      this.chartSearchContext.matchScope.set(matchScope);
      this.chartSearchContext.activeChartId.set(savedChartId);
      this.chartSearchContext.activeChartKey.set(chartKey);
      this.chartSearchContext.relationSourceChartId.set(relationSource ?? null);
      this.chartSearchContext.compositeActive.set(preset?.composite != null);
    });
  });

  /** Locks & Keys aan/uit — sessie-lokaal, zoals de transit-modi. */
  protected readonly locksKeysMode = signal(false);

  /**
   * Chart-zichtbaarheid binnen de modus (bijstuurronde 3/4): `true` = bodygraph
   * verkleind in het wiel (default bij activeren), `false` = enkel mandala —
   * zelfde mechanisme als {@link moonTransitChartVisible}, geschakeld via de
   * chart-/mandala-/both-knoppen zonder de onderliggende `chartViewMode` aan
   * te raken.
   */
  protected readonly locksKeysChartVisible = signal(true);

  /**
   * Mandala-zichtbaarheid binnen de modus (2026-10-10): bij activeren springt
   * het wiel altijd aan, maar sinds de L&K-markeringen op de chart zelf mag de
   * chart-knop het wiel uitzetten (`false` = enkel de bodygraph). Eén van beide
   * blijft altijd zichtbaar: zonder wiel is de chart er sowieso.
   */
  protected readonly locksKeysMandalaVisible = signal(true);

  /** Dedup-key van het gedockte Locks & Keys-infopaneel. */
  private readonly locksKeysPanelKey = 'locks-keys';

  protected readonly locksKeysPanelOpen = computed(() =>
    this.transitsFloatingPanels.panels().some(panel => panel.key === this.locksKeysPanelKey),
  );

  /**
   * De knop werkt op elk tab-type (bijstuurronde 8): zonder geladen chart
   * (algemene weergave), op relatie-/composite-tabs (ronde 5) én op
   * cycle-tabs — daar telt een key als "samen ingevuld" zodra de natal óf het
   * cyclusmoment hem activeert, met de bestaande kleuren (chart 1 blauw,
   * tweede kant mint) als eigenaars-markering. De computed blijft bestaan als
   * haak voor eventuele toekomstige uitsluitingen.
   */
  protected readonly locksKeysAvailable = computed(() => true);

  /** Neutrale (lege) wiel-configuratie voor de algemene scope van de vergelijkweergave. */
  private readonly locksKeysEmptyConfiguration: HdBodygraphConfig = { schemaVersion: 1, activations: [] };

  /**
   * Spiegelt de getoonde chart naar de gedeelde Locks & Keys-staat, zodat het
   * gedockte paneel en het detailpaneel (via {@link HdLocksKeysStateService})
   * dezelfde activaties zien als de mandala-laag. Op een composite-tab gaat de
   * volledige partnerchart (chart 2) mee als tweede kant.
   */
  private readonly syncLocksKeysChartState = effect(() => {
    const configuration = this.displayConfiguration();
    const planetary = this.displayPlanetaryActivations();
    // De tweede kant (mint) in de gecombineerde weergave: de composite-partner
    // of het cyclusmoment (bijstuurronde 8 — {@link overlayMomentChart} dekt
    // beide); in een kant-weergave is de display*-pijplijn al de geselecteerde
    // persoon. Op een gewone tab met de Just Now-transit actief speelt het
    // huidige moment dezelfde mint-rol, zodat zichtbaar is welke lock/key een
    // in-het-moment-activatie heeft.
    const momentChart = this.combinedSideSelection() === 'both' ? this.overlayMomentChart() : null;
    const justNowMoment = this.justNowTransitVisualsActive() ? this.justNowTransitActivations() : [];
    const partnerActivations = momentChart?.configuration.activations ?? justNowMoment;
    const partnerPlanetary = momentChart?.planetaryActivations ?? justNowMoment;
    // Namen van de twee kanten (composite): voedt de echte chartnamen in de
    // relatie-sectie i.p.v. "Chart 1"/"Chart 2".
    const composite = this.activeCompositeInfo();
    untracked(() => {
      this.locksKeysState.chartActivations.set(configuration.activations);
      this.locksKeysState.chartPlanetaryActivations.set(planetary);
      this.locksKeysState.partnerActivations.set(partnerActivations);
      this.locksKeysState.partnerPlanetaryActivations.set(partnerPlanetary);
      this.locksKeysState.chart1Name.set(composite?.source.name ?? '');
      this.locksKeysState.chart2Name.set(composite?.partner.name ?? '');
    });
  });

  /**
   * Het gedockte paneel is de controlekamer van de modus: sluit de gebruiker
   * het paneel (kruisje/chip), dan gaat de modus mee uit — en omgekeerd sluit
   * {@link deactivateLocksKeysMode} het paneel.
   */
  private readonly closeLocksKeysModeWithPanel = effect(() => {
    if (this.locksKeysMode() && !this.locksKeysPanelOpen()) {
      untracked(() => this.deactivateLocksKeysMode());
    }
  });

  protected toggleLocksKeysMode(): void {
    if (this.locksKeysMode()) {
      this.deactivateLocksKeysMode();
      return;
    }
    if (!this.locksKeysAvailable()) {
      return;
    }
    // De maan-transit deelt dezelfde mandala-ruimte en blijft wederzijds
    // exclusief; de Just Now-transit mag sinds bijstuurronde 8 samen met
    // Locks & Keys actief zijn (het moment wordt dan de mint tweede kant).
    this.deactivateMoonTransitMode();
    // hasChart uit de gedeelde staat: dekt op composite-tabs ook de partner.
    this.locksKeysState.resetForActivation(this.locksKeysState.hasChart());
    this.locksKeysChartVisible.set(true);
    this.locksKeysMandalaVisible.set(true);
    this.openLocksKeysPanel();
    this.locksKeysMode.set(true);
  }

  protected deactivateLocksKeysMode(): void {
    this.locksKeysMode.set(false);
    this.locksKeysState.compare.set(false);
    if (this.locksKeysPanelOpen()) {
      this.transitsFloatingPanels.closeForKey(this.locksKeysPanelKey);
    }
  }

  /**
   * "Bekijk Locks & Keys"-knop op de composite-relatie-sectie van het
   * Geavanceerd-paneel: opent de L&K-modus (zoals de shortcut-knop) of, als die
   * al aan staat, focust het paneel opnieuw.
   */
  protected handleLocksKeysRequestedFromAdvanced(): void {
    if (this.locksKeysMode()) {
      this.openLocksKeysPanel();
      return;
    }
    this.toggleLocksKeysMode();
  }

  /**
   * Launcher voor de lock-/key-iconen in de poort-info (spec 2026-10-10 §4):
   * opent de modus zoals de "Bekijk Locks & Keys"-knop; een key-icoon geeft
   * de periode mee waarin de poort key is, zodat de modus die periode toont.
   */
  private launchLocksKeysMode(periodId?: HdLocksKeysPeriodId): void {
    // Eerst openen: het activeren zet het periodepaar op dat van nu, de
    // meegegeven periode komt daarna.
    this.handleLocksKeysRequestedFromAdvanced();
    if (periodId != null) {
      this.locksKeysState.selectPeriod(periodId);
    }
  }

  /**
   * Locks & keys op de bodygraph buiten de modus (drie-standen-toggle in het
   * Geavanceerd-paneel), in de gedeelde periode. Gebonden op de hoofdchart en
   * de drie drieluik-charts; elke chart filtert in `chart`-modus op zijn eigen
   * activaties.
   */
  protected readonly locksKeysChartOverlay = computed<HdBodygraphLocksKeysOverlay | null>(() => {
    const mode = this.locksKeysState.chartOverlayMode();
    return mode === 'off' ? null : { periodId: this.locksKeysState.periodId(), mode };
  });

  private openLocksKeysPanel(): void {
    this.transitsFloatingPanels.openOrFocus<HdLocksKeysPanelComponent, undefined>({
      key: this.locksKeysPanelKey,
      component: HdLocksKeysPanelComponent,
      data: undefined,
      dockTab: { label: 'Locks & Keys' },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  /** Klik op een lock in het wiel: open het detailpaneel van die lock. */
  protected handleLocksKeysLockClicked(lockGate: number): void {
    this.transitsFloatingPanels.openOrFocus<HdLocksKeysLockDetailComponent, HdLocksKeysLockDetailData>({
      key: hdLocksKeysLockDetailPanelKey(lockGate),
      component: HdLocksKeysLockDetailComponent,
      data: { gate: lockGate },
      dockTab: { label: `Lock ${lockGate}` },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  private buildLocksKeysViewDataFor(periodId: HdLocksKeysPeriodId): HdMandalaLocksKeysViewData {
    const overview = this.locksKeysState.overviewFor(periodId);
    const periodMeta = getHdLocksKeysPeriodMeta(periodId);
    return {
      periodId,
      scope: overview.scope,
      pairs: overview.entries.map(entry => ({
        lockGate: entry.lock.gate,
        keyGate: entry.keyGate,
        group: entry.lock.group,
        // Algemene scope: de volledige neutrale ster — elke key telt als actief.
        keyActivated: overview.scope === 'general' ? true : entry.keyActivated,
      })),
      showLocksStar: this.locksKeysState.showLocksStar(),
      showKeysStar: this.locksKeysState.showKeysStar(),
      equinoxGate: periodMeta.equinoxGate,
      equinoxGroup: periodMeta.equinoxGroup,
    };
  }

  /** Viewdata voor de mandala-laag in de enkelvoudige Locks & Keys-weergave. */
  protected readonly locksKeysViewData = computed<HdMandalaLocksKeysViewData | null>(() => {
    if (!this.locksKeysMode() || this.locksKeysState.compare()) {
      return null;
    }
    return this.buildLocksKeysViewDataFor(this.locksKeysState.periodId());
  });

  protected readonly locksKeysCompareActive = computed(
    () => this.locksKeysMode() && this.locksKeysState.compare(),
  );

  /** Algemene Locks & Keys-scope actief: het wiel toont de neutrale mandala. */
  private readonly locksKeysNeutralWheel = computed(
    () => this.locksKeysMode() && this.locksKeysState.effectiveScope() === 'general',
  );

  /**
   * Configuratie/activaties/glyphs van het hoofdwiel: in de algemene Locks &
   * Keys-scope een neutrale mandala (geen chartkleuren of planeetglyphs),
   * anders exact de gewone weergave.
   */
  /**
   * Weergave-configuratie voor de chart-lagen (bodygraph én mandala): in de
   * algemene Locks & Keys-scope de keys-imprint van de gekozen periode (enkel
   * de 8 keys als activaties), anders de gewone weergave-configuratie. "Op
   * chart" of de modus uitzetten toont dus meteen weer de actieve chart.
   */
  private readonly locksKeysAwareDisplayConfiguration = computed<HdBodygraphConfig>(() =>
    this.locksKeysNeutralWheel()
      ? buildHdLocksKeysGeneralImprintConfig(this.locksKeysState.periodId())
      : this.displayConfiguration(),
  );

  protected readonly mandalaWheelConfiguration = computed<HdBodygraphConfig>(() => {
    const base = this.locksKeysAwareDisplayConfiguration();
    // Gecombineerde weergave (composite-partner of cyclusmoment): laat ook de
    // slice-centerkleuren en poortlabels van de tweede persoon oplichten — de
    // planeetglyphs tonen beide kanten al (mandalaGateGlyphs). In de neutrale
    // Locks & Keys-scope of een kant-weergave blijft het de basisconfiguratie.
    if (this.locksKeysNeutralWheel() || this.combinedSideSelection() !== 'both') {
      return base;
    }
    const partner = this.overlayMomentChart()?.configuration.activations ?? [];
    if (partner.length === 0) {
      return base;
    }
    return { ...base, activations: [...base.activations, ...partner] };
  });

  protected readonly mandalaWheelPlanetaryActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => (this.locksKeysNeutralWheel() ? [] : this.displayPlanetaryActivations()),
  );

  protected readonly mandalaWheelGateGlyphs = computed<readonly HdMandalaGateGlyph[]>(() =>
    this.locksKeysNeutralWheel() ? [] : this.mandalaGateGlyphs(),
  );

  /** Toggle-state van de Misc-tab → Mandala-sectie (patroon-overlays op het wiel); bron voor het wiel. */
  protected readonly mandalaPatternOptions = signal<HdMandalaPatternOptions>(HD_MANDALA_PATTERN_OPTIONS_DEFAULT);

  /** Misc-tab → Bodygraph-sectie: monopool + bewustzijnskristallen-laag op de chart. */
  protected readonly showMonopoleAndCrystals = signal(false);

  /**
   * Kruis-assen (planeet↔tegenplaneet, personality + design) van de geladen chart:
   * Zon/Aarde voor het incarnatiekruis, Noord-/Zuidknoop voor de life cross. Leeg
   * wanneer de laag uit staat of de chart de planeten niet bevat.
   */
  private computeMandalaCrossPairs(
    enabled: boolean,
    planetKeyA: 'sun' | 'northNode',
    planetKeyB: 'earth' | 'southNode',
  ): readonly (readonly [number, number])[] {
    if (!enabled) {
      return [];
    }
    const activations = this.displayPlanetaryActivations();
    const idA = getHdPlanetDisplayConfig(planetKeyA).planetId;
    const idB = getHdPlanetDisplayConfig(planetKeyB).planetId;
    const pairs: (readonly [number, number])[] = [];
    for (const side of ['personality', 'design'] as const) {
      const a = findActivationFromList(activations, side, idA);
      const b = findActivationFromList(activations, side, idB);
      if (a && b) {
        pairs.push([a.gate, b.gate]);
      }
    }
    return pairs;
  }

  private readonly mandalaPatternIncarnationCrossPairs = computed(() =>
    this.computeMandalaCrossPairs(this.mandalaPatternOptions().showIncarnationCross, 'sun', 'earth'),
  );

  private readonly mandalaPatternLifeCrossPairs = computed(() =>
    this.computeMandalaCrossPairs(this.mandalaPatternOptions().showLifeCross, 'northNode', 'southNode'),
  );

  /** Patronen-overlay voor het hoofdwiel (Mandala-tab); null wanneer alle lagen uit staan. */
  protected readonly mandalaPatternsViewData = computed<HdMandalaPatternsViewData | null>(() => {
    const options = this.mandalaPatternOptions();
    if (
      !options.showChannelLinks
      && !options.showPartnerLinks
      && !options.showIncarnationCross
      && !options.showLifeCross
    ) {
      return null;
    }
    return {
      showChannelLinks: options.showChannelLinks,
      showPartnerLinks: options.showPartnerLinks,
      incarnationCrossPairs: this.mandalaPatternIncarnationCrossPairs(),
      lifeCrossPairs: this.mandalaPatternLifeCrossPairs(),
    };
  });

  protected readonly locksKeysCompareLeftViewData = computed<HdMandalaLocksKeysViewData | null>(() =>
    this.locksKeysCompareActive() ? this.buildLocksKeysViewDataFor(this.locksKeysState.periodPair()[0]) : null,
  );

  protected readonly locksKeysCompareRightViewData = computed<HdMandalaLocksKeysViewData | null>(() =>
    this.locksKeysCompareActive() ? this.buildLocksKeysViewDataFor(this.locksKeysState.periodPair()[1]) : null,
  );

  /** Vergelijk-wielen: chart-scope toont de chart in beide wielen, algemeen een neutraal wiel. */
  protected readonly locksKeysCompareConfiguration = computed<HdBodygraphConfig>(() =>
    this.locksKeysState.effectiveScope() === 'chart'
      ? this.displayConfiguration()
      : this.locksKeysEmptyConfiguration,
  );

  protected readonly locksKeysComparePlanetaryActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => {
      if (this.locksKeysState.effectiveScope() !== 'chart') {
        return [];
      }
      const partner = this.locksKeysState.partnerPlanetaryActivations();
      return partner.length === 0
        ? this.displayPlanetaryActivations()
        : [...this.displayPlanetaryActivations(), ...partner];
    },
  );

  /**
   * De vergelijkweergave verbergt de planeetgrids standaard: de twee wielen
   * hebben de breedte nodig (bijstuurronde 1). Wie ze op een groot scherm toch
   * wil, zet ze met de gewone grid-knop weer aan — deze sessie-lokale override
   * laat de persistente `showPlanetGrid`-voorkeur onaangeroerd, zodat de
   * enkelvoudige weergave (en de rest van de app) automatisch zijn eigen
   * instelling terugkrijgt.
   */
  private readonly locksKeysCompareGridOverride = signal(false);

  protected readonly effectiveShowPlanetGrid = computed(() =>
    this.locksKeysCompareActive() ? this.locksKeysCompareGridOverride() : this.showPlanetGrid(),
  );

  /** Elke wissel van/naar de vergelijkweergave start weer met verborgen grids. */
  private readonly resetLocksKeysCompareGridOverride = effect(() => {
    this.locksKeysCompareActive();
    untracked(() => this.locksKeysCompareGridOverride.set(false));
  });

  protected override togglePlanetGrid(): void {
    if (this.locksKeysCompareActive()) {
      this.locksKeysCompareGridOverride.update(visible => !visible);
      return;
    }
    super.togglePlanetGrid();
  }

  /** Infoblok tussen de twee vergelijk-wielen (periode-labels, kruisen, key-tellers). */
  protected readonly locksKeysCompareInfo = computed(() => {
    if (!this.locksKeysCompareActive()) {
      return null;
    }
    const lang = this.pageContentLang.effectiveLang();
    const t = (key: string, params?: Record<string, unknown>) =>
      this.pageGateInfoService.locksKeysTextIn(key, lang, params);
    const chartScope = this.locksKeysState.effectiveScope() === 'chart';
    const pair = this.locksKeysState.periodPair();
    // De 2027-titel enkel bij het paar rond de shift; elk ander paar krijgt de algemene tekst.
    const isShiftPair = HD_LOCKS_KEYS_SHIFT_PERIOD_IDS.every(id => pair.includes(id));
    return {
      title: t(isShiftPair ? 'compare.title' : 'compare.titleGeneric'),
      subtitle: t(isShiftPair ? 'compare.subtitle' : 'compare.subtitleGeneric'),
      sharedLocksNote: t('compare.sharedLocksNote'),
      keysActiveLabel: t('compare.keysActiveLabel'),
      periods: pair.map(periodId => {
        const text = hdLocksKeysPeriodText(this.pageGateInfoService, lang, periodId);
        return {
          id: periodId,
          label: text.label,
          crosses: text.crosses,
          keynote: text.keynote,
          activatedCount: chartScope ? this.locksKeysState.overviewFor(periodId).activatedKeyCount : null,
        };
      }),
    };
  });

  /** Library-chart-id van de actieve tab; null = nog niet opgeslagen (sterretje). */
  protected readonly savedChartId = signal<string | null>(null);
  /** Chart-type voor de save-actie (Standard/JustNow/…) van de actieve tab. */
  protected readonly activeChartType = signal<PlatformChartType>(PlatformChartType.Standard);
  /** Opent/sluit de aanmaak-dialog voor een nieuwe (berekende) chart. */
  protected readonly createDialogOpen = signal(false);
  /** Niet-opgeslagen wijzigingen aan een reeds opgeslagen chart (bv. na recalc). */
  protected readonly pendingSaveChanges = signal(false);

  /**
   * Gedeelde tab: enkel een standaardchart is als eigen chart op te slaan. Cycle-,
   * composite- en momentcharts hangen aan broncharts van de deler (relaties, natal
   * openen, herberekenen) die de kijker niet heeft (Steven 2026-10-20); die rekent
   * hij zelf wanneer hij de broncharts heeft.
   */
  protected readonly sharedTabSaveable = computed(() => this.activeChartType() === PlatformChartType.Standard);

  /** Save-knop/Ctrl-S: nieuwe (niet-opgeslagen) chart óf opgeslagen chart met wijzigingen. */
  protected readonly canSaveActiveChart = computed(
    () => this.activePreset() != null
      && (this.savedChartId() == null || this.pendingSaveChanges())
      && (!this.isSharedTab() || this.sharedTabSaveable()),
  );

  // ── Gedeelde chart in de actieve tab (spec 2026-10-20-chart-share §4.6) ──
  /** Token van de deellink waarmee de actieve tab geopend werd; null = eigen/onopgeslagen chart. */
  protected readonly activeSharedToken = signal<string | null>(null);
  /** Voornaam van de deler (banner). */
  protected readonly activeSharedByName = signal<string | null>(null);
  protected readonly isSharedTab = computed(() => this.activeSharedToken() != null);
  /** Basis-/Geavanceerd-panelen: gedeelde tab → Relaties en Momenten tonen niets. */
  protected readonly panelViewMode = computed<HdChartPanelViewMode>(() => (this.isSharedTab() ? 'shared' : 'owner'));
  /** Deelknop: enkel op een opgeslagen eigen chart. */
  // Delen hangt aan de UPAM-feature (HdChartShareAccessService): zonder feature geen deelknop.
  protected readonly canShareActiveChart = computed(
    () => this.shareAccess.featureGranted() && this.savedChartId() != null && !this.isSharedTab(),
  );
  /** Doel van de deeldialoog; null = gesloten. */
  protected readonly shareDialogTarget = signal<HdChartShareTarget | null>(null);

  // Per-tab selecties die vroeger in chart-presets leefden (spec §4.2/§5).
  protected readonly selectedGameId = signal<HdChartGameId>('gate-guessing');
  protected readonly selectedGameVariantId = signal<HdChartGameVariantId>('numbers');
  protected readonly gateRangeSortEnabled = signal(false);
  protected readonly gateRangeStart = signal<number>(HD_BODYGRAPH_GATES[0]);
  protected readonly gateRangeEnd = signal<number>(HD_BODYGRAPH_GATES[HD_BODYGRAPH_GATES.length - 1]);
  /** Doel-chart (library-id) van "Teken je chart" — gekozen in de spelkeuze-dialog. */
  protected readonly selectedDrawChartTargetId = signal('');

  // Globale dropdown-selecties (spec §6).
  protected readonly colorConfigPresetId = this.settings.colorConfigPresetId;
  protected readonly planetGridColorConfigPresetId = this.settings.planetGridColorConfigPresetId;
  // Start gesloten: sinds de chart manager is het settings-panel geen verplichte
  // eerste stap meer; de gebruiker opent het zelf wanneer nodig.
  protected readonly settingsPanelOpen = this.settings.panelOpen;
  protected readonly includeNonHdFeatures = this.settings.includeNonHdFeatures;
  protected readonly loadingPreset = signal(false);

  /**
   * Verplaats-tool voor de sleepbare overlays (planeetblokjes + maan-transit
   * kaarten, plan 2026-07-07) — wederzijds exclusief met annotatie (spec §8).
   */
  protected readonly moveToolActive = signal(false);

  /** Pins van de actieve chart; leven op de preset zelf (spec §9). */
  protected readonly gateBlockPins = computed<readonly HdGateBlockPin[]>(
    () => this.activePreset()?.gateBlockLayout?.pins ?? [],
  );

  /** Eén bron voor de zichtbaarheid van de blokjes-laag (template + tool-guard). */
  protected readonly gateBlockLayerVisible = computed(
    () => this.showGateBlocks() && this.hasPlanetaryActivations() && !this.gameActive()
      && !this.tripleChartView(),
  );

  /** Pins van de maan-transit kaartstacks; leven op de preset (plan 2026-07-07). */
  protected readonly moonTransitPins = computed<readonly HdMoonTransitPin[]>(
    () => this.activePreset()?.moonTransitLayout?.pins ?? [],
  );

  /** Pins van de just-now transit-kaarten; leven op de preset (spec notable moments §4.1). */
  protected readonly justNowTransitPins = computed<readonly HdJustNowTransitPinV1[]>(
    () => this.activePreset()?.justNowTransitLayout?.pins ?? [],
  );

  /** De just-now kaartenlaag rendert zodra de overlay-mandala zichtbaar is mét view-data. */
  protected readonly justNowTransitCardsVisible = computed(
    () => this.justNowTransitMandalaVisible() && this.justNowTransitViewData() != null,
  );

  /** De kaartenlaag rendert zodra de maan-transit modus view-data heeft. */
  protected readonly moonTransitCardsVisible = computed(() => this.moonTransitViewData() != null);

  // Mandala-wiel weergave (Fase 3+): chart-only (default), mandala-only of beide.
  protected readonly chartViewMode = signal<HdChartViewMode>('chart');

  // ── Maan-transit modus (spec 2026-07-06-hd-moon-transit-chart-design.md) ──

  private readonly pageGateInfoService = inject(HdGateInfoService);
  private readonly pagePlanetInfoService = inject(HdPlanetInfoService);

  /** Connectorstijl van de transitlijnen (globaal persistente setting, §6.5). */
  protected readonly moonTransitConnectorStyle = this.settings.moonTransitConnectorStyle;

  /** Markering van de maanpoorten in het wiel (globaal persistent, ronde 3). */
  protected readonly moonTransitGateMarkerStyle = this.settings.moonTransitGateMarkerStyle;

  /** Bronkleuren van de Just Now-transit overlay (globaal persistent). */
  protected readonly justNowTransitColorMode = this.settings.justNowTransitColorMode;

  /** Afzonderlijke laadstatus: de gewone Just Now-chartknop blijft onafhankelijk. */
  protected readonly justNowTransitLoading = signal(false);

  /**
   * Bevroren API-snapshot voor de actieve overlaysessie. Er is bewust geen
   * polling: uit- en opnieuw aanzetten haalt een nieuw huidig moment op.
   */
  private readonly justNowTransitSnapshot = signal<HdTransitOverlaySnapshot | null>(null);

  /** Transit-blok van de actieve tab; niet-null ⇔ deze tab toont een bewaard moment (snapshot-chart). */
  protected readonly activeTransitInfo = computed(() => this.activePreset()?.transit ?? null);

  /**
   * Document-gedreven overlay-snapshot van een geladen snapshot-tab: zelfde
   * vorm als de live snapshot, maar zonder fetch — het moment staat vast in
   * het document (spec notable moments §4.3).
   */
  private readonly documentTransitSnapshot = computed(() => {
    const transit = this.activeTransitInfo();
    if (transit == null) {
      return null;
    }
    return {
      personalityActivations: filterHdJustNowPersonalityActivations(transit.personalityActivations ?? []),
      personalityChiron: this.resolveChironFromMetadata(transit.momentMetadata ?? undefined, 'personality'),
      momentMetadata: transit.momentMetadata ?? null,
      fetchedAt: new Date(transit.momentUtc),
    };
  });

  /**
   * Eén bron voor alle overlay-visuals: een tijdslider-sample wint (dat ís de
   * gekozen stand), dan de snapshot-tab (daar is de live modus niet
   * beschikbaar), anders de live overlaysessie.
   */
  private readonly effectiveJustNowSnapshot = computed(() =>
    this.transitSampleSnapshot()
      ?? this.documentTransitSnapshot()
      ?? (this.justNowTransitMode() ? this.justNowTransitSnapshot() : null));

  // ── Tijdslider + momentsamples (spec momenten stap 4) ─────────────────────

  private readonly viewTimeService = inject(TransitViewTimeService);
  private readonly egiPageNavigation = inject(EgiPageNavigationService);

  /** Volledige berekening van de huidige sliderstand; voedt de overlay met voorrang. */
  private readonly transitSampleSnapshot = signal<HdTransitOverlaySnapshot | null>(null);
  protected readonly transitSampleLoading = signal(false);
  /** Sliderstand (UTC-iso); null = het anker van de actieve context. */
  private readonly timeSliderValueUtc = signal<string | null>(null);
  /** Onthouden sliderstand per range-tab, sessie-lokaal (beslispunt 5). */
  private readonly rangeSliderStandByTab = new Map<string, string>();
  private transitSampleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Meest recente sliderstand die nog op een berekening wacht (trailing van de throttle). */
  private pendingSampleUtc: string | null = null;
  /** Start van de laatste berekening — bepaalt de throttle-wachttijd. */
  private lastSampleStartMs = 0;
  /** Laatste-wint-guard over throttle én lopende fetch. */
  private transitSampleRequestId = 0;
  private readonly transitSampleCache = new Map<string, HdTransitOverlaySnapshot>();
  /** Alleen een pin die wíj zetten mag ook weer opgeheven worden. */
  private sliderPinnedViewTime = false;

  /**
   * Context van de tijdslider boven de chart: een range-tab (bewaard moment
   * mét einde) of de live just-now-overlay op een gewone chart. Het anker is
   * de neutrale stand: bereik-start (beslispunt 2/3) of het nu-moment van de
   * overlaysessie (snap-terug, wens Steven).
   */
  protected readonly timeSliderState = computed<
    { mode: 'range' | 'live'; startUtc: string; endUtc: string; anchorUtc: string } | null
  >(() => {
    const transit = this.activeTransitInfo();
    if (transit?.momentEndUtc) {
      return { mode: 'range', startUtc: transit.momentUtc, endUtc: transit.momentEndUtc, anchorUtc: transit.momentUtc };
    }
    const snapshot = this.justNowTransitMode() && transit == null ? this.justNowTransitSnapshot() : null;
    if (snapshot != null) {
      const anchorMs = snapshot.fetchedAt.getTime();
      return {
        mode: 'live',
        startUtc: new Date(anchorMs - LIVE_SLIDER_WINDOW_MS).toISOString(),
        endUtc: new Date(anchorMs + LIVE_SLIDER_WINDOW_MS).toISOString(),
        anchorUtc: snapshot.fetchedAt.toISOString(),
      };
    }
    return null;
  });

  protected readonly timeSliderValue = computed(() => this.timeSliderValueUtc() ?? this.timeSliderState()?.anchorUtc ?? null);

  protected readonly timeSliderAtAnchor = computed(() => {
    const state = this.timeSliderState();
    const value = this.timeSliderValue();
    return state != null && value != null && Date.parse(value) === Date.parse(state.anchorUtc);
  });

  /**
   * Contextwissel (tab, modus aan/uit, andere chart): sliderstand en sample
   * terugzetten naar wat bij de nieuwe context hoort. Een range-tab herstelt
   * zijn onthouden stand en haalt meteen een sample (het document draagt bij
   * een range lege activaties); de live-modus start neutraal op het nu-moment.
   */
  private readonly syncTimeSliderContext = effect(() => {
    const state = this.timeSliderState();
    const tabId = this.tabsService.activeTabId();
    untracked(() => {
      this.cancelPendingTransitSample();
      if (state == null) {
        this.timeSliderValueUtc.set(null);
        this.clearTransitSample();
        return;
      }
      if (state.mode === 'range') {
        const remembered = (tabId ? this.rangeSliderStandByTab.get(tabId) : undefined) ?? null;
        this.timeSliderValueUtc.set(remembered);
        this.queueTransitSample(remembered ?? state.anchorUtc, { immediate: true });
        return;
      }
      this.timeSliderValueUtc.set(null);
      this.clearTransitSample();
    });
  });

  protected handleTimeSliderValueChange(utcIso: string): void {
    this.applyTimeSliderValue(utcIso, { immediate: false });
  }

  /** Discrete stand (loslaten, stapknop, afspeeltick, momentkiezer): meteen doorrekenen. */
  protected handleTimeSliderValueCommitted(utcIso: string): void {
    this.applyTimeSliderValue(utcIso, { immediate: true });
  }

  private applyTimeSliderValue(utcIso: string, options: { immediate: boolean }): void {
    const state = this.timeSliderState();
    if (state == null) {
      return;
    }
    this.timeSliderValueUtc.set(utcIso);
    if (state.mode === 'range') {
      const tabId = this.tabsService.activeTabId();
      if (tabId) {
        this.rangeSliderStandByTab.set(tabId, utcIso);
      }
    } else if (Date.parse(utcIso) === Date.parse(state.anchorUtc)) {
      // Terug op het nu-moment: het originele snapshot volstaat, geen sample.
      this.clearTransitSample();
      return;
    }
    this.queueTransitSample(utcIso, options.immediate ? { immediate: true } : undefined);
  }

  /**
   * Tijdlijn-knop in de Momenten-sectie of de manager → de levenstijdlijn als
   * dóórklikpagina van deze chart-pagina (breadcrumb-terugknop springt terug
   * naar de chart, wens Steven). Vraagt de manager-dialoog een ándere chart,
   * dan wordt die eerst hier geopend zodat de terugknop op de juiste chart
   * uitkomt. Fallback: het gewone menu-item (bv. zolang de doorklikpagina nog
   * niet in het beheer geconfigureerd is).
   */
  /**
   * Openen vanuit een kalender (sectie-kalender of tijdlijnpagina-kalender):
   * nieuwe tab mét het Notities-paneel actief, ook voor gewone charts
   * (spec levenstijdlijn §2.9).
   */
  protected handleChartOpenWithNotes(chartId: string): void {
    this.forceNotesTabOnce.set(true);
    void this.loadChartFromLibrary(chartId, /* inNewTab */ true);
  }

  /**
   * Brein-knop in de Momenten-sectie: het paneel "Mijn mind zegt" van de actieve
   * chart (spec 2026-10-22-mind-logboek §5), gedockt zoals de andere info-panelen.
   * Het paneel leest de relaties uit de gedeelde datacache en opent een notitie
   * via dezelfde route als de sectie-kalender (nieuwe tab mét Notities).
   */
  protected handleMindLogRequested(chartId: string): void {
    const preset = this.savedChartId() === chartId ? (this.activePreset() ?? null) : null;
    this.floatingPanels.openOrFocus<HdMindLogPanelComponent, HdMindLogPanelData>({
      key: `mind-log:${chartId}`,
      component: HdMindLogPanelComponent,
      data: {
        chartId,
        chartName: preset?.name ?? this.activePreset()?.name ?? '',
        preset,
        openChartWithNotes: id => this.handleChartOpenWithNotes(id),
      },
      dockTab: { label: this.pageTransloco.translate('humanDesign.mindLog.tabLabel', {}, this.pageContentLang.effectiveLang()) },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  protected async handleTimelineRequested(chartId: string): Promise<void> {
    if (chartId !== this.savedChartId()) {
      await this.loadChartFromLibrary(chartId, true);
      if (this.savedChartId() !== chartId) {
        return; // laden mislukt of afgebroken (bv. lopend spel)
      }
    }
    if (this.egiPageNavigation.navigateToClickTroughPage('lifeTimeline', { chartId }) === false) {
      this.egiPageNavigation.navigateToPage(HD_LIFE_TIMELINE_PAGE_ID, { chartId });
    }
  }

  /** Ankerknop: range springt naar bereik-start (mét sample), live snapt terug naar het nu-moment. */
  protected handleTimeSliderAnchorRequested(): void {
    const state = this.timeSliderState();
    if (state == null) {
      return;
    }
    this.timeSliderValueUtc.set(null);
    if (state.mode === 'range') {
      const tabId = this.tabsService.activeTabId();
      if (tabId) {
        this.rangeSliderStandByTab.delete(tabId);
      }
      this.queueTransitSample(state.anchorUtc, { immediate: true });
      return;
    }
    this.clearTransitSample();
  }

  /**
   * Throttle met trailing: de eerste beweging rekent meteen, daarna hoogstens
   * één berekening per interval met telkens de nieuwste stand — zo beweegt de
   * chart al mee tijdens het slepen en komt de eindstand altijd nog.
   */
  private queueTransitSample(utcIso: string, options?: { immediate?: boolean }): void {
    if (options?.immediate) {
      this.cancelPendingTransitSample();
      this.pendingSampleUtc = utcIso;
      this.flushPendingTransitSample();
      return;
    }
    this.pendingSampleUtc = utcIso;
    if (this.transitSampleTimer != null) {
      return; // de geplande flush pakt vanzelf de nieuwste stand
    }
    const wait = Math.max(0, TRANSIT_SAMPLE_THROTTLE_MS - (Date.now() - this.lastSampleStartMs));
    this.transitSampleTimer = setTimeout(() => {
      this.transitSampleTimer = null;
      this.flushPendingTransitSample();
    }, wait);
  }

  private flushPendingTransitSample(): void {
    const utcIso = this.pendingSampleUtc;
    if (utcIso == null) {
      return;
    }
    this.pendingSampleUtc = null;
    this.lastSampleStartMs = Date.now();
    void this.fetchTransitSample(utcIso);
  }

  private cancelPendingTransitSample(): void {
    if (this.transitSampleTimer != null) {
      clearTimeout(this.transitSampleTimer);
      this.transitSampleTimer = null;
    }
    this.pendingSampleUtc = null;
  }

  private clearTransitSample(): void {
    this.cancelPendingTransitSample();
    this.transitSampleRequestId++;
    this.transitSampleSnapshot.set(null);
    this.transitSampleLoading.set(false);
    this.releaseSliderViewTimePin();
  }

  /**
   * Volledige momentberekening van een sliderstand: zelfde compute-pad als de
   * moment-dialog en de cycles (natal-kern + locatie uit het actieve document),
   * op minuut afgerond en gememoïseerd. Laatste-wint: een nieuwere aanvraag
   * maakt het resultaat van een oudere stil ongeldig.
   */
  private async fetchTransitSample(utcIso: string): Promise<void> {
    const preset = this.activePreset();
    if (preset == null) {
      return;
    }
    const minuteMs = Math.round(Date.parse(utcIso) / 60_000) * 60_000;
    // De stand staat al in beeld (bv. commit direct na de throttled sleep-berekening).
    if (this.transitSampleSnapshot()?.fetchedAt.getTime() === minuteMs) {
      return;
    }
    const requestId = ++this.transitSampleRequestId;
    const minuteUtc = new Date(minuteMs).toISOString();
    const cacheKey = `${this.savedChartId() ?? preset.name}|${minuteUtc}`;
    const cached = this.transitSampleCache.get(cacheKey);
    if (cached != null) {
      this.applyTransitSample(cached);
      return;
    }
    this.transitSampleLoading.set(true);
    try {
      const core = extractCycleSourceCore(preset.birthData);
      const location = (preset.birthData as { location?: { city?: string | null; country?: string | null } }).location ?? {};
      const doc = await this.chartLibrary.calculateFromUtc({
        name: preset.name,
        utc: minuteUtc,
        timezone: core.timezone || 'UTC',
        location: { city: location.city ?? null, country: location.country ?? null, lat: core.latitude, lon: core.longitude },
      });
      if (requestId !== this.transitSampleRequestId) {
        return;
      }
      const sample: HdTransitOverlaySnapshot = {
        personalityActivations: filterHdJustNowPersonalityActivations(doc.planetaryActivations ?? []),
        personalityChiron: this.resolveChironFromMetadata(doc.metadata, 'personality'),
        momentMetadata: doc.metadata ?? null,
        fetchedAt: new Date(minuteUtc),
      };
      this.storeTransitSample(cacheKey, sample);
      this.applyTransitSample(sample);
    } catch {
      if (requestId === this.transitSampleRequestId) {
        this.notifier.error(this.pageTransloco.translate(
          'humanDesign.chart.timeSlider.sampleError',
          undefined,
          this.pageContentLang.effectiveLang(),
        ));
      }
    } finally {
      if (requestId === this.transitSampleRequestId) {
        this.transitSampleLoading.set(false);
      }
    }
  }

  private storeTransitSample(key: string, sample: HdTransitOverlaySnapshot): void {
    if (this.transitSampleCache.size >= TRANSIT_SAMPLE_CACHE_MAX) {
      const oldest = this.transitSampleCache.keys().next().value;
      if (oldest != null) {
        this.transitSampleCache.delete(oldest);
      }
    }
    this.transitSampleCache.set(key, sample);
  }

  private applyTransitSample(sample: HdTransitOverlaySnapshot): void {
    this.transitSampleSnapshot.set(sample);
    // De app-weergavetijd pint mee: lanes, info-card en Nu & Straks (indien
    // geopend) tonen dezelfde stand.
    this.viewTimeService.pin(moment(sample.fetchedAt));
    this.sliderPinnedViewTime = true;
  }

  private releaseSliderViewTimePin(): void {
    if (!this.sliderPinnedViewTime) {
      return;
    }
    this.sliderPinnedViewTime = false;
    this.viewTimeService.unpin();
  }

  // ── Just Now-paneel (spec 2026-10-14) ─────────────────────────────────────

  private readonly justNowPanelState = inject(HdJustNowPanelStateService);
  /** Dedup-key van het gedockte Just Now-paneel. */
  private readonly justNowPanelKey = 'just-now';

  protected readonly justNowPanelOpen = computed(() =>
    this.transitsFloatingPanels.panels().some(panel => panel.key === this.justNowPanelKey),
  );

  /**
   * Het paneel hoort bij de live overlaymodus zoals het L&K-paneel bij de L&K-
   * modus (besluit 1, 2026-10-14): paneel sluiten = overlay uit. Bewaarde
   * momentcharts (snapshot-tabs) krijgen het paneel niet (besluit 2).
   */
  private readonly closeJustNowModeWithPanel = effect(() => {
    // Op phone is het paneel een bottom-sheet en vervangt elke volgende sheet
    // (bv. de poortinfo vanuit het paneel) de vorige: daar mag het verdwijnen
    // van het paneel de overlay niet uitzetten.
    if (this.justNowTransitMode() && !this.justNowPanelOpen() && !this.viewport.isPhone()) {
      untracked(() => this.deactivateJustNowTransitMode());
    }
  });

  /**
   * Spiegelt wat het paneel nodig heeft naar zijn state-service: de natal chart
   * zoals getoond, de transitactivaties van de effectieve snapshot (een
   * slider-sample wint, zoals voor de overlay) en het moment. Modus uit = leeg.
   */
  private readonly syncJustNowPanelState = effect(() => {
    if (!this.justNowTransitMode()) {
      untracked(() => this.justNowPanelState.clear());
      return;
    }
    const configuration = this.displayConfiguration();
    const planetary = this.displayPlanetaryActivations();
    const transit = this.justNowTransitActivations();
    const momentUtc = this.effectiveJustNowSnapshot()?.fetchedAt.toISOString();
    const chartName = this.activePreset()?.name ?? '';
    untracked(() => {
      this.justNowPanelState.natalConfiguration.set(configuration);
      this.justNowPanelState.natalPlanetaryActivations.set(planetary);
      this.justNowPanelState.transitActivations.set(transit);
      this.justNowPanelState.momentUtc.set(momentUtc);
      this.justNowPanelState.chartName.set(chartName);
    });
  });

  private openJustNowPanel(): void {
    this.transitsFloatingPanels.openOrFocus<HdJustNowPanelComponent, undefined>({
      key: this.justNowPanelKey,
      component: HdJustNowPanelComponent,
      data: undefined,
      dockTab: { label: HD_JUST_NOW_PANEL_TITLE, icon: HD_JUST_NOW_ICONS.transit },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  // ── Geboortevenster (spec 2026-10-16) ───────────────────────────────────

  private readonly birthWindowState = inject(HdBirthWindowStateService);
  private readonly birthWindowPanelKey = HD_BIRTH_WINDOW_PANEL_KEY;
  protected readonly birthWindowPanelOpen = computed(() =>
    this.transitsFloatingPanels.panels().some(panel => panel.key === this.birthWindowPanelKey),
  );

  /**
   * Standaardcharts en charts van een moment (Just Now, Rave New Year; Steven
   * 2026-10-25: daar is geen betrouwbaarheid, maar zien wat er rond het moment
   * wisselt is even nuttig), met geboortemoment en planeetactivaties, en
   * bewaarde momenten (overlay: enkel het momenttijdstip schuift). Cycle charts
   * en composites niet: die liggen vast door de natal chart (Steven 2026-10-25).
   */
  protected readonly birthWindowAvailable = computed(() => {
    if (this.birthWindowMomentPreset() != null) return true;
    const preset = this.activePreset();
    if (preset == null || !HD_BIRTH_WINDOW_CHART_TYPES.has(this.activeChartType())) return false;
    if (preset.cycle != null || preset.composite != null || preset.transit != null) return false;
    if ((preset.planetaryActivations?.length ?? 0) === 0) return false;
    return extractCycleSourceCore(preset.birthData).birthDateUtc != null;
  });

  /**
   * Bewaard moment (Steven 2026-10-25): het venster schuift het tijdstip van
   * het moment, de natal chart blijft als vaste laag (`birth-window-moment.ts`).
   * Een computed, zodat de venster-preset per chart dezelfde referentie houdt
   * (een nieuwe referentie wist kandidaat en preview).
   */
  private readonly birthWindowMomentPreset = computed(() => {
    const preset = this.activePreset();
    return preset != null && this.activeChartType() === PlatformChartType.Transit ? buildHdBirthWindowMomentPreset(preset) : undefined;
  });

  private readonly birthWindowOverlay = computed<HdBirthWindowOverlay | undefined>(() => {
    const preset = this.activePreset();
    return preset != null && this.birthWindowMomentPreset() != null
      ? hdBirthWindowOverlayOf(preset, this.justNowTransitColorMode())
      : undefined;
  });

  /** Exacte transitactivaties per kandidaat-UTC van een bewaard moment (+ metadata voor de Chiron-rij). */
  private readonly birthWindowMomentExactCache = new Map<string, HdBirthWindowMomentExact>();

  // De preview van de chart op de kandidaattijd (spec §3.3) leeft in
  // `HdBirthWindowStateService.previewPreset`: het paneel toont die chart zelf
  // (feedbackronde 1); de hoofdchart, het tab-snapshot, `presetModified` en
  // `pendingSaveChanges` blijven onaangeroerd.
  protected readonly birthWindowConfirmTarget = signal<HdBirthWindowConfirmTarget | null>(null);
  private birthWindowPendingCommitMs: number | undefined;
  private birthWindowPreviewTimer: ReturnType<typeof setTimeout> | undefined;
  private birthWindowPreviewRequestId = 0;
  /** Exacte documenten per kandidaat-UTC (minuut), zodat terugslepen niets opnieuw rekent. */
  private readonly birthWindowExactCache = new Map<string, HdBodygraphChartPreset>();

  /**
   * Spiegelt de actieve chart naar de gedeelde staat zolang het paneel open is
   * en registreert de handlers (preview, overnemen, poortinfo). Een andere
   * chart (of geen) maakt preview en kandidaat ongedaan; paneel dicht = alles leeg.
   */
  private readonly syncBirthWindowState = effect(() => {
    if (!this.birthWindowPanelOpen()) {
      untracked(() => this.resetBirthWindow());
      return;
    }
    const preset = this.activePreset() ?? undefined;
    const available = this.birthWindowAvailable();
    const savedChartId = this.savedChartId() ?? undefined;
    const momentPreset = this.birthWindowMomentPreset();
    const overlay = this.birthWindowOverlay();
    const momentMode = this.activeChartType() === PlatformChartType.JustNow || momentPreset != null;
    untracked(() => {
      const state = this.birthWindowState;
      const next = available ? (momentPreset ?? preset) : undefined;
      if (state.preset() !== next) {
        this.clearBirthWindowPreview();
        state.candidateMs.set(undefined);
      }
      // Overlay vóór de preset: het paneel laadt het venster zodra de preset wijzigt.
      state.overlay.set(overlay);
      state.preset.set(next);
      state.momentMode.set(momentMode);
      state.savedChartId.set(savedChartId);
      state.chartName.set(preset?.name ?? '');
      state.requestHandlers.set({
        previewAt: ms => this.birthWindowPreviewAt(ms),
        commit: ms => this.requestBirthWindowCommit(ms),
        openGateInfo: request => this.handleGateInfoRequested(request),
      });
    });
  });

  /**
   * Chip bij de geboortegegevens in het Geavanceerd-paneel: de enige ingang
   * (Steven 2026-10-17: geen shortcut-knop, de balk moet op phone passen).
   */
  protected handleBirthWindowRequestedFromAdvanced(): void {
    if (!this.birthWindowAvailable()) return;
    this.openBirthWindowPanel();
  }

  private openBirthWindowPanel(): void {
    const titleKey = hdBirthWindowTextKey('title', this.activeChartType() === PlatformChartType.JustNow || this.birthWindowMomentPreset() != null);
    const label = this.pageTransloco.translate<string>(`humanDesign.chart.birthWindow.${titleKey}`, undefined, this.pageContentLang.effectiveLang());
    this.transitsFloatingPanels.openOrFocus<HdBirthWindowPanelComponent, undefined>({
      key: this.birthWindowPanelKey,
      component: HdBirthWindowPanelComponent,
      data: undefined,
      dockTab: { label, icon: HD_BIRTH_WINDOW_ICON },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
      undockPlacement: 'beside-dock',
    });
  }

  private resetBirthWindow(): void {
    this.clearBirthWindowPreview();
    this.birthWindowExactCache.clear();
    this.birthWindowMomentExactCache.clear();
    this.birthWindowState.clear();
    this.birthWindowState.requestHandlers.set(undefined);
  }

  private clearBirthWindowPreview(): void {
    if (this.birthWindowPreviewTimer != null) {
      clearTimeout(this.birthWindowPreviewTimer);
      this.birthWindowPreviewTimer = undefined;
    }
    this.birthWindowPreviewRequestId++;
    this.birthWindowState.previewPreset.set(undefined);
  }

  /**
   * Chart volgt de kandidaattijd (besluit 5): meteen uit de venstersegmenten
   * (poorten/lijnen exact, zonder backend), en op rust het volledige document
   * via `/calculate` (zelfde laatste-wint-patroon als de transit-slider).
   */
  private birthWindowPreviewAt(ms: number | undefined): void {
    if (this.birthWindowPreviewTimer != null) {
      clearTimeout(this.birthWindowPreviewTimer);
      this.birthWindowPreviewTimer = undefined;
    }
    const requestId = ++this.birthWindowPreviewRequestId;
    const preset = this.birthWindowState.preset();
    if (ms === undefined || preset == null) {
      this.birthWindowState.previewPreset.set(undefined);
      return;
    }
    const utcIso = new Date(ms).toISOString();
    if (this.birthWindowState.overlay() != null) {
      this.birthWindowMomentPreviewAt(preset, ms, utcIso, requestId);
      return;
    }
    const exact = this.birthWindowExactCache.get(utcIso);
    if (exact != null) {
      this.birthWindowState.previewPreset.set(exact);
      return;
    }
    const data = this.birthWindowState.data();
    if (data != null && ms >= data.fromMs && ms <= data.toMs) {
      const activations = birthWindowActivationsAt(data, ms);
      if (activations.length > 0) {
        this.birthWindowState.previewPreset.set(buildBirthWindowPreviewPreset(preset, activations));
      }
    }
    this.birthWindowPreviewTimer = setTimeout(() => {
      this.birthWindowPreviewTimer = undefined;
      void this.fetchBirthWindowExactPreview(preset, utcIso, requestId);
    }, BIRTH_WINDOW_PREVIEW_DEBOUNCE_MS);
  }

  private async fetchBirthWindowExactPreview(preset: HdBodygraphChartPreset, utcIso: string, requestId: number): Promise<void> {
    try {
      const fresh = await this.chartLibrary.calculateFromUtc(buildBirthWindowCalculateRequest(preset, utcIso));
      const carried = carryUserHandwork(fresh, preset);
      if (this.birthWindowExactCache.size >= BIRTH_WINDOW_EXACT_CACHE_MAX) {
        const oldest = this.birthWindowExactCache.keys().next().value;
        if (oldest != null) this.birthWindowExactCache.delete(oldest);
      }
      this.birthWindowExactCache.set(utcIso, carried);
      if (requestId !== this.birthWindowPreviewRequestId) return;
      this.birthWindowState.previewPreset.set(carried);
    } catch {
      // De preview uit de segmenten blijft staan; overnemen rekent sowieso opnieuw.
    }
  }

  /**
   * Preview op een bewaard moment: meteen de transitplaneten uit de segmenten
   * (het venster bevat enkel de personality-kant), op rust de exacte via
   * `/calculate`. De natal chart (configuratie) blijft; het paneel tekent de
   * transitlaag zelf.
   */
  private birthWindowMomentPreviewAt(windowPreset: HdBodygraphChartPreset, ms: number, utcIso: string, requestId: number): void {
    const cached = this.birthWindowMomentExactCache.get(utcIso);
    if (cached != null) {
      this.birthWindowState.previewPreset.set(buildHdBirthWindowMomentPreview(windowPreset, cached.activations, true));
      return;
    }
    const data = this.birthWindowState.data();
    if (data != null && ms >= data.fromMs && ms <= data.toMs) {
      const activations = birthWindowActivationsAt(data, ms);
      if (activations.length > 0) {
        this.birthWindowState.previewPreset.set(buildHdBirthWindowMomentPreview(windowPreset, activations, false));
      }
    }
    this.birthWindowPreviewTimer = setTimeout(() => {
      this.birthWindowPreviewTimer = undefined;
      void this.fetchBirthWindowMomentExact(windowPreset, utcIso)
        .then(exact => {
          if (requestId !== this.birthWindowPreviewRequestId) return;
          this.birthWindowState.previewPreset.set(buildHdBirthWindowMomentPreview(windowPreset, exact.activations, true));
        })
        .catch(() => undefined);
    }, BIRTH_WINDOW_PREVIEW_DEBOUNCE_MS);
  }

  /** Exacte transit (personality-kant) van een bewaard moment op een ander tijdstip, gecachet per UTC. */
  private async fetchBirthWindowMomentExact(windowPreset: HdBodygraphChartPreset, utcIso: string): Promise<HdBirthWindowMomentExact> {
    const cached = this.birthWindowMomentExactCache.get(utcIso);
    if (cached != null) return cached;
    const fresh = await this.chartLibrary.calculateFromUtc(buildBirthWindowCalculateRequest(windowPreset, utcIso));
    const exact: HdBirthWindowMomentExact = {
      activations: filterHdJustNowPersonalityActivations(fresh.planetaryActivations ?? []),
      momentMetadata: fresh.metadata ?? null,
    };
    if (this.birthWindowMomentExactCache.size >= BIRTH_WINDOW_EXACT_CACHE_MAX) {
      const oldest = this.birthWindowMomentExactCache.keys().next().value;
      if (oldest != null) this.birthWindowMomentExactCache.delete(oldest);
    }
    this.birthWindowMomentExactCache.set(utcIso, exact);
    return exact;
  }

  /** "Geboortetijd overnemen": eerst bevestigen met oude en nieuwe tijd (besluit 4). */
  private async requestBirthWindowCommit(ms: number): Promise<void> {
    const preset = this.birthWindowState.preset();
    const birthMs = this.birthWindowState.birthMs();
    if (preset == null || birthMs === undefined) return;
    const lang = this.pageContentLang.effectiveLang();
    const zone = this.birthWindowState.timeZoneId();
    this.birthWindowPendingCommitMs = ms;
    this.birthWindowConfirmTarget.set({
      oldText: formatBirthClock(birthMs, zone, lang, { seconds: true, date: true }),
      newText: formatBirthClock(ms, zone, lang, { seconds: true, date: true }),
      deltaText: formatBirthWindowSignedDelta(ms - birthMs, lang),
      unsaved: this.savedChartId() == null,
      moment: this.birthWindowState.momentMode(),
    });
  }

  /**
   * Bevestigd: exact hetzelfde pad als "chart bewerken met een andere tijd"
   * (herberekenen, handwerk meenemen, metadata + document bijwerken); de
   * naam, notities en relaties blijven. Onopgeslagen chart: enkel de tab.
   */
  protected async handleBirthWindowConfirmed(): Promise<void> {
    const ms = this.birthWindowPendingCommitMs;
    const preset = this.birthWindowState.preset();
    const target = this.birthWindowConfirmTarget();
    if (ms === undefined || preset == null || target == null) return;
    const lang = this.pageContentLang.effectiveLang();
    const moment = target.moment ?? false;
    const t = (key: string) =>
      this.pageTransloco.translate<string>(`humanDesign.chart.birthWindow.${hdBirthWindowTextKey(`commit.${key}`, moment)}`, undefined, lang);
    this.birthWindowConfirmTarget.set({ ...target, busy: true });
    this.birthWindowState.committing.set(true);
    try {
      const utcIso = new Date(ms).toISOString();
      const next = this.birthWindowState.overlay() != null
        ? await this.buildBirthWindowMomentCommit(preset, utcIso)
        : await this.buildBirthWindowBirthCommit(preset, utcIso);
      const savedId = this.savedChartId();
      this.clearBirthWindowPreview();
      this.birthWindowState.candidateMs.set(undefined);
      if (savedId != null) {
        const { meta } = await this.chartLibrary.getChartDocument(savedId);
        await this.chartLibrary.updateChart({ id: savedId, name: preset.name, preset: next, current: meta });
        await this.handleChartUpdated(savedId);
      } else {
        this.handlePresetApplied(next);
      }
      this.birthWindowConfirmTarget.set(null);
      this.notifier.success(t('applied'));
    } catch (e) {
      this.notifier.error(e instanceof Error && e.message ? e.message : t('failed'));
      this.birthWindowConfirmTarget.set({ ...target, busy: false });
    } finally {
      this.birthWindowState.committing.set(false);
      this.birthWindowPendingCommitMs = undefined;
    }
  }

  /** Geboortechart: herberekend op de nieuwe tijd, handwerk en invoermodus behouden (bewerkpad, besluit 4). */
  private async buildBirthWindowBirthCommit(preset: HdBodygraphChartPreset, utcIso: string): Promise<HdBodygraphChartPreset> {
    const cached = this.birthWindowExactCache.get(utcIso);
    const fresh = cached ?? carryUserHandwork(await this.chartLibrary.calculateFromUtc(buildBirthWindowCalculateRequest(preset, utcIso)), preset);
    return preserveBirthEntryMode({ ...fresh, name: preset.name }, preset);
  }

  /**
   * Bewaard moment: enkel het moment verschuift (tijdstip, transitactivaties,
   * metadata van het moment); de natal chart, naam, notities en momentdetails
   * blijven. De backend leidt het momenttijdstip van de chart af uit het
   * transit-blok (`ReplaceChartDocumentHandler`).
   */
  private async buildBirthWindowMomentCommit(windowPreset: HdBodygraphChartPreset, utcIso: string): Promise<HdBodygraphChartPreset> {
    const active = this.activePreset();
    if (active?.transit == null) throw new Error(this.pageTranslate('humanDesign.chart.birthWindow.commit.failed'));
    const exact = await this.fetchBirthWindowMomentExact(windowPreset, utcIso);
    return applyHdBirthWindowMomentTime(active, utcIso, exact);
  }

  /** Overlay-visuals actief: live modus óf een geladen snapshot-tab. */
  protected readonly justNowTransitVisualsActive = computed(() => this.effectiveJustNowSnapshot() != null);

  /** In deze modus betekent `false` chart-only en `true` chart + mandala. */
  protected readonly justNowTransitMandalaVisible = signal(false);

  protected readonly justNowTransitStatusTooltip = computed<string | null>(() => {
    const snapshot = this.effectiveJustNowSnapshot();
    if (snapshot == null) {
      return null;
    }
    const pad = (value: number) => String(value).padStart(2, '0');
    const at = snapshot.fetchedAt;
    return this.pageTransloco.translate(
      'humanDesign.chart.justNowTransit.activeTooltip',
      { time: `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}` },
      this.pageContentLang.effectiveLang(),
    );
  });

  private readonly justNowTransitHoverTarget = signal<
    | { readonly kind: 'formation'; readonly channelKey: string }
    | { readonly kind: 'gate'; readonly gate: number }
    | null
  >(null);

  /**
   * De overlayknop is enkel zinvol bovenop een geladen chart, en (voorlopig)
   * nooit op een cycle- óf composite-tab (spec §10; composite-uitsluiting
   * 2026-08-20): die tabs dragen al hun eigen twee-kleurenoverlay.
   */
  protected readonly justNowTransitAvailable = computed(
    () => this.displayConfiguration().activations.length > 0
      && this.activeCycleInfo() == null
      && this.activeCompositeInfo() == null
      // Snapshot-tab: de overlay staat er vast in het document — de live
      // toggle is daar zinloos en zou het bewaarde moment overschrijven.
      && this.activeTransitInfo() == null,
  );

  /**
   * Tab- of chartwissel naar een context waar een transit-modus niet
   * beschikbaar is (cycle-/composite-tab, chart zonder activaties) schakelt
   * een nog actieve modus meteen uit: de knop staat daar disabled, dus de
   * modus mag nooit "blijven hangen" (feedback 2026-08-20). Dekt ook de
   * tab-switch-flow, die — anders dan een chart-load — niet via
   * {@link handlePresetApplied} loopt.
   */
  /**
   * Snapshot-tab: de mandala-zichtbaarheid van de overlay volgt de weergave-
   * modus van de tab (de live modus zet dit in de toggle-flow; een geladen
   * snapshot heeft geen toggle-moment).
   */
  private readonly syncSnapshotTabMandalaVisible = effect(() => {
    if (this.activeTransitInfo() == null) {
      return;
    }
    const wantsMandala = this.chartViewMode() !== 'chart';
    untracked(() => this.justNowTransitMandalaVisible.set(wantsMandala));
  });

  private readonly deactivateUnavailableTransitModes = effect(() => {
    if (this.justNowTransitMode() && !this.justNowTransitAvailable()) {
      untracked(() => this.deactivateJustNowTransitMode());
    }
    if (this.moonTransitMode() && !this.moonTransitAvailable()) {
      untracked(() => this.deactivateMoonTransitMode());
    }
    if (this.locksKeysMode() && !this.locksKeysAvailable()) {
      untracked(() => this.deactivateLocksKeysMode());
    }
  });

  private readonly justNowTransitOverview = computed(() => {
    const snapshot = this.effectiveJustNowSnapshot();
    if (snapshot == null) {
      return null;
    }
    return buildHdJustNowTransitOverview({
      baseConfiguration: this.displayConfiguration(),
      basePlanetaryActivations: this.displayPlanetaryActivations(),
      transitPersonalityActivations: snapshot.personalityActivations,
      baseRenderState: this.displayRenderState(),
    });
  });

  protected readonly justNowTransitOverlay = computed<HdBodygraphTransitOverlay | null>(() => {
    const overview = this.justNowTransitOverview();
    if (overview == null) {
      return null;
    }
    return {
      activations: overview.transitActivations,
      colorMode: this.justNowTransitColorMode(),
    };
  });

  protected readonly justNowTransitActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => this.justNowTransitOverview()?.transitActivations ?? [],
  );

  // ── Cycle charts (spec cycle charts §10) ──────────────────────────────────

  /** Rechter dock-area (Basis/Geavanceerd/Editor) — voor de cycle-default-tab. */
  private readonly rightDockArea = viewChild(DockAreaComponent);

  /**
   * Laatst verwerkte actieve preset van het auto-Geavanceerd-effect (constructor
   * punt 8) — dedupe zodat elke getoonde cycle-preset precies één keer de
   * Geavanceerd-tab voorselecteert.
   */
  private lastAutoAdvancedPreset: HdBodygraphChartPreset | null = null;

  /**
   * Eenmalig het Notities-paneel forceren voor de eerstvolgende getoonde
   * preset: openen vanuit een kalender (?openNotes=1 of de sectie-kalender)
   * selecteert Notities óók voor gewone charts (spec levenstijdlijn §2.9).
   */
  private readonly forceNotesTabOnce = signal(false);

  /** Cycle-blok van de actieve tab; niet-null ⇔ deze tab toont een cycle chart. */
  protected readonly activeCycleInfo = computed(() => this.activePreset()?.cycle ?? null);

  /** Composite-blok van de actieve tab; niet-null ⇔ deze tab toont een composite chart. */
  protected readonly activeCompositeInfo = computed(() => this.activePreset()?.composite ?? null);

  /**
   * Cycle- óf composite-tab: de "gecombineerde" tabs met vaste
   * twee-kleurenoverlay en vaste D·P-grids voor beide kanten. Stuurt de
   * shortcut-bar-vergrendelingen (blokjes, Resonance-LPS, PHS-weergave) en
   * het bron-drieluik (feedback 2026-08-23).
   */
  protected readonly cycleOrCompositeActive = computed(
    () => this.activeCycleInfo() != null || this.activeCompositeInfo() != null,
  );

  /**
   * Blokjes-laag uit zodra de actieve tab een cycle- of composite-chart toont
   * (feedback 2026-08-23): de shortcut-knop staat daar disabled, dus de laag
   * mag — net als de transit-modi — nooit "blijven hangen". Dekt ook de
   * tab-switch-flow, die niet via {@link handlePresetApplied} loopt. De
   * (globaal persistente) voorkeur gaat bewust mee uit: terug op een gewone
   * tab staan de blokjes uit tot de gebruiker ze opnieuw aanzet.
   */
  private readonly deactivateGateBlocksOnCycleOrComposite = effect(() => {
    if (this.showGateBlocks() && this.cycleOrCompositeActive()) {
      untracked(() => this.showGateBlocks.set(false));
    }
  });

  /**
   * De "tweede kant" van een gecombineerde tab: het cycle-moment of de
   * composite-partner — beide spelen dezelfde groene overlay-rol.
   */
  private readonly overlayMomentChart = computed(() =>
    this.activeCycleInfo()?.moment ?? this.activeCompositeInfo()?.partnerChart ?? null);

  /** Twee-kleurenoverlay van de tweede kant (top-level blauw, cycle/partner groen). */
  protected readonly cycleOverlay = computed<HdBodygraphTransitOverlay | null>(() => {
    const moment = this.overlayMomentChart();
    if (moment == null) return null;
    const activations = filterHdCycleOverlayActivations(moment.planetaryActivations);
    return activations.length === 0 ? null : { activations, colorMode: 'two-color' };
  });

  /**
   * Eén overlay-slot op de bodygraph; de cycle-weergave wint (modi zijn
   * exclusief). In een kant-weergave op gecombineerde tabs (bijstuurronde 7)
   * vervalt de twee-kleurenoverlay: de geselecteerde kant staat er als
   * volwaardige eigen chart.
   */
  protected readonly chartOverlay = computed<HdBodygraphTransitOverlay | null>(() =>
    this.combinedSideSelection() !== 'both'
      ? null
      : this.cycleOverlay() ?? this.justNowTransitOverlay(),
  );

  /** Cycle-moment-activaties (gevalideerd, P+D) voor de cycle-grids. */
  protected readonly cycleMomentGridActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => this.cycleOverlay()?.activations ?? [],
  );

  /** Informatieve Chiron van de tweede kant per zijde (metadata.extraPlanets). */
  protected readonly cycleMomentDesignChiron = computed(() => {
    const moment = this.overlayMomentChart();
    return moment ? this.resolveChironFromMetadata(moment.metadata, 'design') : null;
  });

  protected readonly cycleMomentPersonalityChiron = computed(() => {
    const moment = this.overlayMomentChart();
    return moment ? this.resolveChironFromMetadata(moment.metadata, 'personality') : null;
  });

  /** Grid-labels op een composite-tab: chartnaam boven de D|P-headers per kant. */
  protected readonly compositeChart1Label = computed(() => this.activeCompositeInfo()?.source.name ?? null);
  protected readonly compositeChart2Label = computed(() => this.activeCompositeInfo()?.partner.name ?? null);

  /**
   * Basis-tabblad op een composite-tab: welke persoon getoond wordt (1 = blauw,
   * 2 = groen). Reset naar chart 1 zodra een andere preset in beeld komt.
   */
  protected readonly basicCompositeSide = signal<1 | 2>(1);

  /** Chart-1-kleur (natal-blauw) voor de persoon-keuzeknoppen. */
  protected readonly compositeChart1Color = HD_JUST_NOW_TRANSIT_BASE_COLOR;

  /** Basis-paneel-bron: bij composite de gekozen persoon, anders de getoonde chart. */
  protected readonly basicConfiguration = computed(() => {
    const composite = this.activeCompositeInfo();
    if (composite == null) {
      return this.displayConfiguration();
    }
    return this.basicCompositeSide() === 2
      ? composite.partnerChart.configuration
      : this.activePreset()!.configuration;
  });

  /**
   * Chiron-posities voor het basis-paneel (bij een composiet die van de gekozen
   * persoon), enkel met de Chiron-setting aan. Geen activaties: zie HdChironPosition.
   */
  protected readonly basicChironPositions = computed<readonly HdChironPosition[]>(() => {
    if (!this.showChiron()) return [];
    const composite = this.activeCompositeInfo();
    const metadata = composite != null && this.basicCompositeSide() === 2
      ? composite.partnerChart.metadata
      : this.activePreset()?.metadata;
    return (['design', 'personality'] as const)
      .map(side => resolveHdChironPosition(metadata, side))
      .filter((position): position is HdChironPosition => position != null);
  });

  protected readonly basicPlanetaryActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(() => {
    const composite = this.activeCompositeInfo();
    if (composite == null) {
      return this.displayPlanetaryActivations();
    }
    return this.basicCompositeSide() === 2
      ? composite.partnerChart.planetaryActivations
      : this.activePreset()?.planetaryActivations ?? [];
  });

  /** Chiron van het nu-moment voor de just-now-transit-grid (alleen bij showChiron). */
  protected readonly justNowTransitChiron = computed(() =>
    this.showChiron() ? this.effectiveJustNowSnapshot()?.personalityChiron ?? null : null,
  );

  /** Zelfde resolutie als de simple grid, maar voor een willekeurig metadata-blok. */
  private resolveChironFromMetadata(
    metadata: Record<string, unknown> | undefined,
    side: 'personality' | 'design',
  ): HdSimpleExtraPlanetVm | null {
    const extraPlanets = metadata?.['extraPlanets'];
    if (!Array.isArray(extraPlanets)) {
      return null;
    }
    const entry = (extraPlanets as Array<Record<string, unknown>>).find(
      candidate => candidate['planetId'] === HD_CHIRON_DISPLAY_CONFIG.planetId && candidate['side'] === side,
    );
    if (!entry) {
      return null;
    }
    return {
      glyph: HD_CHIRON_DISPLAY_CONFIG.astronomiconChar,
      gate: Number(entry['gate']),
      line: Number(entry['line']),
      tooltip: this.pageTransloco.translate<string>(
        'humanDesign.chart.simpleGrid.chironTooltip',
        undefined,
        this.pageContentLang.effectiveLang(),
      ),
    };
  }

  /** Titelaccent van de cycle-kant van de grids (transit-tekstgroen). */
  protected readonly cycleTitleColor = HD_JUST_NOW_TRANSIT_TEXT_COLOR;

  protected override gateInfoTransitActivations(): readonly HdBodygraphPlanetaryActivation[] {
    // Cycle chart: de poort-/kanaalinfo toont de cycle-activaties als transitlaag
    // (zelfde groene bron-markering als bij de just-now-transit).
    const cycleOverlay = this.cycleOverlay();
    if (cycleOverlay != null) {
      return cycleOverlay.activations;
    }
    return this.justNowTransitVisualsActive() ? this.justNowTransitActivations() : [];
  }

  /** Cycle chart: eigen rijlabel ("Saturnus-return-transit") + bron-tint-stijl in de info-panelen. */
  protected override gateInfoTransitPresentation(): { style: 'just-now' | 'cycle'; rowLabel: string | null } {
    // Composite chart: de partner-naam als rijlabel, in dezelfde groene bron-stijl.
    const composite = this.activeCompositeInfo();
    if (composite != null) {
      return { style: 'cycle', rowLabel: composite.partner.name };
    }
    return this.cycleTransitPresentation();
  }

  /** Composite chart: chart 1 kleurt in de info-panelen natal-blauw. */
  protected override gateInfoBaseStyle(): 'standard' | 'composite' {
    return this.activeCompositeInfo() != null ? 'composite' : 'standard';
  }

  /** Composite chart: chart 1-naam vóór het kant-label van de basisrijen. */
  protected override gateInfoBaseRowLabel(): string | null {
    return this.activeCompositeInfo()?.source.name ?? null;
  }

  private cycleTransitPresentation(): { style: 'just-now' | 'cycle'; rowLabel: string | null } {
    const cycle = this.activeCycleInfo();
    if (cycle == null) {
      return super.gateInfoTransitPresentation();
    }
    const lang = this.pageContentLang.effectiveLang();
    const translate = (key: string) => this.pageTransloco.translate<string>(key, undefined, lang);
    const base = hdCycleLabel(translate, cycle.key);
    const cycleName = cycle.key === 'solarReturn' && cycle.solarReturnYear != null
      ? `${base} ${cycle.solarReturnYear}`
      : base;
    return {
      style: 'cycle',
      rowLabel: this.pageTransloco.translate<string>(
        'humanDesign.advancedPanel.cycles.transitRowLabel',
        { cycle: cycleName },
        lang,
      ),
    };
  }

  protected override centerInfoConfiguration(): HdBodygraphConfig {
    return this.moonTransitChartConfiguration();
  }

  protected readonly justNowBasePlanetGridLabels = computed<HdJustNowBasePlanetGridLabels>(() => {
    const lang = this.pageContentLang.effectiveLang();
    const prefix = 'humanDesign.chart.justNowTransit.planetGrid';
    return {
      title: this.pageTransloco.translate(`${prefix}.baseTitle`, undefined, lang),
      exaltation: this.pageTransloco.translate(`${prefix}.exaltation`, undefined, lang),
      detriment: this.pageTransloco.translate(`${prefix}.detriment`, undefined, lang),
      sameGate: this.pageTransloco.translate(`${prefix}.sameGate`, undefined, lang),
      harmonicGate: this.pageTransloco.translate(`${prefix}.harmonicGate`, undefined, lang),
    };
  });

  protected readonly justNowTransitPlanetGridLabels = computed<HdJustNowTransitPlanetGridLabels>(() => ({
    title: this.pageTransloco.translate(
      'humanDesign.chart.justNowTransit.planetGrid.transitTitle',
      undefined,
      this.pageContentLang.effectiveLang(),
    ),
  }));

  protected readonly justNowTransitLineData: HdJustNowLineDataResolver = (gate, line) => {
    const translation = this.pageGateInfoService
      .gateInfoIn(gate, this.pageContentLang.effectiveLang())
      .translations.lines[line - 1];
    if (translation == null) {
      return null;
    }
    return {
      exaltationPlanet: translation.exaltation.planet,
      detrimentPlanet: translation.detriment.planet,
    };
  };

  protected readonly justNowTransitPlanetName = (planetId: number): string => {
    const config = getHdPlanetDisplayConfigByPlanetId(planetId);
    if (config == null) {
      return `${planetId}`;
    }
    return this.pagePlanetInfoService
      .planetInfoIn(config.key, this.pageContentLang.effectiveLang())
      .name;
  };

  protected readonly justNowTransitViewData = computed<HdMandalaJustNowTransitViewData | null>(() => {
    const overview = this.justNowTransitOverview();
    if (overview == null) {
      return null;
    }
    // In combinatie met Locks & Keys (bijstuurronde 8) wijken de Just
    // Now-ringen, -connectors en -cards: de locks-laag + de mint
    // moment-glyphs en -chips dragen daar de transitinformatie.
    if (this.locksKeysMode()) {
      return null;
    }
    return {
      formations: overview.formations,
      connectorStyle: this.moonTransitConnectorStyle(),
    };
  });

  protected readonly justNowTransitHighlightedChannelKeys = computed<ReadonlySet<string>>(() => {
    const target = this.justNowTransitHoverTarget();
    const overview = this.justNowTransitOverview();
    if (target == null || overview == null) {
      return new Set<string>();
    }
    if (target.kind === 'formation') {
      return new Set([target.channelKey]);
    }
    return new Set(
      overview.formations
        .filter(formation => formation.gates.includes(target.gate))
        .map(formation => formation.channelKey),
    );
  });

  /**
   * Positie van de personality-maan "nu" (just-now fetch) + het moment van de
   * fetch (voor de tooltip op het maan-icoon), of null.
   */
  protected readonly moonTransitToday = signal<
    { longitude: number; gate: number; line: number; fetchedAt: Date } | null
  >(null);

  /**
   * Chart-zichtbaarheid binnen de modus: de mandala/both-knoppen schakelen
   * dit (finetune 2026-07-06) zonder de onderliggende `chartViewMode` aan te
   * raken. Reset naar zichtbaar bij het activeren van de modus.
   */
  protected readonly moonTransitChartVisible = signal(true);

  /**
   * Actueel hover-doel binnen de modus (finetune-ronde 2): een formatie
   * (card- of connector-hover) of een maanpoort in het wiel. Eén bron voor de
   * blauwe kanaalstuk-highlight in de bodygraph én de card/connector-
   * highlights — het enige hover-effect binnen de modus.
   */
  private readonly moonTransitHoverTarget = signal<
    | { kind: 'formation'; channelKey: string; moonGate: number }
    | { kind: 'gate'; gate: number }
    | null
  >(null);

  /** Maanpoort waarvan het kanaalstuk in de bodygraph blauw kleurt, of null. */
  protected readonly moonTransitHoveredGate = computed<number | null>(() => {
    const target = this.moonTransitHoverTarget();
    if (target == null) {
      return null;
    }
    return target.kind === 'gate' ? target.gate : target.moonGate;
  });

  /**
   * ChannelKeys die card-border + connector-opacity-highlight krijgen: bij een
   * poort-hover álle formaties van die maanpoort, anders de ene formatie.
   */
  protected readonly moonTransitHighlightedChannelKeys = computed<ReadonlySet<string>>(() => {
    const target = this.moonTransitHoverTarget();
    if (target == null) {
      return new Set<string>();
    }
    if (target.kind === 'formation') {
      return new Set([target.channelKey]);
    }
    const formations = this.moonTransitOverview()?.formationsByGate.get(target.gate) ?? [];
    return new Set(formations.map(formation => formation.channelKey));
  });

  /** De maan-knop is enkel zinvol met een geladen chart met activaties. */
  protected readonly moonTransitAvailable = computed(
    () => this.displayConfiguration().activations.length > 0
      && this.activeCycleInfo() == null
      && (this.activeChartType() === PlatformChartType.Standard || this.activeChartType() === PlatformChartType.JustNow),
  );

  /**
   * Effectieve weergave-settings: de maan-transit modus overschrijft als
   * computed laag (nooit de onderliggende signals muteren — modus uit = alles
   * automatisch terug, §6.1). Templates lezen de effective-varianten; de
   * settings-handlers blijven op de gewone signals schrijven.
   */
  // Locks & Keys vóór Just Now: wanneer beide actief zijn (bijstuurronde 8)
  // is de mandala-gerichte locks-weergave leidend en levert de Just Now enkel
  // de mint moment-data.
  protected readonly effectiveViewMode = computed<HdChartViewMode>(() =>
    this.moonTransitMode()
      ? (this.moonTransitChartVisible() ? 'both' : 'mandala')
      : this.locksKeysMode()
        ? (!this.locksKeysMandalaVisible() ? 'chart' : this.locksKeysChartVisible() ? 'both' : 'mandala')
        : this.justNowTransitVisualsActive()
          ? (this.justNowTransitMandalaVisible() ? 'both' : 'chart')
          : this.chartViewMode(),
  );
  protected readonly effectiveSimplePlanetGrid = computed(() =>
    this.moonTransitMode() || this.justNowTransitVisualsActive() || this.locksKeysMode()
      || this.tripleChartView()
      ? true
      : this.simplePlanetGrid(),
  );
  /**
   * De geavanceerde Resonance-LPS is enkel actief in de volledige
   * (niet-eenvoudige) grid buiten de transit-modi (die de eenvoudige grid
   * forceren).
   */
  protected readonly effectiveAdvancedLpsGrid = computed(() =>
    // Phone: de Resonance-LPS (±349px per kant) past er ruimtelijk nooit —
    // de voorkeur blijft in het mobiele settings-profiel bewaard.
    !this.effectiveSimplePlanetGrid() && this.advancedLpsGrid() && !this.viewport.isPhone(),
  );
  /**
   * De zij-statistieken worden naast de geavanceerde Resonance-LPS enkel
   * getoond wanneer ze er gemeten onder passen ({@link sideStatsFitBesideResonance},
   * QHD en hoger); anders geforceerd verborgen. De onderliggende
   * gebruikersvoorkeur (`showPlanetSideStats`) blijft bewaard, zodat de
   * statistieken bij het terugschakelen naar de gewone LPS-view (of bij meer
   * ruimte) automatisch terugkomen indien ze voorheen aan stonden.
   */
  protected readonly effectiveShowPlanetSideStats = computed(
    () => this.showPlanetSideStats()
      && !this.viewport.isPhone()
      && (!this.effectiveAdvancedLpsGrid() || this.sideStatsFitBesideResonance()),
  );
  /**
   * Mandala-layout voor álle weergaven van deze page (2026-08-20): de kern
   * (poortenring + binnengat) heeft een vaste maat en de optionele I-Tjing- en
   * kwartaalringen groeien er aan de búitenkant bij — ringen aan/uit verkleint
   * dus nooit wat er al staat. Enkel wanneer het geheel niet past, schaalt
   * álles uniform mee (ringen én — via {@link mandalaChartScale} — de chart):
   * in de transit-modi is de wielrand het plafond zodat de card-zone en de
   * today-marker erbuiten vrij blijven; daarbuiten is de viewBox-rand de grens.
   */
  /** Kwartaalband zichtbaar, ook in de één-ring-modus van de telefoon (zelfde regel als het wiel zelf). */
  private readonly mandalaQuarterRingEffectivelyVisible = computed(() => {
    const single = this.effectiveSingleInfoRing();
    return single != null ? single === 'quarters' : this.showMandalaQuarterRing();
  });

  private readonly effectiveMandalaLayout = computed(() =>
    buildMandalaWheelLayout(
      {
        zodiacRing: this.showMandalaZodiacRing(),
        quarterRing: this.showMandalaQuarterRing(),
        ichingRing: this.showMandalaIchingRing(),
        godheadRing: this.showMandalaGodheadRing(),
      },
      {
        // Locks & Keys klemt net als de transit-modi op de wielrand: de lock- en
        // key-iconen hangen erbuiten en hebben die marge nodig. Met de
        // kwartaalband aan (en geen van die modi) blijft een smalle strook vrij
        // voor de seizoens-badges buiten de rand (lecture 2026-10-21, 4C).
        ...(this.moonTransitMode() || this.justNowTransitVisualsActive() || this.locksKeysMode()
          ? { maxOuterRadius: MOON_TRANSIT_WHEEL_EDGE_RADIUS }
          : this.mandalaQuarterRingEffectivelyVisible()
            ? { maxOuterRadius: MANDALA_WHEEL_TARGET_OUTER_RADIUS - HD_SEASON_MARKER_ROOM }
            : undefined),
        // Phone: één informatieve ring tegelijk (ring-cyclus-knop).
        singleInfoRing: this.effectiveSingleInfoRing(),
      },
    ),
  );

  /** Single-info-ring alleen op phone; desktop houdt de gewone ring-toggles. */
  protected readonly effectiveSingleInfoRing = computed<MandalaInfoRingKey | null>(() =>
    this.viewport.isPhone() ? this.mobileMandalaRing() : null,
  );

  protected readonly effectiveMandalaGeometry = computed(() => this.effectiveMandalaLayout().geometry);

  /**
   * Uniforme wiel-schaal (≤ 1) als CSS-variabele op de chart-mandala-stack: de
   * bodygraph in de both-weergave krimpt met exact dezelfde factor als de
   * mandala-radii, zodat de verhouding wiel ↔ chart constant blijft.
   */
  protected readonly mandalaChartScale = computed(() => this.effectiveMandalaLayout().scale);

  /**
   * Planeetactivatie-glyphs voor de activatie-zone van het mandala-wiel. De
   * kleuren spiegelen exact de bron-semantiek van de actieve chartweergave:
   * standaard design-rood/personality-zwart (met respect voor de zijde-modus);
   * op cycle-/composite-tabs de twee-kleurenoverlay (top-level/chart 1 blauw,
   * cyclusmoment/partner groen); in de Just Now-transit modus de gekozen
   * kleurmodus (bronkleuren of natal-blauw) + transit-groen voor de
   * transit-activaties. De volgorde per bron (design vóór personality, dan
   * planeetvolgorde) bepaalt de stapelvolgorde in de slice.
   */
  protected readonly mandalaGateGlyphs = computed<readonly HdMandalaGateGlyph[]>(() => {
    // Voor het groen bewust de kanaalstuk-kleur (lichter mint) i.p.v. het
    // donkere tekstgroen: op kleine glyphs geeft dát genoeg afstand tot het
    // chart1-blauw om in één oogopslag te onderscheiden (2026-08-20). In een
    // kant-weergave (bijstuurronde 7) valt dit door naar de gewone
    // zijde-kleuren: de geselecteerde kant staat er als volwaardige chart.
    if ((this.activeCycleInfo() != null || this.activeCompositeInfo() != null)
      && this.combinedSideSelection() === 'both') {
      return [
        ...buildHdMandalaGateGlyphs(this.allPlanetaryActivations(), 'base', () => HD_JUST_NOW_TRANSIT_BASE_COLOR),
        ...buildHdMandalaGateGlyphs(this.cycleMomentGridActivations(), 'overlay', () => HD_JUST_NOW_TRANSIT_COLOR),
      ];
    }

    if (this.justNowTransitVisualsActive()) {
      const baseColor = this.justNowTransitColorMode() === 'two-color'
        ? () => HD_JUST_NOW_TRANSIT_BASE_COLOR
        : hdMandalaSideGlyphColor;
      return [
        ...buildHdMandalaGateGlyphs(this.displayPlanetaryActivations(), 'base', baseColor),
        ...buildHdMandalaGateGlyphs(this.justNowTransitActivations(), 'transit', () => HD_JUST_NOW_TRANSIT_COLOR),
      ];
    }

    return buildHdMandalaGateGlyphs(this.displayPlanetaryActivations(), 'base', hdMandalaSideGlyphColor);
  });

  // ── Ruimte-detectie: wiel ↔ flankerende planeetgrids ─────────────────────

  private readonly chartStackHost = viewChild('chartStackHost', { read: ElementRef<HTMLElement> });
  // `read: ElementRef` is essentieel op de rechter ref: die staat op een
  // component (de grid), en zonder read zou de component-instantie terugkomen.
  private readonly mandalaLeftOverlay = viewChild('mandalaLeftOverlay', { read: ElementRef<HTMLElement> });
  private readonly mandalaRightOverlay = viewChild('mandalaRightOverlay', { read: ElementRef<HTMLElement> });

  /**
   * Gemeten maximale wielbreedte (px) tussen de flankerende planeetgrids, of
   * null zonder zichtbare grids/meting. Gevoed door een ResizeObserver op de
   * chart-stack en de grid-containers: verschijnen/verdwijnen of hersizen van
   * een grid herberekent de waarde vanzelf.
   */
  protected readonly mandalaAvailableWidth = signal<number | null>(null);

  private readonly mandalaSpaceObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(() => {
      this.measureMandalaAvailableWidth();
      this.measureSideStatsSpace();
      this.measureTripleChartSpace();
      this.publishChartContentWidth();
    })
    : null;

  /** (Her)koppelt de observer wanneer de gemeten elementen wisselen (@if-takken). */
  private readonly mandalaSpaceEffect = effect(onCleanup => {
    const observer = this.mandalaSpaceObserver;
    if (observer == null) {
      return;
    }
    const elements = [
      this.chartStackHost()?.nativeElement,
      this.mandalaLeftOverlay()?.nativeElement,
      this.mandalaRightOverlay()?.nativeElement,
      this.tripleLeftChartHost()?.nativeElement,
      this.tripleRightChartHost()?.nativeElement,
    ].filter((element): element is HTMLElement => element != null);
    for (const element of elements) {
      observer.observe(element);
    }
    this.measureMandalaAvailableWidth();
    this.measureSideStatsSpace();
    this.measureTripleChartSpace();
    this.publishChartContentWidth();
    onCleanup(() => observer.disconnect());
  });

  // ── Breedte van het chartgedeelte voor zwevende leespanelen ─────────────

  private readonly document = inject(DOCUMENT);

  /**
   * Zet de breedte van het chartgedeelte (de chart-stack: de kaart met de
   * bodygraph en de flankerende grids) als `--hd-chart-content-width` op
   * `:root`. Zwevende panelen leven in de overlay-container, buiten deze
   * pagina, dus de variabele moet globaal staan. Gebruikt door de losgemaakte
   * Locks & Keys-panelen (Steven 2026-10-30: zo breed als het chartgedeelte,
   * niet schermbreed); volgt live mee met de venstermaat en het in-/uitklappen
   * van het rechterpaneel. Zonder meetbare chart (bv. phone, laadfout) blijft
   * de laatste waarde staan; bij het verlaten van de pagina wordt ze gewist.
   */
  private publishChartContentWidth(): void {
    const width = this.chartStackHost()?.nativeElement.clientWidth ?? 0;
    if (width <= 0) return;
    this.document.documentElement.style.setProperty(HD_CHART_CONTENT_WIDTH_VAR, `${width}px`);
  }

  private readonly clearChartContentWidth = inject(DestroyRef).onDestroy(() =>
    this.document.documentElement.style.removeProperty(HD_CHART_CONTENT_WIDTH_VAR));

  // ── Ruimte-detectie: drieluik-zijcharts ↔ flankerende planeetgrids ───────

  private readonly chartMandalaStack = viewChild('chartMandalaStack', { read: ElementRef<HTMLElement> });
  private readonly tripleLeftChartHost = viewChild('tripleLeftChartHost', { read: ElementRef<HTMLElement> });
  private readonly tripleRightChartHost = viewChild('tripleRightChartHost', { read: ElementRef<HTMLElement> });

  /**
   * Inschuif (px) van de drieluik-zijcharts richting het midden, zodat ze op
   * een cycle-/composite-tab (twee grid-kolommen per kant) niet onder de
   * planeetgrids schuiven (feedback 2026-09-27, Full HD). 0 zolang er ruimte
   * genoeg is: een gewone tab en QHD blijven ongewijzigd.
   */
  private readonly tripleSidePull = signal(0);

  /** Zelfde px-waarde als CSS-var voor de middenchart-marge (null = CSS-default 0). */
  protected readonly tripleSidePullPx = computed<string | null>(() => {
    const pull = this.tripleSidePull();
    return this.tripleChartView() && !this.viewport.isPhone() && pull > 0 ? `${pull}px` : null;
  });

  private measureTripleChartSpace(): void {
    const stack = this.chartMandalaStack()?.nativeElement ?? null;
    const leftChart = this.tripleLeftChartHost()?.nativeElement ?? null;
    const rightChart = this.tripleRightChartHost()?.nativeElement ?? null;
    if (stack == null || leftChart == null || rightChart == null || leftChart.offsetWidth <= 0 || rightChart.offsetWidth <= 0) {
      this.tripleSidePull.set(0);
      return;
    }
    const leftGrid = this.mandalaLeftOverlay()?.nativeElement ?? null;
    const rightGrid = this.mandalaRightOverlay()?.nativeElement ?? null;
    this.tripleSidePull.set(tripleChartSidePullPx({
      leftGridRight: leftGrid != null && leftGrid.offsetWidth > 0 ? leftGrid.getBoundingClientRect().right : null,
      rightGridLeft: rightGrid != null && rightGrid.offsetWidth > 0 ? rightGrid.getBoundingClientRect().left : null,
      leftChart: leftChart.getBoundingClientRect(),
      rightChart: rightChart.getBoundingClientRect(),
      // De meting gebeurt in de al verschoven stand: de helper rekent terug
      // met de inschuif die op dat moment écht in de DOM staat (niet de
      // signal-waarde, die de DOM nog niet bereikt kan hebben).
      appliedPull: Number.parseFloat(stack.style.getPropertyValue('--triple-side-pull')) || 0,
    }));
  }

  // ── Ruimte-detectie: zij-statistieken onder de Resonance-LPS ─────────────

  /**
   * Laatste geometrie-meting van de chart-stack t.o.v. het design-grid-paneel
   * (zonder statistieken), of null zonder meetbaar paneel. Zelfde
   * ResizeObserver als de wiel-detectie: het paneel leeft in de linker
   * grid-container, dus elke maatwijziging (Resonance aan/uit, venster,
   * statistieken tonen/verbergen) meet opnieuw.
   */
  private readonly sideStatsSpace = signal<HdSideStatsSpaceMeasurement | null>(null);

  /**
   * Laatst gemeten hoogte (px) van het statistiekenblok — gemeten telkens het
   * zichtbaar is (meestal al in de gewone LPS-view), zodat de beslissing voor
   * de Resonance-view niet afhangt van de huidige zichtbaarheid (geen
   * aan/uit-oscillatie). Null tot de eerste meting → terugvalschatting.
   */
  private readonly measuredSideStatsHeight = signal<number | null>(null);

  private measureSideStatsSpace(): void {
    const stack: HTMLElement | null = this.chartStackHost()?.nativeElement ?? null;
    const left: HTMLElement | null = this.mandalaLeftOverlay()?.nativeElement ?? null;
    const panel = (left?.querySelector('.planet-grid-panel') ?? null) as HTMLElement | null;
    if (stack == null || panel == null || stack.clientHeight <= 0) {
      this.sideStatsSpace.set(null);
      return;
    }
    const stackRect = stack.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const stats = (left?.querySelector('.side-stats-combined') ?? null) as HTMLElement | null;
    if (stats != null && stats.offsetHeight > 0) {
      this.measuredSideStatsHeight.set(stats.offsetHeight);
    }
    this.sideStatsSpace.set({
      stackHeight: stackRect.height,
      gridPanelBottom: panelRect.bottom - stackRect.top,
      resonance: panel.closest('hd-planet-grid')?.classList.contains('resonance-mode') ?? false,
    });
  }

  /**
   * Passen de zij-statistieken onder de geavanceerde Resonance-LPS zonder dat
   * de chart-kolom gaat scrollen (feedback 2026-08-23)? Op Full HD niet —
   * vandaar de vroegere vaste uitsluiting — op QHD en hoger wél. Gemeten,
   * niet geschat op viewport-maat; zonder meting blijft de uitsluiting gelden.
   */
  protected readonly sideStatsFitBesideResonance = computed(() =>
    sideStatsFitBelowResonanceGrid(this.sideStatsSpace(), this.measuredSideStatsHeight()),
  );

  private measureMandalaAvailableWidth(): void {
    const stack = this.chartStackHost()?.nativeElement;
    const left = this.mandalaLeftOverlay()?.nativeElement ?? null;
    const right = this.mandalaRightOverlay()?.nativeElement ?? null;
    const overlayWidth = Math.max(left?.offsetWidth ?? 0, right?.offsetWidth ?? 0);
    if (stack == null || stack.clientWidth <= 0 || overlayWidth <= 0) {
      this.mandalaAvailableWidth.set(null);
      return;
    }
    // Het wiel staat gecentreerd: de breedste overlay-grid bepaalt de vrije
    // ruimte aan wéérszijden (grid-marge + ademruimte inbegrepen).
    this.mandalaAvailableWidth.set(stack.clientWidth - 2 * (overlayWidth + HD_MANDALA_GRID_CLEARANCE_PX));
  }

  /**
   * Max-breedte voor de chart-mandala-stack in both/mandala-weergave: het
   * geheel (wiel + chart, via de vierkante stack) krimpt zodat het naast de
   * grids past — nooit verder dan de zinvolle ondergrens.
   */
  protected readonly mandalaMaxWidthPx = computed<string | null>(() => {
    // Phone: de grids flankeren het wiel niet (ze staan eronder), dus de
    // meting tegen de overlay-breedtes zegt daar niets — wiel op volle breedte.
    if (this.viewport.isPhone()) {
      return null;
    }
    const available = this.mandalaAvailableWidth();
    if (available == null) {
      return null;
    }
    return `${Math.max(available, HD_MANDALA_MIN_WHEEL_PX)}px`;
  });

  /**
   * Naast de geavanceerde Resonance-LPS is er op een Full HD-scherm te weinig
   * ruimte voor een zinvol wiel: onder de ondergrens verbergen we de
   * chart-kaart volledig (op QHD en breder past alles wél samen).
   */
  protected readonly mandalaHiddenForSpace = computed(() =>
    !this.viewport.isPhone()
    && this.effectiveAdvancedLpsGrid()
    && !this.tripleChartView()
    && this.effectiveViewMode() !== 'chart'
    && (this.mandalaAvailableWidth() ?? Number.POSITIVE_INFINITY) < HD_MANDALA_MIN_WHEEL_PX,
  );

  /** Kleurvariant van de kwartaalband (A/B-setting, gepersisteerd). */
  protected readonly mandalaQuarterColorVariant = this.settings.mandalaQuarterColorVariant;

  /** Dierenriem-band: donker (default) of licht (globaal persistent, feedback 2026-08-23). */
  protected readonly mandalaZodiacRingVariant = this.settings.mandalaZodiacRingVariant;

  /**
   * Gefadede centerkleur in de slices van geactiveerde poorten
   * (visualisatie-setting, gepersisteerd; default áán — besluit 2026-08-20).
   */
  protected readonly mandalaSliceCenterFade = this.settings.mandalaSliceCenterFade;

  /**
   * Kwartaal-keynote-delen in de actieve contenttaal (i18n-keys
   * `humanDesign.chart.mandala.quarters.*`); sternamen blijven taal-neutraal
   * in het wiel zelf.
   */
  protected readonly mandalaQuarterTexts = computed<HdMandalaQuarterTexts>(() => {
    const lang = this.pageContentLang.effectiveLang();
    return buildHdMandalaQuarterTexts(key => this.pageTransloco.translate(key, {}, lang));
  });

  /** Dierenriem-tekennamen in de actieve contenttaal (tooltip op de band). */
  protected readonly mandalaZodiacNames = computed<HdMandalaZodiacNames>(() => {
    const lang = this.pageContentLang.effectiveLang();
    return buildHdMandalaZodiacNames(key => this.pageTransloco.translate(key, {}, lang));
  });

  /** Tooltips van de seizoensmarkeringen en kwartaalbanden van het wiel (contenttaal). */
  protected readonly mandalaSeasonTexts = computed<HdMandalaSeasonTexts>(() => {
    const lang = this.pageContentLang.effectiveLang();
    return buildHdMandalaSeasonTexts((key, params) => this.pageTransloco.translate(key, params ?? {}, lang));
  });

  /**
   * Gelokaliseerde lock-keynotes (rol per lock-poort, bv. "De Weg") voor de
   * cirkeltekst boven de slotjes in de Locks & Keys-modus. `null` buiten die
   * modus zodat het wiel de labels niet tekent en de scope-lookup wegvalt.
   */
  protected readonly mandalaLocksKeysKeynotes = computed<Readonly<Record<number, string>> | null>(() => {
    if (!this.locksKeysMode()) {
      return null;
    }
    const lang = this.pageContentLang.effectiveLang();
    const keynotes: Record<number, string> = {};
    for (const lock of HD_LOCKS_KEYS_LOCKS) {
      keynotes[lock.gate] = this.pageGateInfoService.locksKeysTextIn(`locks.${lock.gate}.role`, lang);
    }
    return keynotes;
  });

  /** Melding wanneer wiel + chart wegens plaatsgebrek verborgen zijn. */
  protected readonly mandalaSpaceHiddenText = computed(() =>
    this.pageTransloco.translate(
      'humanDesign.chart.mandala.spaceHiddenMessage',
      {},
      this.pageContentLang.effectiveLang(),
    ),
  );

  // ── Inklapbaar rechter configuratie-/dock-paneel ─────────────────────────

  /**
   * Rechter paneel ingeklapt: de kolom krimpt tot een smalle rail zodat de
   * chart-zone de ruimte krijgt (kleinere schermen). Het dock-area blijft
   * gemount (enkel verborgen) zodat gedockte panelen hun staat behouden.
   */
  protected readonly configPanelCollapsed = this.settings.configPanelCollapsed;

  protected toggleConfigPanelCollapsed(): void {
    this.configPanelCollapsed.update(collapsed => !collapsed);
  }

  /** Standaard- en minimumbreedte van het rechterpaneel; dezelfde 680px als de desktop-grid in de scss. */
  protected readonly configPanelMinWidth = 680;

  /**
   * Breedte van het rechterpaneel via de sleeplijn: live tijdens het slepen,
   * bewaard in de settings bij het loslaten. De desktop-grid klemt hem zodat
   * de chart-kolom minstens even breed blijft als het paneel standaard is.
   */
  protected readonly configPanelWidth = linkedSignal(() => this.settings.configPanelWidth());

  /**
   * De sleeplijn verschijnt enkel wanneer breder iets oplevert: de inhoud van het
   * rechterpaneel is breder dan het paneel. Staat het paneel al breder, dan blijft
   * hij staan, zodat terugzetten altijd kan.
   */
  protected readonly configPanelResizable = computed(
    () => this.configPanelWidth() != null || (this.rightDockArea()?.wantsMoreWidth() ?? false),
  );

  /**
   * Zolang de inhoud van het rechterpaneel niet past: de meting achter het knopje
   * onder de greep dat het paneel net zo breed maakt als die inhoud nodig heeft.
   */
  protected readonly configPanelContentShortfall = computed(() => {
    const dockArea = this.rightDockArea();
    return dockArea?.wantsMoreWidth() ? () => dockArea.missingWidth() : undefined;
  });

  protected handleConfigPanelResized(width: number | undefined): void {
    this.settings.configPanelWidth.set(width);
  }

  // ── Mobiele toolbar (docs/MOBIEL.md §9 besluit 2) ────────────────────────

  /**
   * Ring-cyclus op phone: één informatieve ring tegelijk om ruimte te sparen
   * (single-info-ring modus van het wiel — de geselecteerde ring komt aan de
   * buitenrand en de kern groeit mee). Cyclus: geen → dierenriem → kwartaal →
   * godhead → I-Tjing → geen ({@link MANDALA_INFO_RING_CYCLE}).
   */
  protected readonly mobileMandalaRing = signal<MandalaInfoRingKey | null>(null);

  protected cycleMandalaRing(): void {
    const current = this.mobileMandalaRing();
    const index = current == null ? -1 : MANDALA_INFO_RING_CYCLE.indexOf(current);
    const next = index + 1 < MANDALA_INFO_RING_CYCLE.length ? MANDALA_INFO_RING_CYCLE[index + 1] : null;
    this.mobileMandalaRing.set(next);
    // Spiegel naar de gewone ring-signals: zo klopt de kleurstaat van de
    // toolbar-knop en toont een resize terug naar desktop dezelfde ring.
    this.showMandalaZodiacRing.set(next === 'zodiac');
    this.showMandalaQuarterRing.set(next === 'quarters');
    this.showMandalaGodheadRing.set(next === 'godheads');
    this.showMandalaIchingRing.set(next === 'iching');
  }

  /** ⋮-menu van de mobiele toolbar: de secundaire acties via het contextmenu. */
  protected handleMobileMoreRequested(position: { x: number; y: number }): void {
    const t = (key: string): string =>
      this.pageTransloco.translate(`humanDesign.chart.shortcutBar.${key}`, {}, this.pageContentLang.effectiveLang());
    const items: ContextMenuItem[] = [
      { label: t('mobileMoreNewChart'), icon: 'user-plus', action: () => this.handleNewChartRequested() },
      ...(this.canSaveActiveChart()
        ? [{ label: t('mobileMoreSaveChart'), icon: 'floppy-disk', action: () => this.saveActiveChart() }]
        : []),
      // Delen via link (spec 2026-10-20-chart-share): op phone achter het ⋮-menu (docs/MOBIEL.md).
      ...(this.canShareActiveChart()
        ? [{ label: t('mobileMoreShareChart'), icon: 'share-nodes', action: () => void this.handleShareChartRequested() }]
        : []),
      // Maan-transit zit op phone niet meer in de balk (plaats voor de transits-knop).
      ...(this.moonTransitAvailable()
        ? [{ label: t('mobileMoreMoonTransit'), icon: 'moon', action: () => this.toggleMoonTransitMode() }]
        : []),
      { label: t('mobileMorePlanetGrid'), icon: 'table-cells-large', action: () => this.togglePlanetGrid() },
      { label: t('mobileMoreSimpleGrid'), icon: 'list', action: () => this.toggleSimplePlanetGrid() },
      { label: t('mobileMoreMiniGrid'), icon: 'table-cells', action: () => this.miniPlanetGrid.update(v => !v) },
      { label: t('mobileMoreSettings'), icon: 'gear', action: () => this.settingsPanelOpen.set(true) },
    ];
    this.pageContextMenu.open(position, items);
  }

  /**
   * Zolang het rechter paneel ingeklapt is, vallen gedockt-openende
   * info-panelen terug op het gewone zwevende gedrag: in de onzichtbare dock
   * zou een klik anders "in het niets" verdwijnen. De onderliggende setting
   * blijft ongemoeid en herleeft bij het uitklappen.
   */
  protected override effectiveGateInfoOpenBehavior(): HdGateInfoOpenBehavior {
    const behavior = this.gateInfoOpenBehavior();
    if (
      this.configPanelCollapsed()
      && (behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose')
    ) {
      return 'full';
    }
    return behavior;
  }

  /** Formatie-overview; alleen berekend zolang de modus actief is. */
  private readonly moonTransitOverview = computed(() => {
    if (!this.moonTransitMode()) {
      return null;
    }
    const lang = this.pageContentLang.effectiveLang();
    return buildHdMoonTransitOverview({
      configuration: this.displayConfiguration(),
      planetaryActivations: this.allPlanetaryActivations(),
      renderState: this.displayRenderState(),
      lineData: (gate, line) => {
        const duality = this.pageGateInfoService.gateInfoIn(gate, lang).translations.lines[line - 1];
        if (duality == null) {
          return null;
        }
        return {
          exaltationPlanet: duality.exaltation.planet,
          detrimentPlanet: duality.detriment.planet,
        };
      },
    });
  });

  /**
   * Tooltip voor het today-maanicoon: "Huidige positie van de maan: poort.lijn
   * @ YYYY-MM-DD om HH:mm" (vertaald, ronde 3), of null zonder maanpositie.
   */
  private readonly moonTransitTodayTooltip = computed<string | null>(() => {
    const today = this.moonTransitToday();
    if (today == null) {
      return null;
    }
    const pad = (value: number) => String(value).padStart(2, '0');
    const at = today.fetchedAt;
    return this.pageTransloco.translate(
      'humanDesign.chart.moonTransit.todayTooltip',
      {
        position: `${today.gate}.${today.line}`,
        date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
        time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
      },
      this.pageContentLang.effectiveLang(),
    );
  });

  /** Viewdata voor de mandala-transitlaag en de card-laag. */
  protected readonly moonTransitViewData = computed<HdMandalaMoonTransitViewData | null>(() => {
    const overview = this.moonTransitOverview();
    if (overview == null) {
      return null;
    }
    return {
      formations: overview.formations,
      todayLongitude: this.moonTransitToday()?.longitude ?? null,
      todayTooltip: this.moonTransitTodayTooltip(),
      connectorStyle: this.moonTransitConnectorStyle(),
      gateMarkerStyle: this.moonTransitGateMarkerStyle(),
    };
  });

  /**
   * Chart-configuratie voor de bodygraph: in de modus wordt de gehoverde
   * maanpoort aangevuld met een tijdelijke `highlight`-activatie zodat het
   * kanaalstuk in het transit-blauw kleurt (zelfde mechanisme als het
   * inkleuren van circuits in de editor). Buiten de modus of zonder hover is
   * dit exact `displayConfiguration` — de echte configuratie wordt nooit
   * gemuteerd, en in de modus staat de chart bovendien in info-modus.
   */
  protected readonly moonTransitChartConfiguration = computed(() => {
    // L&K-bewuste basis: in de algemene Locks & Keys-scope toont ook de
    // bodygraph de keys-imprint van de periode i.p.v. de actieve chart.
    const base = this.locksKeysAwareDisplayConfiguration();
    const hoveredGate = this.moonTransitHoveredGate();
    if (!this.moonTransitMode() || hoveredGate == null) {
      return base;
    }
    return {
      ...base,
      activations: [
        ...base.activations,
        {
          id: 'moon-transit-hover',
          gate: hoveredGate,
          side: 'highlight' as const,
          sourceType: 'manual' as const,
          sourceKey: 'moon-transit-hover',
        },
      ],
    };
  });

  /**
   * Zijde-filter voor de getoonde chart: `full` = volledige chart (design +
   * personality), `design`/`personality` = enkel die zijde. Filtert puur de
   * weergave (chart, mandala, eigenschappen, planeetgrid) — de onderliggende
   * `configuration` blijft de volledige bewerkbare chart. Zo herrekenen de
   * afgeleide eigenschappen (type, autoriteit, definitie, …) net als bij een
   * handmatige edit, zodat men bv. ziet dat de design-zijde een Reflector zou
   * zijn i.p.v. een Generator.
   */
  protected readonly chartSideMode = signal<HdChartSideMode>('full');

  /**
   * Drieluik-weergave (spec 2026-07-31): design | volledig | personality als
   * drie gelijktijdige charts. Sessie-lokaal, zoals `chartSideMode`. Op een
   * cycle-/composite-tab wordt het een bron-drieluik (zie
   * {@link tripleLeftChart} / {@link tripleRightChart}).
   */
  protected readonly tripleChartView = signal(false);

  /**
   * Zijde-modus die templates/computeds effectief gebruiken: het drieluik
   * toont alle zijden tegelijk en gedraagt zich dus als 'full', zonder de
   * onderliggende `chartSideMode` te muteren (effective-override, spec §4.2).
   *
   * Op gecombineerde (cycle-/composite-)tabs is de design/personality-FILTER
   * nooit van toepassing (bijstuurronde 7): de zijde-knoppen kiezen daar een
   * KANT via {@link combinedSideSelection} — de filter zou enkel de top-level
   * chart raken terwijl overlay, wiel-glyphs en grids hem negeren (de
   * incoherentie uit ronde 6).
   */
  protected readonly effectiveSideMode = computed<HdChartSideMode>(() =>
    this.tripleChartView() || this.cycleOrCompositeActive() ? 'full' : this.chartSideMode(),
  );

  /**
   * Kant-selectie op gecombineerde (cycle-/composite-)tabs (bijstuurronde 7):
   * de zijde-knoppen kiezen daar geen design/personality-zijde maar een kant —
   * D-knop (links) = chart 1 / natal, P-knop (rechts) = chart 2 / cyclusmoment,
   * midden = de gecombineerde twee-kleurenweergave. De geselecteerde kant
   * wordt als volwaardige, op zichzelf staande chart getoond (volledige P+D,
   * zonder overlay, read-only), zodat élke consument stroomafwaarts — wiel,
   * bodygraph, Locks & Keys, panelen — via de gewone display*-pijplijn
   * hetzelfde antwoord geeft. Buiten gecombineerde tabs altijd 'both'.
   */
  protected readonly combinedSideSelection = computed<'left' | 'both' | 'right'>(() => {
    if (!this.cycleOrCompositeActive() || this.tripleChartView()) {
      return 'both';
    }
    const mode = this.chartSideMode();
    return mode === 'design' ? 'left' : mode === 'personality' ? 'right' : 'both';
  });

  /**
   * Zijcharts van het drieluik (spec §6.1 + feedback 2026-08-23). Gewone tab:
   * de zijde-gefilterde design-/personality-chart. Composite-tab: links chart 1
   * (de persoon van de top-level snapshot), rechts chart 2 (de partner) —
   * beide als volledige D+P-chart, met hun naam als label in de chart-1-blauwe
   * resp. chart-2-groene overlaykleur. Cycle-tab: links de persoon (natal),
   * rechts het cyclusmoment zelf. Het middenchart toont in beide gevallen de
   * combinatie (top-level + twee-kleurenoverlay).
   */
  protected readonly tripleLeftChart = computed<HdTripleSideChart>(() => {
    const composite = this.activeCompositeInfo();
    const cycle = this.activeCycleInfo();
    if (composite != null || cycle != null) {
      return {
        configuration: this.configuration(),
        label: composite?.source.name ?? cycle?.source.name ?? '',
        labelKind: 'chart1',
      };
    }
    return {
      configuration: filterHdBodygraphConfigBySide(this.configuration(), 'design'),
      label: 'DESIGN / BODY',
      labelKind: 'design',
    };
  });

  protected readonly tripleRightChart = computed<HdTripleSideChart>(() => {
    const composite = this.activeCompositeInfo();
    if (composite != null) {
      return {
        configuration: composite.partnerChart.configuration,
        label: composite.partner.name,
        labelKind: 'chart2',
      };
    }
    const cycle = this.activeCycleInfo();
    if (cycle != null) {
      const lang = this.pageContentLang.effectiveLang();
      const translate = (key: string) => this.pageTransloco.translate<string>(key, undefined, lang);
      return {
        configuration: cycle.moment.configuration,
        label: buildCycleMomentLabel(translate, cycle.key, cycle.anchorUtc, cycle.solarReturnYear),
        labelKind: 'chart2',
      };
    }
    return {
      configuration: filterHdBodygraphConfigBySide(this.configuration(), 'personality'),
      label: 'PERSONALITY',
      labelKind: 'personality',
    };
  });

  /** Knopgrootte van de shortcut-bar (globaal persistente setting; default `sm`). */
  protected readonly shortcutButtonSize = this.settings.shortcutButtonSize;

  /** Scope van de "gelijkaardige charts"-matchlijsten (globaal persistente setting). */
  protected readonly chartMatchScope = this.settings.chartMatchScope;

  private readonly sideRenderStateFactory = inject(HdBodygraphRenderStateFactory);

  /**
   * Configuratie die effectief gevisualiseerd wordt: op gecombineerde tabs de
   * geselecteerde kant ({@link combinedSideSelection} — chart 2/moment als
   * volwaardige eigen chart), anders de op `effectiveSideMode` gefilterde
   * chart. 'left' en 'both' geven bewust dezelfde referentie terug (de
   * top-level chart 1); het verschil zit in de overlay ({@link chartOverlay})
   * en de glyphs.
   */
  protected readonly displayConfiguration = computed(() => {
    const config = this.configuration();
    const combined = this.combinedSideSelection();
    if (combined === 'right') {
      return this.overlayMomentChart()?.configuration ?? config;
    }
    const mode = this.effectiveSideMode();
    if (mode === 'full') {
      return config;
    }
    return filterHdBodygraphConfigBySide(config, mode);
  });

  /**
   * Render-state van de getoonde configuratie: exact de top-level chart
   * hergebruikt zijn bestaande state; elke afwijkende weergave (zijde-filter
   * of kant-selectie) krijgt een eigen berekening.
   */
  protected readonly displayRenderState = computed(() => {
    const displayed = this.displayConfiguration();
    return displayed === this.configuration()
      ? this.renderState()
      : this.sideRenderStateFactory.create(displayed);
  });

  /** Planeetactivaties die bij de getoonde kant/zijde horen (voor het Basis-paneel). */
  protected readonly displayPlanetaryActivations = computed(() => {
    const combined = this.combinedSideSelection();
    if (combined === 'right') {
      return this.overlayMomentChart()?.planetaryActivations ?? [];
    }
    if (combined === 'left') {
      return this.allPlanetaryActivations();
    }
    switch (this.effectiveSideMode()) {
      case 'design':
        return this.designActivations();
      case 'personality':
        return this.personalityActivations();
      default:
        return this.allPlanetaryActivations();
    }
  });

  /**
   * In een enkel-zijde-weergave is de chart louter ter inzage: forceer de
   * info-modus zodat klikken op poorten geen edits maakt (die zouden immers
   * enkel op de gefilterde zijde inwerken en de verborgen zijde wissen).
   * Idem in de maan-transit modus: de chart is daar puur observatie, en de
   * hover-highlight-activatie mag nooit via een gate-cycle in de echte
   * configuratie belanden. En idem in een kant-weergave op gecombineerde
   * tabs: een edit op de getoonde chart 2 zou anders in de top-level chart 1
   * belanden.
   */
  protected readonly displayPrimaryGateAction = computed(() =>
    this.effectiveSideMode() === 'full' && this.combinedSideSelection() === 'both'
      && !this.moonTransitMode() && !this.justNowTransitVisualsActive() && !this.locksKeysMode()
      // Phone is bekijken + info (besluit 3, docs/MOBIEL.md §9): nooit editen.
      && !this.viewport.isPhone()
      ? this.chartGatePrimaryAction()
      : 'show-info',
  );
  /** Mini-densiteit van de volledige planeetgrid (mobiel profiel, docs/MOBIEL.md §9). */
  protected readonly miniPlanetGrid = this.settings.miniPlanetGrid;

  /** Densiteit voor de hd-planet-grid-instanties: mini enkel in de klassieke volle grid. */
  protected readonly effectivePlanetGridDensity = computed<HdPlanetGridDensity>(() =>
    this.miniPlanetGrid() && !this.effectiveAdvancedLpsGrid() ? 'mini' : 'normal',
  );

  protected readonly showMandalaZodiacRing = signal(false);
  protected readonly showMandalaQuarterRing = signal(false);
  protected readonly showMandalaIchingRing = signal(false);
  protected readonly showMandalaGodheadRing = signal(false);
  protected readonly showPlanetSideStats = signal(true);

  /**
   * Viewmodel voor de tab-bar: de actieve tab volgt de LIVE pagina-signals
   * zodat de titel meteen reageert op laden/edits/spel; inactieve tabs tonen
   * hun opgeslagen snapshot-staat.
   */
  protected readonly tabBarItems = computed<readonly HdChartTabBarItem[]>(() => {
    const activeId = this.tabsService.activeTabId();
    return this.tabsService.tabs().map(tab =>
      tab.id === activeId
        ? {
            id: tab.id,
            presetName: this.activePreset()?.name ?? null,
            presetModified: this.presetModified(),
            gameMode: this.gameMode(),
            presetTooltip: this.activePreset()?.tabTooltip ?? null,
            unsaved: this.canSaveActiveChart(),
            shared: this.isSharedTab(),
            // Enkel de actieve tab laadt; een stille verversing (data al zichtbaar) telt niet.
            loading: this.loadingPreset() || this.dataCache.isColdLoading(this.savedChartId()),
            printable: isHdChartPrintable(this.activePreset()),
          }
        : {
            id: tab.id,
            presetName: tab.presetName,
            presetModified: tab.presetModified,
            gameMode: tab.game.gameMode,
            presetTooltip: tab.preset?.tabTooltip ?? null,
            unsaved: tab.preset != null
              && ((tab.savedChartId ?? null) == null || (tab.pendingSaveChanges ?? false))
              && ((tab.sharedToken ?? null) == null || (tab.chartType ?? PlatformChartType.Standard) === PlatformChartType.Standard),
            shared: (tab.sharedToken ?? null) != null,
            printable: isHdChartPrintable(tab.preset),
          },
    );
  });


  constructor() {
    super();

    // 1. Globale settings hydrateren gebeurt in HdChartSettingsStore (centrale
    //    signal-store); de settings-signals hierboven zijn aliassen daarop, dus
    //    ze zijn al gehydrateerd vóór het eerste tab-apply.

    // 2. Tabs laden en het actieve snapshot toepassen.
    const initialTab = this.tabsService.initialize();
    this.applySnapshot(initialTab);

    // 3. Autosave actieve tab: elke relevante wijziging debounced wegschrijven
    //    (de service debounced zelf; dit effect levert enkel verse snapshots).
    effect(() => {
      const snapshot = this.trackAndExtractSnapshot();
      untracked(() => this.tabsService.updateActiveSnapshot(snapshot));
    });

    // 4. Globale settings persisteren gebeurt in HdChartSettingsStore.

    // Lock-/key-iconen in de poort-info mogen de modus enkel openen zolang
    // deze pagina leeft (cheat sheets e.d. tonen dan enkel de tooltip).
    this.locksKeysState.modeLauncher.set(periodId => this.launchLocksKeysMode(periodId));
    inject(DestroyRef).onDestroy(() => this.locksKeysState.modeLauncher.set(null));

    // Browsertab: de naam van de actieve chart, zodat meerdere browsertabs te onderscheiden
    // zijn. Niet in spelmodus: daar is de chart verborgen (ook de tab toont dan geen naam).
    effect(() => {
      const chartName = this.gameMode() ? undefined : (this.activePreset()?.name ?? undefined);
      untracked(() => this.pageTitle.setDetail(chartName));
    });
    inject(DestroyRef).onDestroy(() => this.pageTitle.setDetail(undefined));

    // Poort- en Locks & Keys-zoektochten openen hun treffers via deze pagina
    // (nieuwe tab, zoals de matchlijsten in Basis); zonder pagina verbergen ze zich.
    this.chartSearchContext.chartOpener.set(chartId => void this.loadChartFromLibrary(chartId, true));
    // Scope-toggle boven de resultaten = snelkoppeling naar dezelfde gebruikerssetting.
    this.chartSearchContext.matchScopeSetter.set(scope => this.chartMatchScope.set(scope));
    inject(DestroyRef).onDestroy(() => {
      this.chartSearchContext.chartOpener.set(null);
      this.chartSearchContext.matchScopeSetter.set(null);
      this.chartSearchContext.activeChartId.set(null);
      this.chartSearchContext.activeChartKey.set(null);
      this.chartSearchContext.relationSourceChartId.set(null);
      this.chartSearchContext.compositeActive.set(false);
    });

    // Doorklikken vanuit het Just Now-paneel lopen via de gewone handlers van
    // de pagina (open-gedrag-instellingen, transitlaag in de info-panelen).
    this.justNowPanelState.requestHandlers.set({
      openGateInfo: request => this.handleGateInfoRequested(request),
      openChannelInfo: request => this.handleChannelInfoRequested(request),
      openCenterInfo: request => this.handleCenterInfoRequested(request),
    });
    inject(DestroyRef).onDestroy(() => this.justNowPanelState.requestHandlers.set(undefined));

    // 6. Hangende debounced writes flushen bij navigatie weg van de pagina,
    //    zodat de laatste edits geen 300–500 ms verliezen. We schrijven eerst
    //    het allerlaatste snapshot weg: een edit waarvan het autosave-effect
    //    nog niet gedraaid heeft (één CD-tick venster) zou anders verloren gaan.
    inject(DestroyRef).onDestroy(() => {
      // Settings-persistentie zit in HdChartSettingsStore (root, flush op hidden).
      this.tabsService.updateActiveSnapshot(this.extractSnapshot());
      this.tabsService.flush();
      // Tijdslider: hangende sample-aanvraag stoppen en een eigen pin op de
      // app-weergavetijd loslaten (de transits-pagina mag niet bevroren achterblijven).
      this.cancelPendingTransitSample();
      this.releaseSliderViewTimePin();
    });

    // 7. Cross-page inladen: ?loadChartId= → laad de library-chart in een verse
    //    tab. Met ?openTimeline=1 (chart manager-pagina) klikt de flow daarna
    //    meteen door naar de levenstijdlijn van die chart, zodat de terugknop
    //    op deze chart-pagina uitkomt. Met ?openNotes=1 (kalender, spec
    //    levenstijdlijn §2.9) opent de chart met het Notities-paneel actief,
    //    ook wanneer het een gewone (niet-moment-)chart is.
    const loadChartId = this.route.snapshot.queryParamMap.get('loadChartId');
    if (loadChartId) {
      const openTimeline = this.route.snapshot.queryParamMap.get('openTimeline') === '1';
      if (this.route.snapshot.queryParamMap.get('openNotes') === '1') {
        this.forceNotesTabOnce.set(true);
      }
      void this.loadChartFromLibrary(loadChartId, /* inNewTab */ true).then(() => {
        if (openTimeline) {
          void this.handleTimelineRequested(loadChartId);
          return;
        }
        void this.router.navigate([], { queryParams: {}, replaceUrl: true });
      });
    }

    // 7a. Vanaf de transits-pagina (die heeft geen tabs): ?justNow=transit of
    //     ?raveNewYear=<jaar> opent die transit-chart in een nieuwe tab.
    const justNowParam = this.route.snapshot.queryParamMap.get('justNow');
    const raveNewYearParam = Number(this.route.snapshot.queryParamMap.get('raveNewYear'));
    if (justNowParam === 'transit' || (Number.isInteger(raveNewYearParam) && raveNewYearParam > 0)) {
      const opened = justNowParam === 'transit'
        ? this.handleJustNowRequested({ personalityOnly: true })
        : this.handleRaveNewYearRequested({ year: raveNewYearParam, variant: 'transit' });
      void opened.then(() => this.router.navigate([], { queryParams: {}, replaceUrl: true }));
    }

    // 7b. Deellink geopend door een ingelogde kijker (spec 2026-10-20-chart-share
    //     §4.6): ?sharedToken= → gedeelde tab (of de eigen library-chart als de
    //     eigenaar zijn eigen link opent). De publieke gastpagina stuurt hierheen.
    const sharedToken = this.route.snapshot.queryParamMap.get('sharedToken');
    if (sharedToken) {
      void this.loadSharedChart(sharedToken).then(() => {
        void this.router.navigate([], { queryParams: {}, replaceUrl: true });
      });
    }

    // 8. Cycle-, composite- of momentchart (transit-blok) in beeld (openen,
    //    berekenen én page-refresh-restore) → standaard de Geavanceerd-docktab
    //    selecteren. Sinds 2026-10-14 ook voor momentcharts (Steven: zoals een
    //    cycle chart, i.p.v. Notities); enkel het openen vanuit een kalender
    //    (`forceNotesTabOnce`, spec levenstijdlijn §2.9) kiest nog Notities.
    //    Dedupe op preset-referentie zodat één keer per getoonde preset
    //    volstaat; zolang de dock-area (of zijn tabs) er nog niet is blijft de
    //    activatie pending.
    effect(() => {
      const preset = this.activePreset();
      const dockArea = this.rightDockArea();
      if (preset === this.lastAutoAdvancedPreset) {
        return;
      }
      const forceNotes = this.forceNotesTabOnce();
      // Staan de notities in een zwevend venster, dan zijn ze al zichtbaar: de
      // vaste Notities-tab (met enkel de plaatshouder) hoeft niet naar voren.
      const notesFloating = untracked(() => this.notesFloating());
      if (forceNotes && notesFloating) {
        this.forceNotesTabOnce.set(false);
      }
      const wantsNotesTab = forceNotes && !notesFloating;
      if (preset?.cycle == null && preset?.composite == null && preset?.transit == null && !wantsNotesTab) {
        this.lastAutoAdvancedPreset = preset;
        return;
      }
      const targetTab = wantsNotesTab ? 'notes' : 'advanced';
      if (dockArea?.activateFixedTab(targetTab)) {
        this.lastAutoAdvancedPreset = preset;
        if (forceNotes) this.forceNotesTabOnce.set(false);
        // Race met de dock-area zelf (finetune 2026-09-21): een panel dat bij
        // de tabwissel (opnieuw) dockt activeert zichzelf ná deze activatie en
        // zou de gekozen tab weer verdringen — twee idempotente bevestigingen.
        setTimeout(() => dockArea.activateFixedTab(targetTab));
        setTimeout(() => dockArea.activateFixedTab(targetTab), 150);
      }
    });

    // Andere preset in beeld → Basis-tabblad terug naar persoon 1 (composite).
    effect(() => {
      this.activePreset();
      this.basicCompositeSide.set(1);
    });

    // Verplaats-tool nooit actief laten hangen wanneer álle versleepbare lagen
    // weg zijn (blokjes-laag én maan-transit-kaarten).
    effect(() => {
      const available = this.gateBlockLayerVisible() || this.moonTransitCardsVisible();
      untracked(() => {
        if (!available && this.moveToolActive()) {
          this.moveToolActive.set(false);
        }
      });
    });

    // Annotatie actief (knop óf P-toets in de annotatielaag) → verplaats-tool uit
    // (wederzijds exclusief, spec §8).
    effect(() => {
      const annotating = this.annotationStore.active();
      untracked(() => {
        if (annotating && this.moveToolActive()) {
          this.moveToolActive.set(false);
        }
      });
    });

    // Het zwevende notities-venster volgt de actieve charttab (zelfde bron
    // als de vaste Notities-tab); replaceData = stil, zonder focus-wissel.
    effect(() => {
      const savedChartId = this.savedChartId();
      const chartName = this.activePreset()?.name ?? '';
      const panel = this.transitsFloatingPanels.panels().find(p => p.key === this.notesPanelKey) as
        | FloatingPanelRef<HdNotesFloatingPanelComponent, HdNotesFloatingPanelData>
        | undefined;
      const current = panel?.data();
      if (panel && (current?.savedChartId !== savedChartId || current?.chartName !== chartName)) {
        untracked(() => panel.replaceData(this.buildNotesPanelData()));
      }
    });

    // De panelen van het Basis-paneel (type, autoriteit, definitie, profiel,
    // levenskruis) volgen de actieve chart, zoals de poortpanelen in de basis.
    // Zie refreshOpenBasicsInfoPanels.
    effect(() => {
      this.activePreset();
      this.basicConfiguration();
      this.basicPlanetaryActivations();
      untracked(() => this.refreshOpenBasicsInfoPanels());
    });

    // showChiron live doorgeven aan een open transits-paneel: de globale
    // setting kan wijzigen via het settings-panel terwijl het paneel open
    // staat. replaceData = stil (geen focus-/interactie-update).
    effect(() => {
      const showChiron = this.showChiron();
      const panel = this.transitsFloatingPanels.panels().find(p => p.key === this.transitsPanelKey) as
        | FloatingPanelRef<HdTransitsPanelComponent, HdTransitsPanelData>
        | undefined;
      if (panel && panel.data()?.showChiron !== showChiron) {
        untracked(() => panel.replaceData(this.buildTransitsPanelData()));
      }
    });

    // Live seintjes van andere toestellen/browsertabs (spec chart-datacache 2026-10-06 §4.2):
    // de datacache is al bijgewerkt; hier volgen de open tabs (naam/document, verwijderd).
    this.dataCache.remoteChanges$
      .pipe(takeUntilDestroyed())
      .subscribe(change => this.handleRemoteChartChange(change));

    // Bibliotheek = bron van waarheid (incident 2026-10-22): een tab bewaart een volledige kopie
    // van het document, ook over herstarts en toestellen heen. De chart van de actieve tab blijft
    // daarom bekeken in de datacache, en elke verse bibliotheekversie (opstart, tabwissel,
    // overschrijvende import, bewerking op de managerpagina, seintje van elders) wordt met de tab
    // vergeleken. De datacache ontdubbelt: de Notities draaien op dezelfde ophaling.
    effect(onCleanup => {
      const chartId = this.savedChartId();
      if (chartId) onCleanup(this.dataCache.chartMeta.watch(chartId));
    });
    effect(() => {
      // Ook bij een wissel tussen twee tabs van dezelfde chart opnieuw vergelijken.
      this.tabsService.activeTabId();
      this.reconcileActiveTabWithCachedLibrary();
    });
  }

  /** Leest (in een effect: getrackt) de gecachete bibliotheekversie van de actieve chart en vergelijkt. */
  private reconcileActiveTabWithCachedLibrary(): void {
    const chartId = this.savedChartId();
    const entry = this.dataCache.chartMeta.entry(chartId);
    // Enkel een vers, afgerond antwoord: een oude waarde die nog stil herlaadt telt niet.
    if (!chartId || entry == null || entry.loading || entry.stale || entry.value == null) return;
    // De cache bewaart de ChartDocumentDto van getChartDocument; een notitie-update levert
    // een ChartDto zonder document (dan valt er niets te vergelijken tot de volgende ophaling).
    const meta = entry.value;
    const document = 'document' in meta && typeof meta.document === 'string' ? meta.document : undefined;
    if (!document) return;
    untracked(() => this.reconcileActiveTabWithLibrary(`${chartId}|${entry.loadedAt ?? ''}`, chartId, document, meta.type));
  }

  /** Laatst vergeleken bibliotheekversie + tabkopie: elk paar maar één keer parsen en vergelijken. */
  private lastReconciled: { version: string; tabPreset: HdBodygraphChartPreset | null } | null = null;
  /** Al gemelde afwijkingen (chart + geboortemoment in de bibliotheek): één melding per afwijking. */
  private readonly notifiedLibraryConflicts = new Set<string>();

  /**
   * Vergelijkt de actieve tab met de verse bibliotheekversie van zijn chart.
   *
   * BELANGRIJK: een clean tab volgt altijd de bibliotheek. Anders toont hij een verouderde chart
   * terwijl "Chart bewerken" de bewaarde versie toont (incident 2026-10-22: tab 14:22, bibliotheek
   * 12:55 na een overschrijvende import), en schrijven hernoemen of opslaan die oude kopie terug.
   * Onopgeslagen werk wint zoals bij de seintjes van elders; verschilt dan het geboortemoment, dan
   * volgt één melding en vraagt opslaan eerst bevestiging ({@link confirmOverwriteOfLibraryVersion}).
   * Tests: "bibliotheek als bron van waarheid" in de spec.
   */
  private reconcileActiveTabWithLibrary(
    version: string,
    chartId: string,
    document: string,
    type: PlatformChartType | null | undefined,
  ): void {
    const tabPreset = this.activePreset();
    if (this.lastReconciled?.version === version && this.lastReconciled.tabPreset === tabPreset) return;
    this.lastReconciled = { version, tabPreset };
    if (this.savedChartId() !== chartId || tabPreset == null) return;
    let library: HdBodygraphChartPreset;
    try {
      library = JSON.parse(document) as HdBodygraphChartPreset;
    } catch {
      return;
    }
    if (hdChartContentFingerprint(library) === hdChartContentFingerprint(tabPreset)) return;
    if (!this.presetModified() && !this.pendingSaveChanges() && !this.gameMode()) {
      // Geen docktab-sprong (cycle → Geavanceerd) bij een verversing.
      this.lastAutoAdvancedPreset = library;
      this.applySavedChart(library, chartId, type ?? PlatformChartType.Standard);
      return;
    }
    const libraryMoment = hdChartBirthMomentKey(library);
    if (libraryMoment === hdChartBirthMomentKey(tabPreset)) return;
    const conflictKey = `${chartId}|${libraryMoment}`;
    if (this.notifiedLibraryConflicts.has(conflictKey)) return;
    this.notifiedLibraryConflicts.add(conflictKey);
    this.notifier.info(this.pageTransloco.translate<string>(
      'humanDesign.chart.libraryVersion.differs',
      { library: hdChartBirthMomentLabel(library), tab: hdChartBirthMomentLabel(tabPreset) },
      this.pageContentLang.effectiveLang(),
    ));
  }

  /**
   * Natal-bron voor een afgeleide chart (cycle, composite, moment) die naar deze opgeslagen chart
   * verwijst: altijd de bibliotheekversie, net als de composite-partner. Een verouderde tabkopie gaf
   * zo ooit een composite en een Just Now-cycle met 12:55 op een bronchart van 14:22 (2026-10-22).
   * Tabhandwerk telt niet mee: een afgeleide chart neemt enkel geboortegegevens en activaties over.
   */
  private async libraryNatalPreset(chartId: string): Promise<HdBodygraphChartPreset> {
    return (await this.chartLibrary.getChartDocument(chartId)).preset;
  }

  /**
   * Vóór een tab een opgeslagen chart overschrijft: heeft de bibliotheekversie een ander
   * geboortemoment dan de tab, dan was de tab verouderd (of bewust herberekend op een andere tijd)
   * en beslist de gebruiker. Weigeren zet de bibliotheekversie in de tab. Een mislukte check
   * blokkeert de save niet (zelfde principe als de duplicaatcheck in de manager).
   */
  private async confirmOverwriteOfLibraryVersion(chartId: string, tabPreset: HdBodygraphChartPreset): Promise<boolean> {
    let loaded: HdChartDocumentResult;
    try {
      loaded = await this.chartLibrary.getChartDocument(chartId);
    } catch {
      return true;
    }
    if (hdChartBirthMomentKey(loaded.preset) === hdChartBirthMomentKey(tabPreset)) return true;
    const lang = this.pageContentLang.effectiveLang();
    const params = { library: hdChartBirthMomentLabel(loaded.preset), tab: hdChartBirthMomentLabel(tabPreset) };
    const confirmed = await this.pageConfirmer.confirm(
      this.pageTransloco.translate<string>('humanDesign.chart.libraryVersion.confirmOverwrite', params, lang),
      { color: 'danger' },
    );
    if (confirmed) return true;
    if (this.savedChartId() === chartId) {
      this.applySavedChart(loaded.preset, chartId, loaded.meta.type ?? PlatformChartType.Standard);
      this.notifier.info(this.pageTransloco.translate<string>('humanDesign.chart.libraryVersion.restored', params, lang));
    }
    return false;
  }

  /** Gate-info panels docken op deze pagina in de uitgebreide (full) zone. */
  protected override gateInfoDockZone(): FloatingPanelDockZone {
    return 'full';
  }

  /** Een chart-load sluit de tijdelijke tool, maar behoudt de pagina-annotaties. */
  protected override handlePresetApplied(preset: HdBodygraphChartPreset): void {
    super.handlePresetApplied(preset);
    this.annotationStore.setTool('none');
    // Cycle-tabs sluiten de transit-modi uit (spec cycle charts §10); het
    // drieluik blijft — het toont daar persoon | combinatie | cyclus.
    if (preset?.cycle != null) {
      this.deactivateJustNowTransitMode();
      this.moonTransitMode.set(false);
      this.moonTransitToday.set(null);
    }
  }

  // ── Snapshot mapping ────────────────────────────────────────────────────

  /** Leest (en TRACKT) alle per-tab state; gebruikt door het autosave-effect. */
  private trackAndExtractSnapshot(): HdChartTabSnapshotV1 {
    return {
      schemaVersion: 1,
      id: untracked(() => this.tabsService.activeTabId()),
      presetName: this.activePreset()?.name ?? null,
      presetModified: this.presetModified(),
      preset: this.activePreset(),
      savedChartId: this.savedChartId(),
      chartType: this.activeChartType(),
      pendingSaveChanges: this.pendingSaveChanges(),
      sharedToken: this.activeSharedToken(),
      sharedByName: this.activeSharedByName(),
      chartSideMode: this.chartSideMode(),
      configuration: this.configuration(),
      game: {
        gameMode: this.gameMode(),
        selectedGameId: this.selectedGameId(),
        selectedVariantId: this.selectedGameVariantId(),
        gateRangeSortEnabled: this.gateRangeSortEnabled(),
        gateRangeStart: this.gateRangeStart(),
        gateRangeEnd: this.gateRangeEnd(),
        drawChartTargetId: this.selectedDrawChartTargetId(),
      },
    };
  }

  private extractSnapshot(): HdChartTabSnapshotV1 {
    return untracked(() => this.trackAndExtractSnapshot());
  }

  private applySnapshot(snapshot: HdChartTabSnapshotV1): void {
    // Zelfde shift-markering als bij handlePresetApplied: een herstelde tab
    // (ook een snapshot van vóór deze regel) rekent zo meteen correct.
    const preset = snapshot.preset == null ? snapshot.preset : applyHdGlobalShiftMarkers(snapshot.preset);
    const bornAfterShift = preset != null && isHdMomentAfterGlobalShift(hdBirthUtc(preset.birthData));
    this.configuration.set(preset == null
      ? snapshot.configuration
      : markHdConfigurationForGlobalShift(snapshot.configuration, bornAfterShift));
    this.activePreset.set(preset);
    this.presetModified.set(snapshot.presetModified);
    this.savedChartId.set(snapshot.savedChartId ?? null);
    this.activeChartType.set(snapshot.chartType ?? PlatformChartType.Standard);
    this.pendingSaveChanges.set(snapshot.pendingSaveChanges ?? false);
    this.activeSharedToken.set(snapshot.sharedToken ?? null);
    this.activeSharedByName.set(snapshot.sharedByName ?? null);
    this.chartSideMode.set(snapshot.chartSideMode ?? 'full');
    this.gameMode.set(snapshot.game.gameMode);
    this.revealedGates.set(new Set<number>());
    // Laad-transient is per-tab: een spinner van de vorige tab mag niet
    // blijven hangen na een wissel.
    this.loadingPreset.set(false);
    // De tools zijn transiënte UI-state: per tab-wissel uit (spec §7).
    this.moveToolActive.set(false);
    this.annotationStore.setTool('none');
    this.applyGameSettings(snapshot.game);
  }

  /** Spelkeuze van een tab (alles behalve de spelmodus zelf). */
  private applyGameSettings(game: Omit<HdChartTabGameSettingsV1, 'gameMode'>): void {
    this.selectedGameId.set(game.selectedGameId);
    this.selectedGameVariantId.set(game.selectedVariantId);
    this.gateRangeSortEnabled.set(game.gateRangeSortEnabled);
    this.gateRangeStart.set(game.gateRangeStart);
    this.gateRangeEnd.set(game.gateRangeEnd);
    this.selectedDrawChartTargetId.set(game.drawChartTargetId ?? '');
  }

  // ── Tab-acties ──────────────────────────────────────────────────────────

  protected async handleTabSelected(tabId: string): Promise<void> {
    if (tabId === this.tabsService.activeTabId()) {
      return;
    }
    if (!(await this.confirmEndGame('switch'))) {
      return;
    }
    this.stopGamesForTabChange();
    const target = this.tabsService.switchTo(tabId, this.extractSnapshot());
    if (target) {
      this.applySnapshot(target);
    }
  }

  protected async handleTabCloseRequested(tabId: string): Promise<void> {
    const closingActive = tabId === this.tabsService.activeTabId();
    if (closingActive && !(await this.confirmEndGame('close'))) {
      return;
    }
    if (closingActive) {
      this.stopGamesForTabChange();
    }
    const next = this.tabsService.closeTab(tabId);
    if (next) {
      this.applySnapshot(next);
    }
  }

  /**
   * "+"-tab: verse tab én meteen de chart manager (sinds 2026-08-23 de enige
   * chartbron; het settings-panel schuift niet meer open). Een keuze in de
   * manager landt in deze verse tab (zie {@link handleManagerLoadRequested}).
   */
  protected async handleAddTabRequested(): Promise<void> {
    if (!(await this.confirmEndGame('switch'))) {
      return;
    }
    this.stopGamesForTabChange();
    const tab = this.tabsService.addTab(this.extractSnapshot());
    this.applySnapshot(tab);
    this.managerDialogOpen.set(true);
  }

  /**
   * Sluit alle tabs en laat één verse "Vrije modus"-tab achter. Vraagt
   * bevestiging omdat dit álle tab-snapshots (charts, edits) wist; doet niets
   * wanneer er al maar één verse tab is.
   */
  protected async handleCloseAllTabsRequested(): Promise<void> {
    if (this.tabsService.tabs().length === 1 && this.isActiveTabFresh()) {
      return;
    }
    const confirmed = await this.pageConfirmer.confirm(
      'Alle tabs sluiten? Dit verwijdert alle tabs en laat één lege "Vrije modus"-tab achter.',
      { color: 'danger' },
    );
    if (!confirmed) {
      return;
    }
    this.stopGamesForTabChange();
    const fresh = this.tabsService.resetToSingleTab();
    this.applySnapshot(fresh);
  }

  /** Drag-reorder vanuit de tab-bar: nieuwe volgorde persisteert in de service. */
  protected handleTabReordered(event: { previousIndex: number; currentIndex: number }): void {
    this.tabsService.reorderTabs(event.previousIndex, event.currentIndex);
  }

  protected handleCloseTabsToLeftRequested(tabId: string): Promise<void> {
    return this.closeSurroundingTabs(tabId, 'left');
  }

  protected handleCloseTabsToRightRequested(tabId: string): Promise<void> {
    return this.closeSurroundingTabs(tabId, 'right');
  }

  /**
   * Sluit in bulk de tabs links/rechts van de anker-tab. Vraagt enkel
   * spel-bevestiging wanneer de actieve tab tot de te sluiten tabs behoort en
   * past het nieuwe actieve snapshot toe wanneer de service er één teruggeeft.
   */
  private async closeSurroundingTabs(tabId: string, side: 'left' | 'right'): Promise<void> {
    const tabs = this.tabsService.tabs();
    const anchorIndex = tabs.findIndex(tab => tab.id === tabId);
    if (anchorIndex === -1) {
      return;
    }
    const activeIndex = tabs.findIndex(tab => tab.id === this.tabsService.activeTabId());
    const activeClosed = side === 'left' ? activeIndex < anchorIndex : activeIndex > anchorIndex;
    if (activeClosed && !(await this.confirmEndGame('close'))) {
      return;
    }
    if (activeClosed) {
      this.stopGamesForTabChange();
    }
    const next = side === 'left'
      ? this.tabsService.closeTabsToTheLeft(tabId)
      : this.tabsService.closeTabsToTheRight(tabId);
    if (next) {
      this.applySnapshot(next);
    }
  }

  /**
   * Confirm-prompt vóór een tab-switch/-close die de spelstate zou wissen.
   *
   * - Geen spel actief → geen prompt.
   * - Lopend, onafgewerkt spel → "…wordt het lopende spel beëindigd" (spec §7).
   * - Afgewerkt spel (score-scherm nog zichtbaar) → "…wordt de spelstatus gewist",
   *   want ook een afgewerkt spel gaat verloren bij een tab-wissel/-sluit.
   */
  private async confirmEndGame(kind: 'switch' | 'close'): Promise<boolean> {
    if (!this.gameActive()) {
      return true;
    }
    const suffix = kind === 'switch' ? 'TabSwitch' : 'TabClose';
    const prefix = this.gameFinished() ? 'clearGameStateOn' : 'endGameOn';
    const messageKey = `humanDesign.chart.controls.${prefix}${suffix}`;
    const message = this.pageTransloco.translate(messageKey, undefined, this.pageContentLang.effectiveLang());
    return this.pageConfirmer.confirm(message, { color: 'danger' });
  }

  /** Beëindigt lopende spellen via het bestaande stop-pad van de base-page. */
  private stopGamesForTabChange(): void {
    if (this.gameActive()) {
      this.handleStopRequested();
    }
  }

  // ── Shortcut-bar handlers ───────────────────────────────────────────────

  /** Pen-knop in de shortcut-bar (of toets P): annotatie-modus aan/uit. */
  protected handleAnnotateToggled(): void {
    this.annotationStore.toggleTool('pen');
    if (this.annotationStore.active()) {
      this.moveToolActive.set(false);
    }
  }

  // ── Planeetblokjes verplaatsen ──────────────────────────────────────────

  /** Verplaats-knop: tool aan/uit; aanzetten sluit de annotatie-modus (spec §8). */
  protected handleMoveToolToggled(): void {
    const next = !this.moveToolActive();
    this.moveToolActive.set(next);
    if (next) {
      this.annotationStore.setTool('none');
    }
  }

  /** Escape verlaat de verplaats-tool (zelfde afsluit-idioom als de annotatie-modus). */
  @HostListener('document:keydown.escape', ['$event'])
  protected onMoveToolEscape(event: Event): void {
    if (this.moveToolActive()) {
      event.preventDefault();
      // Voorrang op de hover-ontdooi-Esc (capture-listener in de hover-service):
      // spiegelt de annotatielaag zodat één Esc-druk enkel de tool afsluit en
      // niet ook een open dialog sluit. Op deze page is de hover-service nooit
      // actief, dus de capture-listener is hier sowieso een no-op.
      event.stopPropagation();
      this.moveToolActive.set(false);
    }
  }

  /**
   * Pin-mutaties lopen allemaal hierlangs: pins leven op de preset (spec §9) zodat
   * tab-autosave, library-save en cross-page load ze gratis meenemen. Op een al
   * opgeslagen chart telt elke mutatie als niet-opgeslagen wijziging → sterretje
   * + Ctrl-S = update (zelfde mechanisme als herberekenen).
   */
  private updateGateBlockPins(
    mutate: (pins: readonly HdGateBlockPinV1[]) => HdGateBlockPinV1[],
  ): void {
    const preset = this.activePreset();
    if (!preset) {
      return;
    }
    const pins = mutate(preset.gateBlockLayout?.pins ?? []);
    this.activePreset.set({
      ...preset,
      gateBlockLayout: pins.length ? { schemaVersion: 1, pins } : undefined,
    });
    if (this.savedChartId() != null) {
      this.pendingSaveChanges.set(true);
    }
  }

  protected handleGateBlockPinCommitted(pin: HdGateBlockPin): void {
    this.updateGateBlockPins(pins => [
      ...pins.filter(existing => hdGateBlockKey(existing) !== hdGateBlockKey(pin)),
      { ...pin },
    ]);
  }

  protected handleGateBlockPinRemoved(key: Pick<HdGateBlockPin, 'gate' | 'planetId' | 'side'>): void {
    this.updateGateBlockPins(pins =>
      pins.filter(existing => hdGateBlockKey(existing) !== hdGateBlockKey(key)),
    );
  }

  /** Reset wist álle pins van de actieve chart — met confirm, want er is geen undo (spec §11). */
  protected async handleGateBlockPinsResetRequested(): Promise<void> {
    if ((this.activePreset()?.gateBlockLayout?.pins.length ?? 0) === 0) {
      return;
    }
    const confirmed = await this.pageConfirmer.confirm(
      this.translateMoveTool('confirmResetGateBlocks'),
      { color: 'danger' },
    );
    if (!confirmed) {
      return;
    }
    this.updateGateBlockPins(() => []);
  }

  // ── Maan-transit kaarten verplaatsen ────────────────────────────────────

  /**
   * Maan-transit-pin-mutaties: zelfde route als de blokjes-pins — pins leven op
   * de preset, en op een al opgeslagen chart telt elke mutatie als
   * niet-opgeslagen wijziging → sterretje + Ctrl-S = update.
   */
  private updateMoonTransitPins(
    mutate: (pins: readonly HdMoonTransitPinV1[]) => HdMoonTransitPinV1[],
  ): void {
    const preset = this.activePreset();
    if (!preset) {
      return;
    }
    const pins = mutate(preset.moonTransitLayout?.pins ?? []);
    this.activePreset.set({
      ...preset,
      moonTransitLayout: pins.length ? { schemaVersion: 1, pins } : undefined,
    });
    if (this.savedChartId() != null) {
      this.pendingSaveChanges.set(true);
    }
  }

  protected handleMoonTransitPinCommitted(pin: HdMoonTransitPin): void {
    this.updateMoonTransitPins(pins => [
      ...pins.filter(existing => existing.channelKey !== pin.channelKey),
      { ...pin },
    ]);
  }

  private updateJustNowTransitPins(
    mutate: (pins: readonly HdJustNowTransitPinV1[]) => HdJustNowTransitPinV1[],
  ): void {
    const preset = this.activePreset();
    if (!preset) {
      return;
    }
    const pins = mutate(preset.justNowTransitLayout?.pins ?? []);
    this.activePreset.set({
      ...preset,
      justNowTransitLayout: pins.length ? { schemaVersion: 1, pins } : undefined,
    });
    if (this.savedChartId() != null) {
      this.pendingSaveChanges.set(true);
    }
  }

  protected handleJustNowTransitPinCommitted(pin: HdJustNowTransitPinV1): void {
    this.updateJustNowTransitPins(pins => [
      ...pins.filter(existing => existing.channelKey !== pin.channelKey),
      { ...pin },
    ]);
  }

  protected handleJustNowTransitPinRemoved(key: { channelKey: string }): void {
    this.updateJustNowTransitPins(pins => pins.filter(existing => existing.channelKey !== key.channelKey));
  }

  /** Reset wist álle just-now kaart-pins van de actieve chart — met confirm, want er is geen undo. */
  protected async handleJustNowTransitPinsResetRequested(): Promise<void> {
    if ((this.activePreset()?.justNowTransitLayout?.pins.length ?? 0) === 0) {
      return;
    }
    const confirmed = await this.pageConfirmer.confirm(
      this.translateMoveTool('confirmResetJustNowTransitCards'),
      { color: 'danger' },
    );
    if (!confirmed) {
      return;
    }
    this.updateJustNowTransitPins(() => []);
  }

  private readonly justNowTransitCardLayer = viewChild(HdJustNowTransitCardLayerComponent);

  /** Contextmenu "Automatische just-now kaarten herberekenen" (pin-mutaties bevriezen de auto-posities). */
  protected handleJustNowTransitRecomputeRequested(): void {
    this.justNowTransitCardLayer()?.recomputeAutoPlacements();
  }

  protected handleMoonTransitPinRemoved(key: { channelKey: string }): void {
    this.updateMoonTransitPins(pins => pins.filter(existing => existing.channelKey !== key.channelKey));
  }

  /** Reset wist álle kaart-pins van de actieve chart — met confirm, want er is geen undo. */
  protected async handleMoonTransitPinsResetRequested(): Promise<void> {
    if ((this.activePreset()?.moonTransitLayout?.pins.length ?? 0) === 0) {
      return;
    }
    const confirmed = await this.pageConfirmer.confirm(
      this.translateMoveTool('confirmResetMoonTransitCards'),
      { color: 'danger' },
    );
    if (!confirmed) {
      return;
    }
    this.updateMoonTransitPins(() => []);
  }

  private readonly moonTransitCardLayer = viewChild(HdMoonTransitCardLayerComponent);

  /** Contextmenu "Automatische transitkaarten herberekenen": de enige weg die de auto-posities aanraakt (pin-mutaties bevriezen ze). */
  protected handleMoonTransitRecomputeRequested(): void {
    this.moonTransitCardLayer()?.recomputeAutoPlacements();
  }

  /** Vertaalt een verplaats-tool-sleutel in de actieve content-taal (zelfde idioom als de tab-/shortcut-bar). */
  private translateMoveTool(key: string): string {
    return this.pageTransloco.translate(
      `humanDesign.chart.moveTool.${key}`,
      undefined,
      this.pageContentLang.effectiveLang(),
    );
  }

  /** Rechterklik op de verplaats-knop: reset-/herbereken-acties per zichtbare versleepbare laag. */
  protected handleMoveToolContextMenu(position: { x: number; y: number }): void {
    const items: ContextMenuItem[] = [];
    if (this.gateBlockLayerVisible()) {
      items.push({
        label: this.translateMoveTool('resetGateBlocks'),
        icon: 'undo',
        action: () => void this.handleGateBlockPinsResetRequested(),
      });
    }
    if (this.moonTransitCardsVisible()) {
      items.push({
        label: this.translateMoveTool('resetMoonTransitCards'),
        icon: 'undo',
        action: () => void this.handleMoonTransitPinsResetRequested(),
      });
      items.push({
        label: this.translateMoveTool('recomputeMoonTransitCards'),
        icon: 'sync-alt',
        action: () => this.handleMoonTransitRecomputeRequested(),
      });
    }
    if (this.justNowTransitCardsVisible()) {
      items.push({
        label: this.translateMoveTool('resetJustNowTransitCards'),
        icon: 'undo',
        action: () => void this.handleJustNowTransitPinsResetRequested(),
      });
      items.push({
        label: this.translateMoveTool('recomputeJustNowTransitCards'),
        icon: 'sync-alt',
        action: () => this.handleJustNowTransitRecomputeRequested(),
      });
    }
    if (items.length === 0) {
      return;
    }
    this.pageContextMenu.open(position, items);
  }

  // ── Nieuwe chart / library-save ─────────────────────────────────────────

  /** Laadt een berekende/herladen chart als ONopgeslagen (sterretje). */
  private applyComputedChart(preset: HdBodygraphChartPreset, type: PlatformChartType): void {
    this.handlePresetApplied(preset);
    this.savedChartId.set(null);
    this.activeChartType.set(type);
    this.pendingSaveChanges.set(false);
    this.activeSharedToken.set(null);
    this.activeSharedByName.set(null);
  }

  /** Laadt een uit de library geladen chart als OPGESLAGEN (geen sterretje). */
  private applySavedChart(preset: HdBodygraphChartPreset, id: string, type: PlatformChartType): void {
    this.handlePresetApplied(preset);
    this.savedChartId.set(id);
    this.activeChartType.set(type);
    this.pendingSaveChanges.set(false);
    this.activeSharedToken.set(null);
    this.activeSharedByName.set(null);
  }

  // ── Chart delen via link (spec 2026-10-20-chart-share §4.6) ─────────────

  /**
   * Deellink geopend door een ingelogde kijker: de eigenaar krijgt zijn eigen
   * library-chart; iedereen anders een gedeelde tab (niet opgeslagen, met token
   * en delernaam) die via "Opslaan in mijn library" een gewone tab wordt.
   */
  protected async loadSharedChart(token: string): Promise<void> {
    let loaded: HdSharedChartResult;
    try {
      loaded = await this.chartShare.getShared(token);
    } catch (e) {
      const busy = e instanceof HdSharedChartLoadError && e.kind === 'tooManyRequests';
      const key = busy ? 'humanDesign.chartShare.loadBusy' : 'humanDesign.chartShare.guest.invalidLink';
      this.notifier.error(this.pageTransloco.translate(key, {}, this.pageContentLang.effectiveLang()));
      return;
    }
    if (loaded.meta.isOwnChart && loaded.meta.ownChartId) {
      await this.loadChartFromLibrary(loaded.meta.ownChartId, true);
      return;
    }
    const existing = this.tabsService.tabs().find(tab => tab.sharedToken === token);
    if (existing) {
      await this.handleTabSelected(existing.id);
      return;
    }
    if (!(await this.confirmEndGame('switch'))) return;
    this.stopGamesForTabChange();
    const tab = this.tabsService.addTab(this.extractSnapshot());
    this.applySnapshot(tab);
    this.applyComputedChart(loaded.preset, loaded.meta.type ?? PlatformChartType.Standard);
    this.activeSharedToken.set(token);
    this.activeSharedByName.set(loaded.meta.sharedByName ?? null);
  }

  /** Shortcut-bar/⋮ "Delen": deeldialoog voor de actieve (opgeslagen, eigen) chart. */
  protected async handleShareChartRequested(): Promise<void> {
    const id = this.savedChartId();
    if (!id || this.isSharedTab()) return;
    let meta = this.dataCache.chartMeta.value(id);
    if (!meta) {
      try {
        meta = (await this.chartLibrary.getChartDocument(id)).meta;
      } catch {
        this.notifier.error('De chart kon niet geladen worden.');
        return;
      }
    }
    this.shareDialogTarget.set({
      id,
      name: this.activePreset()?.name ?? meta.name ?? '',
      type: this.activeChartType(),
      shareToken: meta.shareToken ?? null,
    });
  }

  /** Deelstatus gewijzigd in de dialoog: de gecachete meta meebewegen (manager/dialoog lezen die). */
  protected handleShareChanged(change: HdChartShareChange): void {
    this.dataCache.chartMeta.update(change.id, meta => ({ ...meta, shareToken: change.shareToken, isShared: change.shareToken != null }));
  }

  /** Shortcut-bar "Nieuwe chart" → open de aanmaak-dialog. */
  protected handleNewChartRequested(): void {
    this.createDialogOpen.set(true);
  }

  /**
   * Dialog leverde een berekende chart → open in een NIEUWE tab (of in de
   * actieve tab wanneer die nog vers is, bv. net via "+") en sla hem
   * standaard meteen op in de library; met "enkel berekenen" aangevinkt blijft
   * de tab onopgeslagen (sterretje, zelf saven).
   */
  protected async handleNewChartCalculated(result: HdChartCalculationResult): Promise<void> {
    if (!(await this.confirmEndGame('switch'))) return;
    this.stopGamesForTabChange();
    this.openTabForNewChart();
    // De ingevulde naam wint van de backend-default (vaak "Chart for <stad>, <land>"),
    // zodat tab-titel én opgeslagen document de gekozen naam dragen. Het charttype
    // volgt uit het document (gedeelde afleiding met de manager).
    const type = chartTypeForPreset(result.preset);
    this.applyComputedChart({ ...result.preset, name: result.name }, type);
    if (!result.calculateOnly) {
      await this.saveActiveChart();
      if (result.markAsMyChart) {
        await this.markSavedChartAsMine();
      }
    }
  }

  /**
   * Vinkje "Markeer als mijn chart" in de aanmaak-dialog (enkel aangeboden
   * zolang er nog geen "mijn chart" is): na het opslaan de markering zetten.
   * Een mislukte markering laat de opgeslagen chart gewoon staan.
   */
  private async markSavedChartAsMine(): Promise<void> {
    const id = this.savedChartId();
    if (id == null) return;
    try {
      await this.relationsService.setMyChart(id, true);
      this.notifier.success('Gemarkeerd als mijn chart.');
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De markering "mijn chart" kon niet opgeslagen worden.');
    }
  }

  /** Opent/sluit de chart-manager-dialog (overzicht van opgeslagen charts). */
  protected readonly managerDialogOpen = signal(false);

  /** Opent/sluit de spelkeuze-dialog (spelmodus + spel-configuratie). */
  protected readonly gameDialogOpen = signal(false);

  /** Shortcut-bar "Chart manager" → open de manager-dialog. */
  protected handleChartManagerRequested(): void {
    this.managerDialogOpen.set(true);
  }

  /** Shortcut-bar "Spel" → open de spelkeuze-dialog. */
  protected handleGameRequested(): void {
    this.gameDialogOpen.set(true);
  }

  /**
   * Manager-dialog koos een chart → open als opgeslagen chart in een NIEUWE
   * tab, of in de actieve tab wanneer die nog vers is (de "+"-flow opent de
   * manager in een verse tab; die mag niet leeg achterblijven).
   */
  protected async handleManagerLoadRequested(payload: { id: string; preset: HdBodygraphChartPreset; type: PlatformChartType }): Promise<void> {
    const fresh = { preset: payload.preset, type: payload.type ?? PlatformChartType.Standard };
    if (await this.selectExistingTabForChart(payload.id, fresh)) {
      this.managerDialogOpen.set(false);
      return;
    }
    if (!(await this.confirmEndGame('switch'))) return;
    this.stopGamesForTabChange();
    this.openTabForNewChart();
    this.applySavedChart(payload.preset, payload.id, payload.type ?? PlatformChartType.Standard);
    this.managerDialogOpen.set(false);
  }

  /** Manager-dialog "Spelmodus" → manager dicht, spelkeuze-dialog open. */
  protected handleManagerGameRequested(): void {
    this.managerDialogOpen.set(false);
    this.gameDialogOpen.set(true);
  }

  /**
   * Manager-dialog "Just Now Transit" → manager dicht, het huidige moment
   * als eigen tab in personality-only weergave (het echte Just Now; via de
   * gewone save-flow als aparte chart op te slaan). Zelfde als de shortcut-knop.
   */
  protected handleManagerJustNowRequested(): void {
    this.managerDialogOpen.set(false);
    void this.handleJustNowRequested({ personalityOnly: true });
  }

  /** Manager-dialog "Just Now Volledig" → manager dicht, de volledige chart (design + personality) van nu. */
  protected handleManagerJustNowFullRequested(): void {
    this.managerDialogOpen.set(false);
    void this.handleJustNowRequested();
  }

  /**
   * Manager-dialog "Toon lege chart" → manager dicht, verse lege tab (zelfde
   * als de +-tab, maar zónder de manager opnieuw te openen).
   */
  protected async handleManagerEmptyChartRequested(): Promise<void> {
    this.managerDialogOpen.set(false);
    if (!(await this.confirmEndGame('switch'))) {
      return;
    }
    this.stopGamesForTabChange();
    const tab = this.tabsService.addTab(this.extractSnapshot());
    this.applySnapshot(tab);
  }

  /**
   * Bestemmingstab voor een nieuw geladen/berekende chart: een verse actieve
   * tab (geen chart, geen spel, geen activaties) wordt hergebruikt; anders
   * komt er een nieuwe tab bij.
   */
  private openTabForNewChart(): void {
    if (this.isActiveTabFresh()) {
      return;
    }
    const tab = this.tabsService.addTab(this.extractSnapshot());
    this.applySnapshot(tab);
  }

  /** Ctrl-S / shortcut-bar "Opslaan": maak een nieuwe library-chart aan. */
  @HostListener('document:keydown.control.s', ['$event'])
  @HostListener('document:keydown.meta.s', ['$event'])
  protected onSaveShortcut(event: Event): void {
    event.preventDefault();
    void this.saveActiveChart();
  }

  protected async saveActiveChart(): Promise<void> {
    const preset = this.activePreset();
    if (!preset) {
      return;
    }
    // Gedeelde cycle-/composite-/momentchart: niet op te slaan (bronchart ontbreekt), zie sharedTabSaveable.
    if (this.isSharedTab() && !this.sharedTabSaveable()) {
      return;
    }
    const savedId = this.savedChartId();
    try {
      if (savedId == null) {
        // Nog niet opgeslagen → nieuwe library-chart aanmaken (gedeelde kern:
        // naam in document, bronrelaties; zie HdChartLibraryService).
        // Gedeelde tab (spec 2026-10-20-chart-share §4.6): kopie als eigen chart,
        // herkomst in source.provider, geen bronrelaties (id's van een andere eigenaar).
        const shared = this.isSharedTab();
        const toSave: HdBodygraphChartPreset = shared
          ? { ...preset, source: { ...preset.source, provider: 'egitura-share' } }
          : preset;
        const { chart: created, relationsWarning } = await this.chartLibrary.createCalculatedChart({
          preset: toSave,
          name: preset.name,
          type: this.activeChartType(),
          skipSourceRelations: shared,
        });
        if (created.id) {
          if (shared) {
            this.activePreset.set(toSave);
          }
          this.savedChartId.set(created.id);
          this.pendingSaveChanges.set(false);
          this.activeSharedToken.set(null);
          this.activeSharedByName.set(null);
          this.notifier.success('Chart opgeslagen in je library.');
        }
        if (relationsWarning) {
          this.notifier.error(relationsWarning);
        }
      } else if (this.pendingSaveChanges()) {
        // Reeds opgeslagen + wijzigingen (bv. na recalc) → document bijwerken, maar nooit stil
        // over een bibliotheekversie met een ander geboortemoment heen.
        if (!(await this.confirmOverwriteOfLibraryVersion(savedId, preset))) {
          return;
        }
        await this.chartLibrary.replaceDocument(savedId, preset);
        this.pendingSaveChanges.set(false);
        this.notifier.success('Chart bijgewerkt.');
        const relationsWarning = await this.chartLibrary.ensureSourceRelations(savedId, preset);
        if (relationsWarning) {
          this.notifier.error(relationsWarning);
        }
      }
      // else: opgeslagen en geen wijzigingen → niets te doen.
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De chart kon niet opgeslagen worden.');
    }
  }

  /**
   * Tab-contextmenu "Moment bewaren": enkel beschikbaar op de actieve tab met
   * een live just-now overlay op een opgeslagen chart (spec notable moments
   * §4.2) — de overlay is page-level, dus alleen de actieve tab toont hem.
   */
  protected readonly momentSaveTabId = computed<string | null>(() =>
    this.justNowTransitMode()
      && this.justNowTransitSnapshot() != null
      && this.savedChartId() != null
      && this.activeTransitInfo() == null
      ? this.tabsService.activeTabId()
      : null,
  );

  /** Doel van de moment-bewaar-dialog; null = gesloten. */
  protected readonly momentSaveDialogTarget = signal<HdMomentSaveTarget | null>(null);

  /**
   * Wat "Moment bewaren" bewaart: de tijdslider-stand als die verschoven is
   * (wens Steven, stap 4), anders het live just-now-snapshot.
   */
  private readonly momentSaveSnapshot = computed(() => this.transitSampleSnapshot() ?? this.justNowTransitSnapshot());

  /** Printdialoog voor de PDF (tab-contextmenu); null = gesloten. */
  protected readonly printDialogTarget = signal<HdChartPrintDialogTarget | null>(null);

  /**
   * Tab-contextmenu "PDF downloaden…": de printdialoog voor de berekende chart van die tab, zonder
   * van tab te wisselen (achtergrondtabs dragen hun preset in de snapshot).
   */
  protected handleTabPdfDownloadRequested(tabId: string): void {
    const preset = tabId === this.tabsService.activeTabId()
      ? this.activePreset()
      : this.tabsService.tabs().find(tab => tab.id === tabId)?.preset;
    if (!isHdChartPrintable(preset)) {
      return;
    }
    this.printDialogTarget.set({ preset });
  }

  protected handlePrintConfirmed(event: { preset: HdBodygraphChartPreset; options: HdChartPrintOptions }): void {
    void this.chartPdfExport.downloadChart(event.preset, event.options);
  }

  protected handleTabMomentSaveRequested(tabId: string): void {
    if (tabId !== this.momentSaveTabId()) {
      return;
    }
    const snapshot = this.momentSaveSnapshot();
    const chartName = this.activePreset()?.name;
    if (snapshot == null || !chartName) {
      return;
    }
    this.momentSaveDialogTarget.set({
      defaultName: buildMomentChartName(chartName, snapshot.fetchedAt.toISOString()),
      momentText: new Intl.DateTimeFormat('nl-BE', { dateStyle: 'long', timeStyle: 'short' })
        .format(snapshot.fetchedAt),
    });
  }

  /**
   * Bevestiging uit de moment-bewaar-dialog: bouw het snapshot-document en sla
   * het op via de gedeelde kern (type Transit + momentOf-bronrelatie). Bij een
   * fout (bv. naamclash — chartnamen zijn uniek per eigenaar) blijft de dialog
   * open met de foutmelding zodat de naam aangepast kan worden.
   */
  protected async handleMomentSaveConfirmed(name: string): Promise<void> {
    const snapshot = this.momentSaveSnapshot();
    const natalChartId = this.savedChartId();
    const target = this.momentSaveDialogTarget();
    if (snapshot == null || this.activePreset() == null || natalChartId == null || target == null) {
      return;
    }
    try {
      const preset = buildTransitSnapshotPreset({
        natalPreset: await this.libraryNatalPreset(natalChartId),
        natalChartId,
        momentUtc: snapshot.fetchedAt.toISOString(),
        personalityActivations: snapshot.personalityActivations,
        momentMetadata: snapshot.momentMetadata,
        name,
        tabTooltip: target.momentText,
      });
      const { chart, relationsWarning } = await this.chartLibrary.createCalculatedChart({
        preset,
        name,
        type: PlatformChartType.Transit,
      });
      this.momentSaveDialogTarget.set(null);
      if (relationsWarning) {
        this.notifier.error(relationsWarning);
      }
      if (!chart.id) {
        return;
      }
      this.notifier.success('Moment bewaard als aparte chart.');
      // Snapshot in een nieuwe tab openen (beslispunt 4); de natal-tab met de
      // live overlay blijft gewoon staan.
      if (!(await this.confirmEndGame('switch'))) {
        return;
      }
      this.stopGamesForTabChange();
      this.openTabForNewChart();
      this.applySavedChart(preset, chart.id, PlatformChartType.Transit);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Het moment kon niet bewaard worden.';
      this.momentSaveDialogTarget.set({ ...target, defaultName: name, errorText: message });
    }
  }

  /**
   * Tab-context-menu "Chart opslaan": zelfde flow als Ctrl-S/de save-knop, maar
   * voor de aangeklikte tab — een niet-actieve tab wordt eerst actief gemaakt
   * (de save-flow werkt op de actieve tab-signals).
   */
  protected async handleTabSaveRequested(tabId: string): Promise<void> {
    if (tabId !== this.tabsService.activeTabId()) {
      await this.handleTabSelected(tabId);
      if (this.tabsService.activeTabId() !== tabId) {
        return; // tabwissel afgebroken (bv. lopend spel)
      }
    }
    await this.saveActiveChart();
  }

  /**
   * Tab-context-menu "Chart herladen": zet de tab terug naar de laatst geladen
   * chart (de actieve preset) zónder backend-herberekening — voor het
   * terugdraaien van edits. `activePreset` blijft bij edits onaangeroerd
   * (immutable updates op `configuration`), dus dit herstelt exact de
   * originele staat; de modified-indicator dooft mee. Is de tab daarna clean
   * en heeft de bibliotheek intussen een andere versie, dan volgt de tab die.
   */
  protected async handleReloadRequested(tabId: string): Promise<void> {
    if (tabId !== this.tabsService.activeTabId()) {
      await this.handleTabSelected(tabId);
      if (this.tabsService.activeTabId() !== tabId) {
        return; // tabwissel afgebroken (bv. lopend spel)
      }
    }
    const preset = this.activePreset();
    if (!preset) {
      this.notifier.info('Deze tab heeft geen chart om te herladen.');
      return;
    }
    this.handlePresetApplied(preset);
    this.lastReconciled = null;
    this.reconcileActiveTabWithCachedLibrary();
    this.notifier.success('Chart herladen.');
  }

  /**
   * Tab-context-menu "Chart herberekenen": haalt verse backend-output op met de
   * bewaarde geboortegegevens en vervangt de chart in de (aangeklikte) tab. Op een
   * opgeslagen chart markeert dit niet-opgeslagen wijzigingen → sterretje + Ctrl-S = update.
   */
  protected async handleRecalculateRequested(tabId: string): Promise<void> {
    if (tabId !== this.tabsService.activeTabId()) {
      await this.handleTabSelected(tabId);
      if (this.tabsService.activeTabId() !== tabId) {
        return; // tabwissel afgebroken (bv. lopend spel)
      }
    }
    const preset = this.activePreset();
    if (!preset) {
      this.notifier.info('Deze tab heeft geen chart om te herberekenen.');
      return;
    }
    // Afgeleide charts (cycle/composite/snapshot): een natal-herberekening zou
    // het samengestelde document vervangen — zelfde bescherming als de
    // rename-only-vergrendeling in de edit-dialog.
    if (preset.cycle != null || preset.composite != null || preset.transit != null) {
      this.notifier.info(this.pageTransloco.translate(
        'humanDesign.chart.controls.recalculateDerivedBlocked',
        undefined,
        this.pageContentLang.effectiveLang(),
      ));
      return;
    }
    this.loadingPreset.set(true);
    const tabTooltip = preset.tabTooltip;
    try {
      const fresh = await this.chartLibrary.recalculate(preset);
      // Pins (blokjes én transitkaarten) zijn handwerk van de gebruiker: mee
      // overnemen; verdwenen activatie-/poort-keys negeert de layout vanzelf (spec §9).
      this.handlePresetApplied({
        ...fresh,
        name: preset.name,
        tabTooltip,
        gateBlockLayout: preset.gateBlockLayout,
        moonTransitLayout: preset.moonTransitLayout,
        justNowTransitLayout: preset.justNowTransitLayout,
      });
      if (this.savedChartId() != null) {
        this.pendingSaveChanges.set(true);
      }
      this.notifier.success('Chart herberekend.');
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'Herberekenen mislukt.');
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /**
   * Selecteert de bestaande tab van een al-geopende library-chart (dedupe bij
   * "Openen" vanuit relaties, cycles of de manager). True = afgehandeld.
   * Met `fresh` erbij wordt de geselecteerde tab meteen ververst met het verse
   * library-document (mits zonder onopgeslagen werk) — het tab-snapshot kan
   * verouderd zijn wanneer de chart elders bewerkt is.
   */
  private async selectExistingTabForChart(
    chartId: string,
    fresh?: { preset: HdBodygraphChartPreset; type: PlatformChartType },
  ): Promise<boolean> {
    // De actieve tab via het live signaal checken: het tab-snapshot synct gedebounced.
    if (this.savedChartId() === chartId) {
      if (fresh) {
        this.refreshActiveTabIfClean(chartId, fresh);
      }
      return true;
    }
    const existing = this.tabsService.tabs().find(
      tab => tab.savedChartId === chartId && tab.id !== this.tabsService.activeTabId(),
    );
    if (!existing) {
      return false;
    }
    if (this.isActiveTabFresh()) {
      // Verse tab (bv. net via "+") + chart die al open staat → naar die tab
      // springen en de overbodige verse tab opruimen.
      this.switchToExistingChartTab(existing.id);
    } else {
      await this.handleTabSelected(existing.id);
    }
    if (fresh) {
      this.refreshActiveTabIfClean(chartId, fresh);
    }
    return true;
  }

  /**
   * Ververst de (nu) actieve tab met het verse library-document wanneer die
   * dezelfde chart toont en geen onopgeslagen werk of lopend spel draagt.
   * De tabwissel kan afgebroken zijn (bv. spel-confirm) — daarom de id-check.
   */
  private refreshActiveTabIfClean(chartId: string, fresh: { preset: HdBodygraphChartPreset; type: PlatformChartType }): void {
    if (this.savedChartId() !== chartId) {
      return;
    }
    if (this.presetModified() || this.pendingSaveChanges() || this.gameMode()) {
      return;
    }
    this.applySavedChart(fresh.preset, chartId, fresh.type);
  }

  /**
   * Manager bewerkte een chart (naam en/of herberekend document): alle open
   * tabs van die chart verversen — de actieve tab live, achtergrond-tabs via
   * hun snapshot — zodat nergens (ook niet in localStorage) het oude document
   * blijft staan. De zonet bewaarde library-versie is de waarheid; eventuele
   * onopgeslagen tab-restjes waren op het oude document gebaseerd en vervallen.
   */
  protected async handleChartUpdated(chartId: string): Promise<void> {
    // De actieve tab via het live signaal checken: het tab-snapshot synct gedebounced.
    const activeShowsChart = this.savedChartId() === chartId;
    const backgroundTabs = this.tabsService.tabs().filter(
      tab => tab.savedChartId === chartId && tab.id !== this.tabsService.activeTabId(),
    );
    if (!activeShowsChart && backgroundTabs.length === 0) {
      return;
    }
    try {
      const { meta, preset } = await this.chartLibrary.getChartDocument(chartId);
      const type = meta.type ?? PlatformChartType.Standard;
      if (activeShowsChart) {
        this.applySavedChart(preset, chartId, type);
      }
      for (const tab of backgroundTabs) {
        this.tabsService.patchTab(tab.id, {
          preset,
          presetName: preset.name ?? null,
          presetModified: false,
          pendingSaveChanges: false,
          chartType: type,
          configuration: preset.configuration,
        });
      }
    } catch {
      this.notifier.error('De chart is bijgewerkt, maar de open tab kon niet ververst worden.');
    }
  }

  private handleRemoteChartChange(change: RemoteChartChange): void {
    if (change.kinds.includes('deleted')) {
      this.releaseTabsForRemoteDeletion(change.chartIds);
      return;
    }
    if (change.kinds.includes('document')) {
      for (const chartId of change.chartIds) {
        void this.refreshCleanTabsForRemoteChange(chartId);
      }
    }
  }

  /**
   * Een chart werd elders gewijzigd (hernoemd, herberekend): open tabs met die chart verversen.
   *
   * BELANGRIJK: enkel clean tabs (geen presetModified, pendingSaveChanges of spelmodus). Anders zou
   * een wijziging op een ander toestel hier onopgeslagen werk overschrijven; dat werk wint, en een
   * latere save van deze tab is gewoon last-write-wins. Tests: "seintjes van elders" in de spec.
   */
  private async refreshCleanTabsForRemoteChange(chartId: string): Promise<void> {
    const activeTabId = this.tabsService.activeTabId();
    const activeIsClean = (): boolean =>
      this.savedChartId() === chartId && !this.presetModified() && !this.pendingSaveChanges() && !this.gameMode();
    const isCleanBackgroundTab = (tab: HdChartTabSnapshotV1): boolean =>
      tab.savedChartId === chartId && tab.id !== this.tabsService.activeTabId()
      && !tab.presetModified && !(tab.pendingSaveChanges ?? false) && !tab.game.gameMode;
    const refreshActive = activeIsClean();
    const backgroundTabIds = this.tabsService.tabs().filter(isCleanBackgroundTab).map(tab => tab.id);
    if (!refreshActive && backgroundTabIds.length === 0) {
      return;
    }
    let loaded: HdChartDocumentResult;
    try {
      loaded = await this.chartLibrary.getChartDocument(chartId);
    } catch {
      // Stil: een verversing van elders mag hier geen foutmelding opleveren; de tab blijft zoals hij was.
      return;
    }
    const { meta, preset } = loaded;
    const type = meta.type ?? PlatformChartType.Standard;
    // Async-race-guard: intussen gewisseld of beginnen bewerken → niet meer aanraken.
    if (refreshActive && this.tabsService.activeTabId() === activeTabId && activeIsClean()) {
      // Geen docktab-sprong (cycle → Geavanceerd) bij een verversing van elders.
      this.lastAutoAdvancedPreset = preset;
      this.applySavedChart(preset, chartId, type);
    }
    for (const tabId of backgroundTabIds) {
      const tab = this.tabsService.tabs().find(candidate => candidate.id === tabId);
      if (!tab || !isCleanBackgroundTab(tab)) continue;
      this.tabsService.patchTab(tabId, {
        preset,
        presetName: preset.name ?? null,
        presetModified: false,
        pendingSaveChanges: false,
        chartType: type,
        configuration: preset.configuration,
      });
    }
  }

  /**
   * Elders verwijderd: de tabs blijven open maar verliezen hun bibliotheeklink (sterretje). Een tab
   * sluiten terwijl je ernaar kijkt is te verrassend, en zo kan je de chart desgewenst opnieuw opslaan.
   */
  private releaseTabsForRemoteDeletion(chartIds: readonly string[]): void {
    let released = false;
    const activeSaved = this.savedChartId();
    if (activeSaved != null && chartIds.includes(activeSaved)) {
      this.savedChartId.set(null);
      this.pendingSaveChanges.set(false);
      released = true;
    }
    for (const tab of this.tabsService.tabs()) {
      if (tab.id === this.tabsService.activeTabId() || tab.savedChartId == null || !chartIds.includes(tab.savedChartId)) {
        continue;
      }
      this.tabsService.patchTab(tab.id, { savedChartId: null, pendingSaveChanges: false });
      released = true;
    }
    if (released) {
      this.notifier.info(this.pageTranslate('humanDesign.chart.remoteDeleted'));
    }
  }

  /** Doel van de hernoem-dialog (tab-contextmenu); null = gesloten. */
  protected readonly renameDialogTarget = signal<HdChartRenameTarget | null>(null);

  /** Tab-contextmenu "Chart hernoemen": eerst naar die tab wisselen, dan de dialog openen. */
  protected async handleTabRenameRequested(tabId: string): Promise<void> {
    if (tabId !== this.tabsService.activeTabId()) {
      await this.handleTabSelected(tabId);
      if (this.tabsService.activeTabId() !== tabId) {
        return; // tabwissel afgebroken (bv. lopend spel)
      }
    }
    const preset = this.activePreset();
    if (!preset) {
      return;
    }
    this.renameDialogTarget.set({ currentName: preset.name });
  }

  /**
   * Nieuwe naam bevestigd: preset (en bij een opgeslagen chart ook de library) bijwerken.
   * Hernoemen raakt in de library enkel de naam: het document blijft de bibliotheekversie, zodat een
   * verouderde of onopgeslagen tabkopie nooit meelift (incident 2026-10-22).
   */
  protected async handleChartRenamed(newName: string): Promise<void> {
    const preset = this.activePreset();
    if (preset == null || !newName || newName === preset.name) {
      return;
    }
    const renamed = { ...preset, name: newName };
    const savedId = this.savedChartId();
    try {
      if (savedId != null) {
        const { meta, preset: library } = await this.chartLibrary.getChartDocument(savedId);
        await this.chartLibrary.updateChart({ id: savedId, name: newName, preset: { ...library, name: newName }, current: meta });
      }
      this.activePreset.set(renamed);
      this.notifier.success('Chartnaam bijgewerkt.');
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De chartnaam kon niet bijgewerkt worden.');
    }
  }

  /** Manager verwijderde charts: sluit eventuele open tabs van die charts. */
  protected handleChartsDeleted(chartIds: string[]): void {
    const doomedIds = new Set(
      this.tabsService.tabs()
        .filter(tab => tab.savedChartId != null && chartIds.includes(tab.savedChartId))
        .map(tab => tab.id),
    );
    // De actieve tab via het live signaal checken: het tab-snapshot synct gedebounced.
    const activeSaved = this.savedChartId();
    if (activeSaved != null && chartIds.includes(activeSaved)) {
      doomedIds.add(this.tabsService.activeTabId());
    }
    if (doomedIds.size === 0) {
      return;
    }
    let next: HdChartTabSnapshotV1 | null = null;
    for (const tabId of doomedIds) {
      if (tabId === this.tabsService.activeTabId()) {
        this.stopGamesForTabChange();
      }
      next = this.tabsService.closeTab(tabId) ?? next;
    }
    if (next) {
      this.applySnapshot(next);
    }
  }

  /** Laadt een opgeslagen chart in de actieve of een nieuwe tab. */
  protected async loadChartFromLibrary(id: string, inNewTab: boolean): Promise<void> {
    let loaded: HdChartDocumentResult;
    try {
      loaded = await this.chartLibrary.getChartDocument(id);
    } catch {
      // Vers ophalen mislukt: een al-open tab selecteren is beter dan enkel een fout.
      if (await this.selectExistingTabForChart(id)) {
        return;
      }
      this.notifier.error('De chart kon niet geladen worden.');
      return;
    }
    const type = loaded.meta.type ?? PlatformChartType.Standard;
    // Staat de chart al open in een tab, selecteer (en ververs) die dan i.p.v. dubbel te openen.
    if (await this.selectExistingTabForChart(id, { preset: loaded.preset, type })) {
      return;
    }
    if (inNewTab) {
      this.stopGamesForTabChange();
      const tab = this.tabsService.addTab(this.extractSnapshot());
      this.applySnapshot(tab);
    }
    // Het opgeslagen type gebruiken — het tab-type is na applySnapshot altijd Standard.
    this.applySavedChart(loaded.preset, id, type);
  }

  // ── Cycle charts: berekenen / relatie / herberekenen (spec cycle charts §5, §8, §6.1) ──

  /**
   * Cycles-sectie → "Just Now Transit": het huidige moment als momentchart
   * over deze chart (enkel de personality-kant van nu, type Transit), net als
   * "Moment bewaren" na de Just Now-transit-knop, maar onopgeslagen in een
   * nieuwe tab. Opslaan (Ctrl-S) legt de momentOf-relatie naar deze chart.
   */
  protected async handleCycleJustNowTransitRequested(): Promise<void> {
    const natalChartId = this.savedChartId();
    if (this.activePreset() == null || natalChartId == null) return;

    this.loadingPreset.set(true);
    try {
      const document = (await firstValueFrom(this.humanDesignApi.getHumandesignJustNow())) as unknown as HdBodygraphChartPreset | null;
      const personalityActivations = filterHdJustNowPersonalityActivations(document?.planetaryActivations ?? []);
      if (personalityActivations.length === 0) {
        this.notifier.error(this.translateJustNowTransit('noPersonalityActivations'));
        return;
      }
      const natalPreset = await this.libraryNatalPreset(natalChartId);
      if (!(await this.confirmEndGame('switch'))) return;

      const momentAt = new Date();
      const momentUtc = momentAt.toISOString();
      const preset = buildTransitSnapshotPreset({
        natalPreset,
        natalChartId,
        momentUtc,
        personalityActivations,
        momentMetadata: document?.metadata ?? null,
        name: buildMomentChartName(natalPreset.name, momentUtc),
        tabTooltip: new Intl.DateTimeFormat('nl-BE', { dateStyle: 'long', timeStyle: 'short' }).format(momentAt),
      });
      this.stopGamesForTabChange();
      const tab = this.tabsService.addTab(this.extractSnapshot());
      this.applySnapshot(tab);
      this.applyComputedChart(preset, PlatformChartType.Transit);
      this.settingsPanelOpen.set(false);
    } catch {
      this.notifier.error(this.translateJustNowTransit('loadError'));
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /** Cycles-sectie → berekenen: moment-chart ophalen, cycle-document bouwen, nieuwe tab. */
  protected async handleCycleCalculateRequested(request: HdCycleCalculateRequest): Promise<void> {
    const tabPreset = this.activePreset();
    const natalChartId = this.savedChartId();
    if (tabPreset == null || natalChartId == null) return;

    this.loadingPreset.set(true);
    try {
      let natalPreset = await this.libraryNatalPreset(natalChartId);
      let core = extractCycleSourceCore(natalPreset.birthData);
      if (core.latitude == null || core.longitude == null) {
        // Maia-imports dragen alleen stad/land: geocodeer de bekende plaats en
        // herstel het document blijvend, dan gewoon doorrekenen.
        const repaired = await this.tryRepairBirthCoordinates(tabPreset, natalChartId);
        if (repaired == null) {
          this.notifier.error('Deze chart heeft geen herbruikbare geboortelocatie voor een cycle chart.');
          return;
        }
        natalPreset = repaired;
        core = extractCycleSourceCore(natalPreset.birthData);
      }
      const anchorUtc = request.anchorUtc ?? moment.utc().toISOString();
      const location = (natalPreset.birthData as { location?: { city?: string | null; country?: string | null } }).location ?? {};
      const momentPreset = await this.chartLibrary.calculateFromUtc({
        name: natalPreset.name,
        utc: anchorUtc,
        timezone: core.timezone || 'UTC',
        location: { city: location.city ?? null, country: location.country ?? null, lat: core.latitude, lon: core.longitude },
      });

      if (!(await this.confirmEndGame('switch'))) return;
      this.stopGamesForTabChange();
      const tab = this.tabsService.addTab(this.extractSnapshot());
      this.applySnapshot(tab);

      const translate = (key: string) =>
        this.pageTransloco.translate<string>(key, undefined, this.pageContentLang.effectiveLang());
      const name = buildCycleChartName(translate, request.key, natalPreset.name, anchorUtc, request.solarReturnYear);
      const preset = buildCycleChartPreset({
        natalPreset,
        natalChartId,
        momentPreset,
        cycleKey: request.key,
        anchorUtc,
        passages: request.passages,
        solarReturnYear: request.solarReturnYear,
        jupiterReturnNumber: request.jupiterReturnNumber,
        name,
        tabTooltip: moment.utc(anchorUtc).local().format('D MMMM YYYY, HH:mm'),
      });
      this.applyComputedChart(preset, PlatformChartType.Cycle);
      this.settingsPanelOpen.set(false);
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De cycle chart kon niet berekend worden.');
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /**
   * Geocode-reparatie voor imports zonder coördinaten (Maia Mechanics): de
   * bekende stad/land opzoeken, `location.lat`/`lon` in het document aanvullen,
   * blijvend opslaan (replaceDocument, promoveert ook de kolommen) en de
   * actieve tab verversen. Null wanneer stad ontbreekt of geocoding niets vindt.
   * De reparatie vertrekt van de bibliotheekversie (enkel het handwerk komt uit
   * de tab): een verouderde tabkopie mag het document nooit terugzetten.
   */
  private async tryRepairBirthCoordinates(
    tabPreset: HdBodygraphChartPreset,
    chartId: string,
  ): Promise<HdBodygraphChartPreset | null> {
    try {
      const { preset: library } = await this.chartLibrary.getChartDocument(chartId);
      const preset = carryUserHandwork(library, tabPreset);
      const location = (preset.birthData as { location?: { city?: string | null; country?: string | null } }).location ?? {};
      // Maia-plaatsnamen dragen soms een regiosuffix ("Wilrijk (Flanders)") dat de geocoder niet kent.
      const city = (location.city ?? '').replace(/\s*\(.*\)\s*$/, '').trim();
      const country = location.country ?? null;
      if (!city) return null;
      const cities = await firstValueFrom(this.geocodingService.getGeocodingCities({ country, query: city }));
      const match = cities?.find(candidate => candidate.name?.toLowerCase() === city.toLowerCase()) ?? cities?.[0] ?? null;
      if (match?.latitude == null || match?.longitude == null) return null;
      const repaired: HdBodygraphChartPreset = {
        ...preset,
        birthData: withHdBirthCoordinates(preset.birthData, { latitude: match.latitude, longitude: match.longitude }),
      };
      await this.chartLibrary.replaceDocument(chartId, repaired);
      this.applySavedChart(repaired, chartId, this.activeChartType());
      this.notifier.success(`Geboortelocatie aangevuld via geocoding (${match.name}) en opgeslagen.`);
      return repaired;
    } catch {
      return null;
    }
  }

  /**
   * Info-icoon bij het composiet-thema: opent het thema-paneel met de boektekst
   * van het benoemde thema — zelfde open-gedrag als de gate-/kanaal-info-panelen.
   */
  protected handleCompositeThemeInfoRequested(
    event: { theme: string; themeKey: string; origin: { x: number; y: number } },
  ): void {
    this.floatingPanels.openOrFocus<HdCompositeThemeInfoComponent, HdCompositeThemeInfoData>({
      key: 'composite-theme-info',
      component: HdCompositeThemeInfoComponent,
      data: { theme: event.theme, themeKey: event.themeKey },
      dockTab: { label: event.theme.split(':')[0]?.trim() || event.theme },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      // Boektekst-paneel: gecentreerd openen — rechts van de klik viel de
      // brede tekst half buiten beeld.
      startPosition: 'center',
    });
  }

  /**
   * Info-icoon/link bij een relatieboek-onderwerp (composite-eigenschappen,
   * kanaalthema-blokken, niet-zelf centers): opent het markdown-paneel van dat
   * onderwerp. Eén paneel per topic (dedup-key), zodat onderwerpen naast
   * elkaar open kunnen.
   */
  protected handleRelationInfoRequested(
    event: { topic: HdRelationInfoTopic; origin: { x: number; y: number } },
  ): void {
    this.floatingPanels.openOrFocus<HdRelationInfoPanelComponent, HdRelationInfoPanelData>({
      key: `composite-relation-info:${event.topic}`,
      component: HdRelationInfoPanelComponent,
      data: { topic: event.topic },
      dockTab: {
        label: this.pageTransloco.translate<string>(
          `humanDesign.advancedPanel.composite.relationInfoTabs.${event.topic}`,
        ),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      // Boektekst-paneel: gecentreerd openen — rechts van de klik viel de
      // brede tekst half buiten beeld.
      startPosition: 'center',
    });
  }

  /**
   * Info-icoon op de levenskruis-rij (Basis-paneel): opent het paneel met de
   * 32 nodale omgevingen. Het levenskruis van de actieve chart gaat mee zodat
   * het paneel de eigen omgeving-secties en omgevingslijnen kan markeren.
   */
  protected handleLifeCrossInfoRequested(): void {
    this.openNodalEnvironmentsPanel({ lifeCross: deriveLifeCross(this.basicPlanetaryActivations()) });
  }

  /**
   * Info-icoon op de levenskruis-rij van het cycle-moment (Cycles-sectie in
   * het Geavanceerd-paneel): hetzelfde paneel, maar met de nodes van de cyclus
   * en de cyclusnaam als context. Zelfde paneel-key als het natal-levenskruis,
   * dus een open paneel wisselt gewoon van data.
   */
  protected handleCycleLifeCrossInfoRequested(event: HdCycleLifeCrossInfoRequest): void {
    this.openNodalEnvironmentsPanel({ lifeCross: event.lifeCross, cycleLabel: event.cycleLabel });
  }

  private openNodalEnvironmentsPanel(data: HdNodalEnvironmentsInfoPanelData): void {
    this.floatingPanels.openOrFocus<HdNodalEnvironmentsInfoPanelComponent, HdNodalEnvironmentsInfoPanelData>({
      key: 'nodal-environments-info',
      component: HdNodalEnvironmentsInfoPanelComponent,
      data,
      dockTab: {
        label: this.pageGateInfoService.nodalEnvironmentsTextIn('tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      // Boektekst-paneel: gecentreerd openen — rechts van de klik viel de
      // brede tekst half buiten beeld.
      startPosition: 'center',
    });
  }

  /**
   * Info-icoon op de profiel-rij (Basis-paneel): opent het paneel met de
   * profielen en genetische rollen. Het profiel van de actieve chart gaat mee
   * zodat het paneel het eigen profiel en de eigen rollen-lijnen kan markeren.
   */
  protected handleProfileInfoRequested(): void {
    this.floatingPanels.openOrFocus<HdProfileInfoPanelComponent, HdProfileInfoPanelData>({
      key: 'profile-info',
      component: HdProfileInfoPanelComponent,
      data: { profile: deriveProfile(this.basicPlanetaryActivations()) },
      dockTab: {
        label: this.pageGateInfoService.profilesInfoTextIn('tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      // Boektekst-paneel: gecentreerd openen — rechts van de klik viel de
      // brede tekst half buiten beeld.
      startPosition: 'center',
    });
  }

  /**
   * Info-icoon op de kruis-rij (Basis-paneel): opent het paneel van het
   * incarnatiekruis van de actieve chart (één paneel per kruis, tab "IX …"),
   * met het profiel van de chart als open profielsectie. Zonder gekend kruis
   * opent het algemene IX-paneel met de lijst van alle kruisen.
   */
  protected handleIncarnationCrossInfoRequested(): void {
    const activations = this.basicPlanetaryActivations();
    const cross = deriveIncarnationCross(activations);
    const placement = { dockZone: this.gateInfoDockZone(), docked: this.gateInfoOpenInitiallyDocked() };
    if (!cross?.name) {
      this.incarnationCrossPanels.openGeneral(placement);
      return;
    }
    this.incarnationCrossPanels.openCross({ cross, profile: deriveProfile(activations) }, placement);
  }

  protected override isChartFollowingPanelKey(key: string): boolean {
    return super.isChartFollowingPanelKey(key) || BASICS_FOLLOWING_PANEL_KEYS.has(key);
  }

  protected override refreshChartFollowingPanels(): void {
    super.refreshChartFollowingPanels();
    this.refreshOpenBasicsInfoPanels({ force: true });
  }

  /** Chartwaarden waarmee de Basis-info-panelen laatst ververst werden. */
  private lastBasicsPanelsSource: string | null = null;

  /**
   * BELANGRIJK: de info-panelen van het Basis-paneel (type, strategie,
   * signatuur, autoriteit, definitie, profiel, levenskruis en de "Toon
   * alle"-overzichten) volgen de actieve chart, zoals de poort- en
   * PHS-panelen. Ze kregen hun data vroeger enkel bij het openen, waardoor bv.
   * het typepaneel na een tabwissel nog het type van de vorige chart toonde
   * (verwarrend volgens gebruikers, beslissing Steven 2026-10-30: volgen, met
   * een slot om een paneel op zijn chart te houden). Vergrendelde panelen
   * blijven staan; zonder geladen chart verandert er niets. Data wordt enkel
   * vervangen als de chartwaarde echt anders is, zodat een gelijk type of
   * profiel de leesplek niet raakt. Specs: "Basis-info-panelen volgen de
   * actieve chart" in chart-environment-full-page.component.spec.ts.
   *
   * Enkel wanneer de chart of zijn waarden wijzigen (tabwissel, bewerking),
   * niet bij elke herberekening: een paneel dat bewust met cycle-context
   * geopend werd (levenskruis of definitie van het cyclusmoment, Geavanceerd-
   * paneel) blijft zo staan tot de volgende echte wissel. `force` = na
   * ontgrendelen, dan altijd syncen.
   */
  private refreshOpenBasicsInfoPanels(options: { force?: boolean } = {}): void {
    const preset = this.activePreset();
    if (preset == null) return;
    const basics = this.basicChartBasics();
    const activations = this.basicPlanetaryActivations();
    const profile = deriveProfile(activations);
    const lifeCross = deriveLifeCross(activations);
    const source = JSON.stringify([preset.id, basics.type, basics.authorityKey, basics.definitionKey, profile, lifeCross]);
    const changed = source !== this.lastBasicsPanelsSource;
    this.lastBasicsPanelsSource = source;
    if (!changed && !options.force) return;
    for (const panel of this.floatingPanels.panels()) {
      if (!this.isFollowingPanel(panel)) continue;
      const family = panel.key.replace(/~\d+$/, '').replace(/-all$/, '');
      switch (family) {
        case 'type-info':
        case 'strategy-info':
        case 'signature-info': {
          const typed = panel as FloatingPanelRef<HdTypeInfoPanelComponent, HdTypeInfoPanelData>;
          const data = typed.data();
          if (JSON.stringify(data.type) !== JSON.stringify(basics.type) || data.authorityKey !== basics.authorityKey) {
            typed.replaceData({ ...data, type: basics.type, authorityKey: basics.authorityKey });
          }
          break;
        }
        case 'authority-info': {
          const typed = panel as FloatingPanelRef<HdAuthorityInfoPanelComponent, HdAuthorityInfoPanelData>;
          if (typed.data().authorityKey !== basics.authorityKey) {
            typed.replaceData({ ...typed.data(), authorityKey: basics.authorityKey });
          }
          break;
        }
        case 'definition-info': {
          const typed = panel as FloatingPanelRef<HdDefinitionInfoPanelComponent, HdDefinitionInfoPanelData>;
          if (typed.data().definitionKey !== basics.definitionKey) {
            typed.replaceData({ ...typed.data(), definitionKey: basics.definitionKey });
          }
          break;
        }
        case 'profile-info': {
          const typed = panel as FloatingPanelRef<HdProfileInfoPanelComponent, HdProfileInfoPanelData>;
          if (JSON.stringify(typed.data().profile) !== JSON.stringify(profile)) typed.replaceData({ profile });
          break;
        }
        case 'nodal-environments-info': {
          const typed = panel as FloatingPanelRef<HdNodalEnvironmentsInfoPanelComponent, HdNodalEnvironmentsInfoPanelData>;
          const data = typed.data();
          if (data.cycleLabel != null || JSON.stringify(data.lifeCross) !== JSON.stringify(lifeCross)) {
            typed.replaceData({ lifeCross });
          }
          break;
        }
      }
    }
  }

  /** Type/autoriteit/definitie van de chart in het Basis-paneel, voor de "jouw …"-markeringen van de info-panelen. */
  private basicChartBasics(): HdChartBasics {
    const renderState = this.sideRenderStateFactory.create(this.basicConfiguration());
    return deriveBasicProperties(renderState, this.basicPlanetaryActivations());
  }

  /** Info-icoon naast de sectietitel Eigenschappen: opent het paneel met de Human Design basics. */
  protected handleBasicsInfoRequested(): void {
    this.floatingPanels.openOrFocus<HdBasicsInfoPanelComponent, HdBasicsInfoPanelData>({
      key: 'basics-info',
      component: HdBasicsInfoPanelComponent,
      data: {},
      dockTab: {
        label: this.pageGateInfoService.chartBasicsInfoTextIn('basics.tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  /**
   * Klik op het monopool-icoon of een bewustzijnskristal (Misc-laag op de
   * bodygraph): opent het paneel "De drie dragers van bewustzijn" naast het
   * klikpunt, zoals de center- en poortpanelen.
   */
  protected handleMonopoleInfoRequested(event: { origin: { x: number; y: number } }): void {
    this.floatingPanels.openOrFocus<HdCosmologyInfoPanelComponent, HdCosmologyInfoPanelData>({
      key: 'cosmology-info',
      component: HdCosmologyInfoPanelComponent,
      data: {},
      dockTab: {
        label: this.pageGateInfoService.chartBasicsInfoTextIn('cosmology.tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: { preferred: event.origin, hint: 'prefer-right-of-anchor' },
    });
  }

  /**
   * Info-icoon op de type-, strategie-, signatuur- of niet-zelf-rij: elk
   * onderwerp krijgt zijn eigen, beperkte paneel (eigen key en dock-tab).
   */
  protected handleTypeInfoRequested(request: { topic: HdTypeInfoTopic }): void {
    const basics = this.basicChartBasics();
    const topic = request.topic;
    const tabLabelKey = topic === 'type' ? 'type.tabLabel' : `type.topics.${topic}.tabLabel`;
    this.floatingPanels.openOrFocus<HdTypeInfoPanelComponent, HdTypeInfoPanelData>({
      key: `${topic}-info`,
      component: HdTypeInfoPanelComponent,
      data: { type: basics.type, authorityKey: basics.authorityKey, topic },
      // Leesplek per type: het eigen-paneel toont enkel het eigen type.
      contentKey: data => JSON.stringify(data.type),
      dockTab: {
        label: this.pageGateInfoService.chartBasicsInfoTextIn(tabLabelKey, this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  /** Info-icoon op de autoriteit-rij: opent het paneel met de zeven autoriteiten. */
  protected handleAuthorityInfoRequested(): void {
    this.floatingPanels.openOrFocus<HdAuthorityInfoPanelComponent, HdAuthorityInfoPanelData>({
      key: 'authority-info',
      component: HdAuthorityInfoPanelComponent,
      data: { authorityKey: this.basicChartBasics().authorityKey },
      contentKey: data => data.authorityKey ?? '',
      dockTab: {
        label: this.pageGateInfoService.chartBasicsInfoTextIn('authority.tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  /** Info-icoon op de definitie-rij: opent het paneel met de definitietypes. */
  protected handleDefinitionInfoRequested(): void {
    this.openDefinitionInfoPanel(this.basicChartBasics().definitionKey);
  }

  /**
   * Info-icoon op de definitie-rij van een cycle chart (Geavanceerd-paneel):
   * zelfde paneel, met de definitie van natal + cyclus samen als "jouw definitie".
   */
  protected handleCycleDefinitionInfoRequested(event: HdCycleDefinitionInfoRequest): void {
    this.openDefinitionInfoPanel(event.definitionKey);
  }

  private openDefinitionInfoPanel(definitionKey: HdDefinitionKey | null): void {
    this.floatingPanels.openOrFocus<HdDefinitionInfoPanelComponent, HdDefinitionInfoPanelData>({
      key: 'definition-info',
      component: HdDefinitionInfoPanelComponent,
      data: { definitionKey },
      contentKey: data => data.definitionKey ?? '',
      dockTab: {
        label: this.pageGateInfoService.chartBasicsInfoTextIn('definition.tabLabel', this.pageContentLang.effectiveLang()),
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  /** Info-icoon naast "Primaire Conditionering": opent het conditionering-info-paneel. */
  protected handleConditioningInfoRequested(): void {
    this.floatingPanels.openOrFocus<HdConditioningInfoPanelComponent, HdConditioningInfoPanelData>({
      key: 'conditioning-info',
      component: HdConditioningInfoPanelComponent,
      data: {},
      dockTab: {
        label: this.pageGateInfoService.cheatSheetTextIn('conditioning', 'dockLabel', this.pageContentLang.effectiveLang()),
        icon: 'circle-nodes',
      },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: 'center',
    });
  }

  /**
   * Relaties-sectie → composiet berekenen: partner-document ophalen, composite-
   * document bouwen uit de geladen chart + de partner, en in een nieuwe tab
   * openen (onopgeslagen, sterretje) — zelfde drieslag als cycle-berekenen.
   */
  protected async handleCompositeCalculateRequested(request: HdCompositeCalculateRequest): Promise<void> {
    const chart1Id = this.savedChartId();
    if (this.activePreset() == null || chart1Id == null) return;

    this.loadingPreset.set(true);
    try {
      const chart1Preset = await this.libraryNatalPreset(chart1Id);
      const partner = await this.chartLibrary.getChartDocument(request.partnerChartId);

      if (!(await this.confirmEndGame('switch'))) return;
      this.stopGamesForTabChange();
      const tab = this.tabsService.addTab(this.extractSnapshot());
      this.applySnapshot(tab);

      const preset = buildCompositeChartPreset({
        chart1Preset,
        chart1Id,
        chart2Preset: partner.preset,
        chart2Id: request.partnerChartId,
      });
      this.applyComputedChart(preset, PlatformChartType.Composite);
      this.settingsPanelOpen.set(false);
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De composite chart kon niet berekend worden.');
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /** Cycles-sectie op een cycle chart → herberekenen (verouderd): snapshot + moment vers, document vervangen. */
  protected async handleCycleRecalculateRequested(): Promise<void> {
    const preset = this.activePreset();
    const cycle = preset?.cycle;
    const savedId = this.savedChartId();
    if (preset == null || cycle == null || savedId == null) return;

    this.loadingPreset.set(true);
    try {
      const natal = await this.chartLibrary.getChartDocument(cycle.source.chartId);
      let anchorUtc = cycle.anchorUtc;
      let passages = cycle.passages;
      if (cycle.key !== 'justNow') {
        const moments = await this.cyclesService.getCycleMoments(cycle.source.chartId, cycle.solarReturnYear);
        const fresh = findHdCycleMoment(moments, cycle);
        if (fresh?.primaryUtc) {
          anchorUtc = fresh.primaryUtc;
          passages = (fresh.passages ?? []).map(p => ({ utc: p.utc ?? '', retrograde: p.retrograde ?? false }));
        }
      }
      const core = extractCycleSourceCore(natal.preset.birthData);
      if (core.latitude == null || core.longitude == null) {
        throw new Error('De natal-chart heeft geen herbruikbare geboortelocatie.');
      }
      const location = (natal.preset.birthData as { location?: { city?: string | null; country?: string | null } }).location ?? {};
      const momentPreset = await this.chartLibrary.calculateFromUtc({
        name: natal.preset.name,
        utc: anchorUtc,
        timezone: core.timezone || 'UTC',
        location: { city: location.city ?? null, country: location.country ?? null, lat: core.latitude, lon: core.longitude },
      });
      const rebuilt = buildCycleChartPreset({
        natalPreset: natal.preset,
        natalChartId: cycle.source.chartId,
        momentPreset,
        cycleKey: cycle.key,
        anchorUtc,
        passages,
        solarReturnYear: cycle.solarReturnYear,
        jupiterReturnNumber: cycle.jupiterReturnNumber,
        name: preset.name,
        tabTooltip: preset.tabTooltip ?? '',
      });
      await this.chartLibrary.replaceDocument(savedId, rebuilt);
      this.applySavedChart(rebuilt, savedId, PlatformChartType.Cycle);
      const relationsWarning = await this.chartLibrary.ensureSourceRelations(savedId, rebuilt);
      if (relationsWarning) {
        this.notifier.error(relationsWarning);
      }
      this.notifier.success('Cycle chart herberekend.');
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : 'De cycle chart kon niet herberekend worden.');
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /**
   * Just Now als losse tab: haalt de chart voor het huidige moment op bij de
   * backend en laadt die als gewone chart. Volledig (design + personality) via
   * de chart manager; `personalityOnly` = "Just Now Transit" (shortcut-knop en
   * chart manager), enkel de transiterende personality-kant. De backend geeft de
   * verwachte EGI-json terug zodat het resultaat exact als elke andere chart
   * behandeld kan worden — enige verschil is de bron (endpoint i.p.v. library).
   */
  protected async handleJustNowRequested(options?: { personalityOnly?: boolean }): Promise<void> {
    this.loadingPreset.set(true);
    try {
      const document = await firstValueFrom(this.humanDesignApi.getHumandesignJustNow());
      if (!document?.configuration) {
        this.notifier.error('De Just Now-chart kon niet geladen worden.');
        return;
      }
      if (!(await this.confirmEndGame('switch'))) {
        return;
      }
      // Open in een NIEUWE tab i.p.v. de actieve te overschrijven (een verse
      // actieve tab wordt hergebruikt).
      this.stopGamesForTabChange();
      this.openTabForNewChart();
      const preset = document as unknown as HdBodygraphChartPreset;
      const { tabName, tabTooltip } = this.buildJustNowLabels();
      this.applyComputedChart({ ...preset, name: tabName, tabTooltip }, PlatformChartType.JustNow);
      if (options?.personalityOnly) {
        // "Toon enkel Just Now" (chart manager): het echte moment is de
        // transiterende personality-zijde. Per-tab bewaard (tab-snapshot),
        // dus andere tabs behouden hun eigen zijde-keuze.
        this.chartSideMode.set('personality');
      }
      this.settingsPanelOpen.set(false);
    } catch {
      this.notifier.error('De Just Now-chart kon niet geladen worden.');
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /**
   * Rave New Year als losse tab (chart manager, of de badge in de transits):
   * het moment waarop de zon poort 41 binnengaat in dat jaar, berekend als
   * gewone chart. Zoals Just Now een wereldmoment (type JustNow); `transit` =
   * enkel de personality-kant, zoals Just Now Transit.
   */
  protected async handleRaveNewYearRequested(request: HdRaveNewYearRequest): Promise<void> {
    this.managerDialogOpen.set(false);
    this.loadingPreset.set(true);
    const loadError = () => this.notifier.error(this.pageTranslate('humanDesign.chartManager.raveNewYear.loadError'));
    try {
      const ingress = await this.raveNewYear.momentFor(request.year);
      if (ingress == null) {
        loadError();
        return;
      }
      // Net ná de intrede (hele seconde + marge), anders kan de zon nog in 60.6 staan.
      const atUtc = hdRaveNewYearChartMoment(ingress);
      const tabName = `Rave New Year ${request.year}`;
      const preset = await this.chartLibrary.calculateFromUtc({
        utc: atUtc.toISOString(),
        name: tabName,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (!preset?.configuration) {
        loadError();
        return;
      }
      if (!(await this.confirmEndGame('switch'))) {
        return;
      }
      this.stopGamesForTabChange();
      this.openTabForNewChart();
      const tabTooltip = this.pageTransloco.translate<string>(
        'humanDesign.chartManager.raveNewYear.tabTooltip',
        {
          year: request.year,
          moment: new Intl.DateTimeFormat(this.pageContentLang.effectiveLang(), { dateStyle: 'long', timeStyle: 'medium' })
            .format(atUtc.toDate()),
        },
        this.pageContentLang.effectiveLang(),
      );
      this.applyComputedChart({ ...preset, name: tabName, tabTooltip }, PlatformChartType.JustNow);
      if (request.variant === 'transit') {
        this.chartSideMode.set('personality');
      }
      this.settingsPanelOpen.set(false);
    } catch {
      loadError();
    } finally {
      this.loadingPreset.set(false);
    }
  }

  /**
   * Eén `Date`-snapshot voor de Just Now-tab: zowel de korte tab-titel
   * (`Just Now HH:mm`) als de hover-tooltip (volledige datum + tijd tot op
   * de seconde, nl-BE) zodat label en tooltip exact hetzelfde moment tonen.
   */
  private buildJustNowLabels(): { tabName: string; tabTooltip: string } {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const tabName = `Just Now ${hours}:${minutes}`;
    const tabTooltip = new Intl.DateTimeFormat('nl-BE', {
      dateStyle: 'long',
      timeStyle: 'medium',
    }).format(now);
    return { tabName, tabTooltip };
  }

  protected handleViewModeChange(mode: HdChartViewMode): void {
    // In de maan-transit modus schakelen de mandala/both-knoppen enkel de
    // chart-zichtbaarheid; de onderliggende viewMode blijft onaangeroerd
    // zodat alles bij het verlaten van de modus vanzelf herstelt.
    if (this.moonTransitMode()) {
      if (mode === 'mandala') {
        this.moonTransitChartVisible.set(false);
      } else if (mode === 'both') {
        this.moonTransitChartVisible.set(true);
      }
      return;
    }
    // Locks & Keys: zelfde idee als de maan-transit — de knoppen schakelen
    // enkel de zichtbaarheid binnen de modus (bijstuurronde 3), nu ook het wiel
    // zelf ('chart' = mandala uit, 2026-10-10). Vóór de Just Now-tak: bij de
    // combinatie van beide modi (ronde 8) is de locks-weergave leidend.
    if (this.locksKeysMode()) {
      this.locksKeysMandalaVisible.set(mode !== 'chart');
      this.locksKeysChartVisible.set(mode !== 'mandala');
      return;
    }
    // Just Now-transit houdt de chart altijd zichtbaar: zowel de mandala-
    // als both-knop betekenen hier de compacte gecombineerde weergave
    // (geldt ook voor een geladen snapshot-tab).
    if (this.justNowTransitVisualsActive()) {
      this.justNowTransitMandalaVisible.set(mode !== 'chart');
      return;
    }
    this.chartViewMode.set(mode);
  }

  /**
   * Haalt een eenmalige Just Now-snapshot op en projecteert uitsluitend de
   * personality-activaties als transitlaag bovenop de geladen chart.
   */
  protected async toggleJustNowTransitMode(): Promise<void> {
    if (this.justNowTransitMode()) {
      this.deactivateJustNowTransitMode();
      return;
    }
    if (this.activeCycleInfo() != null) return; // cycle-weergave sluit andere modi uit (spec §10)
    if (!this.justNowTransitAvailable() || this.justNowTransitLoading()) {
      return;
    }

    this.justNowTransitLoading.set(true);
    try {
      const document = await firstValueFrom(this.humanDesignApi.getHumandesignJustNow());
      const personalityActivations = filterHdJustNowPersonalityActivations(
        (document as unknown as HdBodygraphChartPreset | null)?.planetaryActivations ?? [],
      );
      if (personalityActivations.length === 0) {
        this.notifier.error(this.translateJustNowTransit('noPersonalityActivations'));
        return;
      }

      // De maan-transit deelt dezelfde mandala-ruimte en is daarom wederzijds
      // exclusief; pas na een geldige fetch schakelen we de maan uit. Locks &
      // Keys mag actief blijven (bijstuurronde 8): het Just Now-moment wordt
      // daar de mint tweede kant.
      this.moonTransitMode.set(false);
      this.moonTransitToday.set(null);
      this.moonTransitHoverTarget.set(null);
      this.justNowTransitSnapshot.set({
        personalityActivations,
        personalityChiron: this.resolveChironFromMetadata(
          (document as unknown as HdBodygraphChartPreset | null)?.metadata,
          'personality',
        ),
        momentMetadata: (document as unknown as HdBodygraphChartPreset | null)?.metadata ?? null,
        fetchedAt: new Date(),
      });
      this.justNowTransitMandalaVisible.set(this.chartViewMode() !== 'chart');
      this.justNowTransitHoverTarget.set(null);
      // Paneel vóór de modus: het sluit-effect leest "modus aan én paneel dicht".
      this.openJustNowPanel();
      this.justNowTransitMode.set(true);
    } catch {
      this.notifier.error(this.translateJustNowTransit('loadError'));
    } finally {
      this.justNowTransitLoading.set(false);
    }
  }

  private deactivateJustNowTransitMode(): void {
    this.justNowTransitMode.set(false);
    this.justNowTransitSnapshot.set(null);
    if (this.justNowPanelOpen()) {
      this.transitsFloatingPanels.closeForKey(this.justNowPanelKey);
    }
    this.justNowTransitHoverTarget.set(null);
    this.hoverHighlight.setGatesHover(null, 'just-now-transit');
    // Tijdslider van de live-modus: stand en sample horen bij de sessie.
    this.timeSliderValueUtc.set(null);
    this.clearTransitSample();
  }

  private deactivateMoonTransitMode(): void {
    this.moonTransitMode.set(false);
    this.moonTransitToday.set(null);
    this.moonTransitHoverTarget.set(null);
  }

  /**
   * Bij het (her)starten van een spelmodus reset de basis-implementatie de
   * chart-configuratie. Deze subclass voegt eigen transit-overlays toe (Just
   * Now Transit + Maan-transit) die eigen signals aandrijven — zonder deze
   * override blijven de groene transit-kleuraccenten actief nadat de speler
   * naar een spelmodus overschakelt.
   */
  protected override clearChartForGameMode(): void {
    super.clearChartForGameMode();
    this.releaseSavedChart();
    this.deactivateJustNowTransitMode();
    this.deactivateMoonTransitMode();
    this.deactivateLocksKeysMode();
  }

  /**
   * BELANGRIJK: spelmodus aanzetten op een tab die een chart toont, opent het
   * spel in een NIEUWE tab (een verse tab wordt hergebruikt). Vroeger maakte
   * de spelmodus de tab ter plekke leeg terwijl `savedChartId` bleef staan:
   * de app dacht dan dat bv. de eigen chart nog in die tab stond, "Mijn chart
   * laden" sprong naar die lege speltab en ververste hem niet (lopend spel).
   * De laatst gekozen spelinstellingen gaan mee naar de nieuwe tab. Niet
   * weghalen: regressietests "spelmodus en tabs" in de paginaspec.
   */
  protected override async toggleGameMode(): Promise<void> {
    if (!this.gameMode() && !this.isActiveTabFresh()) {
      const gameSettings = this.extractSnapshot().game;
      this.openTabForNewChart();
      this.applyGameSettings(gameSettings);
    }
    await super.toggleGameMode();
  }

  /** Reset ("alles wissen") laat geen tab achter die nog een opgeslagen chart claimt. */
  protected override handlePresetResetRequested(): void {
    super.handlePresetResetRequested();
    if (this.activePreset() == null) {
      this.releaseSavedChart();
    }
  }

  /**
   * Een tab zonder chart mag geen library-chart meer claimen: anders dedupen
   * "Openen"/"Mijn chart laden", verversen na een bewerking en sluiten na een
   * verwijdering allemaal naar deze lege tab.
   */
  private releaseSavedChart(): void {
    this.savedChartId.set(null);
    this.pendingSaveChanges.set(false);
    this.activeChartType.set(PlatformChartType.Standard);
  }

  private translateJustNowTransit(key: 'loadError' | 'noPersonalityActivations'): string {
    return this.pageTransloco.translate(
      `humanDesign.chart.justNowTransit.${key}`,
      undefined,
      this.pageContentLang.effectiveLang(),
    );
  }

  /**
   * Maan-knop in de shortcut-bar (spec 2026-07-06 §6.1): toggle van de modus.
   * Bij activeren wordt de today-marker vers opgehaald (hertoggle = refresh,
   * beslissing §11.7); bij deactiveren wissen we hem zodat een volgende sessie
   * niet met een verouderde positie start. Niet persistent (beslissing §11.5).
   */
  protected toggleMoonTransitMode(): void {
    const next = !this.moonTransitMode();
    if (next && this.activeCycleInfo() != null) return; // cycle-weergave sluit andere modi uit (spec §10)
    if (next) {
      this.deactivateJustNowTransitMode();
      this.deactivateLocksKeysMode();
    }
    this.moonTransitMode.set(next);
    this.moonTransitHoverTarget.set(null);
    if (next) {
      this.moonTransitChartVisible.set(true);
      void this.refreshMoonTransitToday();
    } else {
      this.moonTransitToday.set(null);
    }
  }

  /** Card- of connector-hover: highlight die ene formatie (+ kanaalstuk blauw). */
  protected handleMoonTransitFormationHovered(formation: HdMoonTransitFormation | null): void {
    this.moonTransitHoverTarget.set(
      formation == null
        ? null
        : { kind: 'formation', channelKey: formation.channelKey, moonGate: formation.moonGate },
    );
  }

  /** Maanpoort-hover in het wiel: highlight álle formaties van die poort. */
  protected handleMoonTransitGateHovered(gate: number | null): void {
    this.moonTransitHoverTarget.set(gate == null ? null : { kind: 'gate', gate });
  }

  /**
   * Kanaalstuk-/poort-hover in de bodygraph zelf (finetune-ronde 8): een poort
   * die een kanaal zou vervolledigen (maanpoort met formaties) spiegelt de
   * wiel-poort-hover — cards, connectors én het kanaalstuk highlighten. Elke
   * andere poort werkt als clear zodat er geen highlight blijft hangen, net
   * als bij de poort-hover op het wiel. Buiten de modus geen effect: deze page
   * enablet de chart-hover-service nooit, dus `gateHovered` is hier verder
   * ongebruikt.
   */
  protected handleMoonTransitChartGateHovered(gate: number | null): void {
    if (!this.moonTransitMode()) {
      return;
    }
    if (gate == null || !(this.moonTransitOverview()?.formationsByGate.has(gate) ?? false)) {
      this.moonTransitHoverTarget.set(null);
      return;
    }
    this.moonTransitHoverTarget.set({ kind: 'gate', gate });
  }

  /** Card- of connector-hover: koppel kaart, lijn en normale chart-highlight. */
  protected handleJustNowTransitFormationHovered(formation: HdJustNowTransitFormation | null): void {
    if (!this.justNowTransitVisualsActive()) {
      return;
    }
    this.justNowTransitHoverTarget.set(
      formation == null ? null : { kind: 'formation', channelKey: formation.channelKey },
    );
    this.hoverHighlight.setGatesHover(formation?.gates ?? null, 'just-now-transit');
  }

  /** Transitpoort-hover op het wiel highlight alle bijhorende kanaalkaarten. */
  protected handleJustNowTransitGateHovered(gate: number | null): void {
    if (!this.justNowTransitVisualsActive()) {
      return;
    }
    this.justNowTransitHoverTarget.set(gate == null ? null : { kind: 'gate', gate });
  }

  /** Bodygraph-hover behoudt zijn gewone dim/highlight en spiegelt enkel de kaarten. */
  protected handleTransitChartGateHovered(gate: number | null): void {
    this.handleMoonTransitChartGateHovered(gate);
    if (!this.justNowTransitVisualsActive()) {
      return;
    }
    const belongsToFormation = gate != null
      && (this.justNowTransitOverview()?.formations.some(formation => formation.gates.includes(gate)) ?? false);
    this.justNowTransitHoverTarget.set(
      belongsToFormation && gate != null ? { kind: 'gate', gate } : null,
    );
  }

  protected handleJustNowTransitFormationClicked(event: HdJustNowTransitFormationClickEvent): void {
    this.openChannelInfoPanel(
      [event.formation.channelKey],
      { preferred: event.origin, hint: 'prefer-right-of-anchor' },
      undefined,
      event.formation.transitActivations,
    );
  }

  /**
   * Klik op een kanaalstuk (finetune-ronde 9): in de maan-transit modus opent
   * een stuk van een maanpoort de kanaal-info mét de virtuele maan-transit-rij
   * — identiek aan een klik op de card of de connector. Zo'n stuk telt tijdens
   * de hover als "defined" (de highlight-activatie kleurt de maanpoort mee),
   * waardoor de basisklasse de kanaal-info zónder maan-rij zou openen; de
   * formaties van de geklikte poort zijn hier per constructie precies de
   * aangevraagde kanalen. Elke andere kanaal-klik (echt defined kanaal, of
   * buiten de modus) volgt het basisgedrag; poort-kliks blijven poort-info.
   */
  protected override handleChannelInfoRequested(event: HdBodygraphChannelInfoRequest): void {
    if (this.moonTransitMode()) {
      const formations = this.moonTransitOverview()?.formationsByGate.get(event.anchorGate) ?? [];
      if (formations.length > 0) {
        this.openChannelInfoPanel(
          formations.map(formation => formation.channelKey),
          { preferred: event.origin, hint: 'prefer-right-of-anchor' },
          event.anchorGate,
        );
        return;
      }
    }
    if (this.justNowTransitVisualsActive()) {
      const overview = this.justNowTransitOverview();
      const anchorFormations = overview?.formationsByGate.get(event.anchorGate) ?? [];
      const channelKeys = [...new Set([
        ...event.channelKeys,
        ...anchorFormations.map(formation => formation.channelKey),
      ])];
      const formations = channelKeys
        .map(channelKey => overview?.formationsByChannelKey.get(channelKey))
        .filter((formation): formation is HdJustNowTransitFormation => formation != null);
      if (formations.length > 0) {
        const seenActivationIds = new Set<string>();
        const transitActivations = formations.flatMap(formation =>
          formation.transitActivations.filter(activation => {
            if (seenActivationIds.has(activation.id)) {
              return false;
            }
            seenActivationIds.add(activation.id);
            return true;
          }),
        );
        this.openChannelInfoPanel(
          channelKeys,
          { preferred: event.origin, hint: 'prefer-right-of-anchor' },
          undefined,
          transitActivations,
        );
        return;
      }
    }
    super.handleChannelInfoRequested(event);
  }

  /**
   * Haalt de actuele personality-maan-positie op via het bestaande just-now
   * endpoint en zet de longitude voor de today-marker. Stil degraderen bij
   * een API-fout: de rest van de maan-transit view is volledig client-side
   * berekend en blijft werken (spec §8).
   */
  private async refreshMoonTransitToday(): Promise<void> {
    try {
      const document = await firstValueFrom(this.humanDesignApi.getHumandesignJustNow());
      const preset = document as unknown as HdBodygraphChartPreset;
      const moon = preset.planetaryActivations?.find(
        activation => activation.side === 'personality'
          && (activation.sourceKey === 'moon' || activation.planet === 'Moon'),
      );
      this.moonTransitToday.set(
        moon?.longitude != null
          ? { longitude: moon.longitude, gate: moon.gate, line: moon.line, fetchedAt: new Date() }
          : null,
      );
    } catch {
      this.moonTransitToday.set(null);
    }
  }

  protected handleChartSideModeChange(mode: HdChartSideMode): void {
    this.chartSideMode.set(mode);
  }

  protected handleTripleViewToggled(): void {
    this.tripleChartView.update(value => !value);
  }

  /**
   * Blokjes-laag is op cycle-/composite-tabs uitgesloten (knop disabled);
   * guard voor programmatische/toekomstige sneltoets-aanroepen zodat de laag
   * daar nooit kort aan flikkert vóór het uitschakel-effect.
   */
  protected override toggleShowGateBlocks(): void {
    if (this.cycleOrCompositeActive()) return;
    super.toggleShowGateBlocks();
  }

  protected toggleMandalaZodiacRing(): void {
    this.showMandalaZodiacRing.update(value => !value);
  }

  protected toggleMandalaGodheadRing(): void {
    this.showMandalaGodheadRing.update(value => !value);
  }

  protected toggleMandalaQuarterRing(): void {
    this.showMandalaQuarterRing.update(value => !value);
  }

  protected toggleMandalaIchingRing(): void {
    this.showMandalaIchingRing.update(value => !value);
  }

  protected togglePlanetSideStats(): void {
    this.showPlanetSideStats.update(value => !value);
  }

  /** Reset-knop: zelfde semantiek als chart-presets.requestReset (confirm bij lopend spel). */
  protected async handleShortcutResetRequested(): Promise<void> {
    if (!this.gameActive()) {
      this.handlePresetResetRequested();
      return;
    }
    if (this.gameFinished() || (await this.pageConfirmer.confirm('Ben je zeker dat je het lopende spel wil resetten?', { color: 'danger' }))) {
      this.handleResetDuringGameRequested();
    }
  }

  /** Stop-knop (shortcut-bar of panel): bestaande confirm-semantiek. */
  protected async handleShortcutStopRequested(): Promise<void> {
    if (this.gameFinished() || (await this.pageConfirmer.confirm('Ben je zeker dat je het lopende spel wil stoppen?', { color: 'danger' }))) {
      this.handleStopRequested();
    }
  }

  /** True wanneer de actieve tab nog leeg is (verse "Toevoegen"-tab). */
  private isActiveTabFresh(): boolean {
    return this.activePreset() == null && !this.gameMode() && this.configuration().activations.length === 0;
  }

  /**
   * Activeert de bestaande chart-tab en verwijdert de overbodige verse tab
   * waarin de gebruiker dezelfde chart koos (bv. "+" → manager → een chart die
   * al open staat). De tab-bar scrolt de nu-actieve tab automatisch in beeld.
   */
  private switchToExistingChartTab(existingTabId: string): void {
    const freshTabId = this.tabsService.activeTabId();
    const target = this.tabsService.switchTo(existingTabId, this.extractSnapshot());
    if (target) {
      this.applySnapshot(target);
    }
    this.tabsService.closeTab(freshTabId);
  }

  protected handlePanelGameSelectionChange(gameId: HdChartGameId): void {
    this.selectedGameId.set(gameId);
    // Variant terugzetten op de eerste geldige van het nieuwe spel.
    const option = HD_CHART_GAME_OPTIONS.find(candidate => candidate.id === gameId) ?? HD_CHART_GAME_OPTIONS[0];
    if (option.variants.length > 0 && !option.variants.some(variant => variant.id === this.selectedGameVariantId())) {
      this.selectedGameVariantId.set(option.variants[0].id);
    }
  }

  protected async handlePanelPlayClicked(): Promise<void> {
    const gameId = this.selectedGameId();
    if (gameId === 'draw-chart') {
      // Doel-chart uit de eigen bibliotheek (sinds 2026-08-23 de enige bron).
      // Race-guard: geen game starten op een andere tab dan waar de gebruiker
      // hem aanvroeg (de load is async).
      const targetId = this.selectedDrawChartTargetId();
      if (!targetId) {
        this.notifier.info('Selecteer eerst een chart om te tekenen.');
        return;
      }
      const tabIdAtStart = this.tabsService.activeTabId();
      this.loadingPreset.set(true);
      try {
        const { preset: targetPreset } = await this.chartLibrary.getChartDocument(targetId);
        if (this.tabsService.activeTabId() !== tabIdAtStart) {
          return;
        }
        this.handlePlayClicked({ gameId, variantId: this.selectedGameVariantId(), targetPreset });
      } catch (e) {
        if (this.tabsService.activeTabId() === tabIdAtStart) {
          this.notifier.error(e instanceof Error ? e.message : 'De geselecteerde chart kon niet geladen worden.');
        }
      } finally {
        if (this.tabsService.activeTabId() === tabIdAtStart) {
          this.loadingPreset.set(false);
        }
      }
      return;
    }
    this.handlePlayClicked({
      gameId,
      variantId: this.selectedGameVariantId(),
      sortedGates: this.resolveSortedGates(),
    } satisfies HdChartGamePlayRequest);
  }

  /** Kopie van chart-presets.resolveSortedGates (spec: per-tab range-keuze). */
  private resolveSortedGates(): readonly number[] | undefined {
    const isMemoryKeynote = this.selectedGameId() === 'gate-guessing' && this.selectedGameVariantId() === 'memory-keynote';
    if (!isMemoryKeynote || !this.gateRangeSortEnabled()) {
      return undefined;
    }
    const start = Math.min(this.gateRangeStart(), this.gateRangeEnd());
    const end = Math.max(this.gateRangeStart(), this.gateRangeEnd());
    return HD_BODYGRAPH_GATES.filter(gate => gate >= start && gate <= end);
  }

  protected handleColorConfigPresetChange(presetId: HdBodygraphColorConfigPresetId): void {
    this.colorConfigPresetId.set(presetId);
    const preset = HD_BODYGRAPH_COLOR_CONFIG_PRESETS.find(entry => entry.id === presetId);
    if (preset) {
      this.handleColorConfigChange(preset.config);
    }
  }

  protected handlePlanetGridColorConfigPresetChange(presetId: HdPlanetGridColorConfigPresetId): void {
    this.planetGridColorConfigPresetId.set(presetId);
    const preset = HD_PLANET_GRID_COLOR_CONFIG_PRESETS.find(entry => entry.id === presetId);
    if (preset) {
      this.handlePlanetGridColorConfigChange(preset.config);
    }
  }

  /** Dispatcht de data-driven stijl-rijen van het panel naar de bestaande handlers. */
  protected handleVisualizationStyleChange(change: { key: string; optionId: string }): void {
    switch (change.key) {
      case 'centerBorderStyle':
        this.handleCenterBorderStyleChange(change.optionId as HdBodygraphCenterBorderStyle);
        break;
      case 'activeGateColorPreset':
        this.handleActiveGateColorPresetChange(change.optionId as HdBodygraphActiveGateColorPreset);
        break;
      case 'undefinedStyle':
        this.handleUndefinedStyleChange(change.optionId as HdUndefinedActivationStyle);
        break;
      case 'nodeBackgroundStyle':
        this.handleNodeBackgroundStyleChange(change.optionId as HdNodeBackgroundStyle);
        break;
      case 'beneathTheLineStyle':
        this.handleBeneathTheLineStyleChange(change.optionId as HdBeneathTheLineStyle);
        break;
      case 'gateNumberAlign':
        this.handleGateNumberAlignChange(change.optionId as HdGateNumberAlign);
        break;
      case 'gateNumberFontSize':
        this.handleGateNumberFontSizeChange(change.optionId as HdGateNumberFontSize);
        break;
      case 'baseRotation':
        this.handleBaseRotationChange(change.optionId as HdBaseRotation);
        break;
      case 'advancedBridgeCalculation':
        this.handleAdvancedBridgeCalculationChange(change.optionId === 'on');
        break;
      case 'bridgeGateStyle':
        this.handleBridgeGateStyleChange(change.optionId as HdBridgeGateStyle);
        break;
      case 'planetGridVisualizationMode':
        this.setVisualizationMode(change.optionId);
        break;
      case 'showChiron':
        this.showChiron.set(change.optionId === 'on');
        break;
      case 'moonTransitConnectorStyle':
        this.moonTransitConnectorStyle.set(change.optionId as HdMoonTransitConnectorStyle);
        break;
      case 'moonTransitGateMarkerStyle':
        this.moonTransitGateMarkerStyle.set(change.optionId as HdMoonTransitGateMarkerStyle);
        break;
      case 'justNowTransitColorMode':
        this.justNowTransitColorMode.set(change.optionId as HdJustNowTransitColorMode);
        break;
      case 'mandalaQuarterColorVariant':
        this.mandalaQuarterColorVariant.set(change.optionId as HdMandalaQuarterColorVariant);
        break;
      case 'mandalaZodiacRingVariant':
        this.mandalaZodiacRingVariant.set(change.optionId as HdMandalaZodiacRingVariant);
        break;
      case 'mandalaSliceCenterFade':
        this.mandalaSliceCenterFade.set(change.optionId === 'on');
        break;
    }
  }

  // ── Globale settings persistentie (verhuisd naar HdChartSettingsStore) ────

  /** Delegator naar de centrale store; behouden omdat de spec dit nog aanroept. */
  private collectGlobalSettings(): HdChartGlobalSettingsV1 {
    return this.settings.collect();
  }
}
